/**
 * INDIE PUBLISHING ROADMAP — Licenced Product v3
 * ───────────────────────────────────────────────
 * Four tabs:
 *   1. Timeline  — combined phases view + table view with notes, tips & target dates
 *   2. Distribution — distribution channel setup
 *   3. Metadata  — book metadata management
 *   4. Vault     — platform reference: logins (URL, email/username), notes
 *
 * Security note on the Vault:
 *   We deliberately DO NOT store passwords. The vault keeps platform URLs,
 *   usernames/emails and non-secret notes only — passwords belong in a proper
 *   password manager. Progress (including the vault) is stored per-licence in
 *   `licenceProgress`, which is server-locked and reachable only via the
 *   validateLicence / getLicenceProgress / saveLicenceProgress callables.
 *   Legacy docs that still contain obfuscated passwords are surfaced once for
 *   the user to move elsewhere, then stripped (see VaultTab migration).
 */

import React, { useState, useEffect, useCallback, useRef, useContext, createContext } from 'react';
import {
  CheckCircle, Circle, ChevronDown, ChevronUp,
  Calendar, BookOpen, Lock, Shield, AlertTriangle,
  Sparkles, ArrowRight, Key, Plus, Trash2, ExternalLink,
  X, Eye, EyeOff, Copy, Check, StickyNote, Table2,
  FileText, Globe, Mail, User, Link2, Pencil, Save,
  ChevronRight, Database, Tag, Search, Hash, Info,
  CheckSquare, Square, AlertCircle, RefreshCw,
  Rocket, Globe2, Package, Clock, AlertOctagon,
  Send, Zap, ShoppingBag, LogOut
} from 'lucide-react';
import ToolNav from './toolkit/ToolNav';
import { getLicenceProgressFn, saveLicenceProgressFn } from './licensing/licenceApi';
import useAntiCopy from './licensing/useAntiCopy';
import Watermark from './licensing/Watermark';
import LicenceGate from './licensing/LicenceGate';
import PricingPlans from './licensing/PricingPlans';
import useLicence from './licensing/useLicence';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Badge } from './components/ui/badge';
import { Card, CardContent } from './components/ui/card';
import { Progress } from './components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './components/ui/dialog';
import { Textarea } from './components/ui/textarea';
import { Select } from './components/ui/select';
import RoadmapFeedbackWidget from './RoadmapFeedbackWidget';

/* Licence gate, Google sign-in, pricing/checkout and account-synced saving are
   shared with the Plot Planner — see src/licensing/. This file keeps only the
   roadmap-specific tool (ChecklistTool) and its data. Progress get/save
   callables are imported above. */

/* ═══════════════════════════════════════════════════════════
   CHECKLIST DATA
═══════════════════════════════════════════════════════════ */
const CHECKLIST = [
  {
    phase: 'Phase 1', title: 'Foundations & Setup',
    timeframe: '12–9 months before release', icon: '🏗️',
    tasks: [
      { id: 'imprint',   title: 'Choose imprint name and confirm for ISBN registration',         tip: 'Your imprint is your publishing brand. Pick something future-proof — you\'ll use it on every book you publish.',                                                                          guide: '/guides/imprint-name.html' },
      { id: 'isbn',      title: 'Register Nielsen account and purchase a block of 10 ISBNs',      tip: 'Each format (ebook, paperback, hardback) needs its own ISBN. Buy a block of 10 — about £164. A single ISBN is £89, more than half the block, so a block is almost always the right call. ISBNs never expire. (A free KDP ISBN lists the publisher as "Independently Published" and only works on Amazon.)', guide: '/guides/isbn-registration.html' },
      { id: 'accounts',  title: 'Set up KDP and IngramSpark accounts (verify bank & tax info)',   tip: 'The W-8BEN tax form takes time. Don\'t leave this until the month before launch. UK tax note: HMRC gives a £1,000 trading allowance — if your total self-publishing income for the tax year is under £1,000 you don\'t need to register or report it; above that you register for Self Assessment.',                                          guide: '/guides/kdp-ingramspark.html' },
      { id: 'folders',   title: 'Create project folder structure and enable cloud backup',        tip: 'Use clear naming: /BookTitle/Drafts, /Formatted, /Covers, /Marketing — back up to at least two locations.',                                                                               guide: '/guides/project-folders.html' },
      { id: 'copyright', title: 'Draft copyright page and metadata spreadsheet',                  tip: 'Lock your metadata early — title, subtitle, series, categories, keywords. Changes later cost time and money.',                                                                            guide: '/guides/copyright-metadata.html' },
      { id: 'cover-designer', title: 'Commission your cover designer',                            tip: 'Good cover designers book up months ahead — approach yours 6–9 months before release. Brief them on genre, comp titles and your series look, and confirm the formats you need (ebook thumbnail plus full paperback/hardback wraps with correct spine widths once page counts are final).', guide: null },
      { id: 'uk-copyright',   title: 'Understand UK copyright (no registration needed)',          tip: 'In the UK, copyright is automatic the moment you create the work — there is no register to file with and no fee to pay. Avoid paid "copyright registration" services; they sell you nothing you do not already have. Just keep dated backups of your manuscript as evidence of authorship.', guide: null },
      { id: 'mailing-list',   title: 'Set up your mailing list with GDPR-compliant consent',       tip: 'Start building your mailing list early — list-building runs alongside writing, long before launch. Under UK GDPR/PECR use clear opt-in consent — no pre-ticked boxes — record when and how each subscriber opted in, name yourself as the data controller and give an easy unsubscribe. A reader magnet (bonus scene, novella) is the classic ethical way to grow the list — pair it with BookFunnel group promos (https://bookfunnel.com) to build momentum early.', guide: null },
    ],
  },
  {
    phase: 'Phase 2', title: 'Editing & Metadata Lock',
    timeframe: '9–6 months before release', icon: '✍️',
    tasks: [
      { id: 'editing',   title: 'Send manuscript for line/copy edit',                             tip: 'Brief your editor on tone, POV, and any in-world terminology. The clearer your brief, the better the result.',                                                                            guide: '/guides/editing.html' },
      { id: 'revisions', title: 'Implement revisions and finalize manuscript text',               tip: 'Once formatting begins, text changes become expensive. Sign off completely before moving on.',                                                                                             guide: '/guides/revisions.html' },
      { id: 'frontback', title: 'Finalize front and back matter',                                 tip: 'Include acknowledgments, author note, series teaser, social links, and newsletter CTA. Add imprint details on the copyright page.',                                                       guide: '/guides/frontback-matter.html' },
      { id: 'metadata',  title: 'Lock metadata (title, subtitle, keywords, categories, pricing)', tip: 'Research categories carefully — this drives discoverability on Amazon. Use Publisher Rocket or browse Amazon categories manually.',                                                       guide: '/guides/guide-metadata-lock.html' },
      { id: 'thema-royalty', title: 'Set BISAC + Thema codes and choose your KDP royalty band',   tip: 'Nielsen and UK/global trade use Thema codes, not just US BISAC — set both (the Metadata tab has searchable pickers for each). Also choose your KDP royalty band: 70% requires the ebook priced roughly £1.99–£9.99 and carries a small delivery fee; 35% has no price window but pays far less. Price for the band you want.', guide: null },
      { id: 'wide-vs-select', title: 'Decide: wide distribution vs KDP Select / Kindle Unlimited', tip: 'Make this call BEFORE setting up preorders. KDP Select gives you Kindle Unlimited and Amazon promo tools but demands ebook EXCLUSIVITY — you cannot sell the ebook on Kobo, Apple, Google or your own site while enrolled. "Wide" gives up KU for reach across every store. You cannot do both for the same ebook at once.', guide: null },
      { id: 'preorders', title: 'Set ebook preorders on KDP (up to 12 months) + print preorders on IngramSpark', tip: 'Preorders consolidate sales for launch-day ranking. KDP has NO print preorders — its ebook preorder runs up to 12 months ahead. KDP print only offers "Schedule a Release" (page hidden, max 90 days, no early sales). True print preorders are IngramSpark only.',                                    guide: '/guides/guide-preorders.html' },
      { id: 'nielsen-basic', title: 'Create your free Nielsen Title Editor basic listing (~20 weeks out)', tip: 'Aim to have a basic Nielsen record live around 20 weeks before publication. Nielsen data feeds Gardners, UK bookshops and libraries, so an early, accurate listing lets shops and librarians find and order your book well ahead of launch. Important: complete your KDP print setup and have Amazon\'s print listing scheduled before you finalise your Nielsen Title Editor listing — in some cases, Amazon ingests Nielsen data before your KDP print listing exists and auto-creates a separate, unlinked "ghost" listing — a duplicate product page that can appear orderable but never fulfils. This doesn\'t happen to every book; it depends on timing and how Amazon matches the data. To be safe, either complete your KDP print setup first, or — if you list with Nielsen first — search Amazon for your print ISBN a week or two later and contact KDP support if an unlinked duplicate appears.', guide: null },
    ],
  },
  {
    phase: 'Phase 3', title: 'Formatting, Proofing & ARCs',
    timeframe: '6–3 months before release', icon: '📐',
    tasks: [
      { id: 'formatter',      title: 'Send manuscript to formatter',                                         tip: 'Provide your formatter with full front/back matter, fonts, and any special styling notes before they begin.',                       guide: '/guides/guide-send-formatter.html' },
      { id: 'receive-proofs', title: 'Receive formatted EPUB and print PDF proofs',                          tip: 'Check headers, page breaks, chapter starts, special characters. Read on an actual e-reader, not just your computer.',               guide: '/guides/guide-receive-proofs.html' },
      { id: 'arc-files',      title: 'Create ARC files (EPUB + PDF with NOT FOR RESALE watermark)',          tip: 'Add a disclaimer page at the front. A quick "ADVANCE READER COPY" label in bold is enough.',                                       guide: '/guides/guide-create-arc-files.html' },
      { id: 'arc-campaign',   title: 'Launch ARC reviewer campaign and track responses',                     tip: 'Aim for 3–5x your target review count — not everyone will post. Use BookFunnel or StoryOrigin. Tell reviewers they MUST disclose that they received a free advance copy (Amazon requires it). Suggested wording: "I received an advance copy and am voluntarily leaving an honest review."', guide: '/guides/guide-arc-campaign.html' },
      { id: 'proofread',      title: 'Send formatted files to proofreader',                                  tip: 'Proofreading happens on the formatted file, not the Word doc. Catches errors introduced during formatting.',                        guide: '/guides/guide-send-proofreader.html' },
      { id: 'cover',          title: 'Prepare full cover wrap with barcode',                                 tip: 'Use IngramSpark\'s cover template calculator for exact spine width. Even 1mm off will cause the file to be rejected.',              guide: '/guides/guide-cover-wrap.html' },
      { id: 'print-proofs',   title: 'Order print proofs from KDP and IngramSpark',                          tip: 'Order at least 2 copies — one from each platform. KDP and IngramSpark use different printers and quality can vary.',               guide: '/guides/guide-order-proofs.html' },
      { id: 'ebook-accessibility', title: 'Make the ebook accessible (EU Accessibility Act)',              tip: 'Since June 2025 the European Accessibility Act requires ebooks sold into the EU to be accessible: a proper EPUB3 with real heading structure and navigation, a logical reading order, alt text on meaningful images, and no text locked inside images. Ask your formatter for an accessible EPUB3 and to complete the accessibility metadata.', guide: null },
      { id: 'audiobook',      title: 'Optional: produce and distribute an audiobook',                       tip: 'Doing audio? Audition several narrators (match tone and the pronunciation of in-world terms), agree pickups/retakes, then proof-listen every finished chapter against the text. Distribute wide — Spotify for Authors, Google Play Books, Kobo, Apple and library platforms — rather than a single exclusive channel.', guide: null },
    ],
  },
  {
    phase: 'Phase 4', title: 'Final Uploads & Marketing',
    timeframe: '3–1 month before release', icon: '🚀',
    tasks: [
      { id: 'approve-proofs', title: 'Approve physical proofs and finalize any corrections',                 tip: 'Don\'t approve until you\'re 100% happy. Corrections after approval cost time and money.',                                          guide: '/guides/guide-approve-proofs.html' },
      { id: 'final-upload',   title: 'Upload final EPUB and print PDFs to KDP and IngramSpark',              tip: 'If you have pre-orders, you MUST upload at least 3 days before release — or Amazon cancels all pre-orders.',                        guide: '/guides/guide-final-upload.html' },
      { id: 'test-links',     title: 'Test "Read Sample" (formerly "Look Inside") previews and all retail links', tip: 'It can take 24–72 hours for previews to update after upload. Check Amazon UK and US separately. (Amazon renamed "Look Inside" to "Read Sample".)',                                  guide: '/guides/guide-test-links.html' },
      { id: 'launch-assets',  title: 'Prepare and schedule launch assets (socials, press kit, email sequence)', tip: 'Schedule everything 2 weeks ahead so you can focus on community during launch week itself.',                                    guide: '/guides/guide-launch-assets.html' },
      { id: 'author-platform', title: 'Set up Amazon Author Central, series page and A+ content',            tip: 'Once your ebook is live, claim your Author Central page (bio, photo, follow button). Link your titles into an Amazon Series page so readers find the whole series, and add A+ Content (branded images, quotes, a series banner) to your product pages — it visibly lifts conversion.', guide: null },
      { id: 'lock-files',     title: 'Lock all files and metadata — final backups',                          tip: 'Save final versions clearly labelled: BookTitle_FINAL_v1. Back up to at least 3 locations.',                                       guide: '/guides/guide-lock-files.html' },
    ],
  },
  {
    phase: 'Phase 5', title: 'Launch & Post-Launch',
    timeframe: 'Launch week and beyond', icon: '🎉',
    tasks: [
      { id: 'announce',        title: 'Announce release day across all platforms',                tip: 'Post at midnight in your primary market timezone. Have 3 posts ready: morning, midday, evening.',                                              guide: '/guides/guide-announce-launch.html' },
      { id: 'arc-reviews',     title: 'Engage ARC team to post reviews on launch week',           tip: 'Send a reminder email 1 week before AND on launch day. It\'s not pushy — it\'s helpful.',                                                     guide: '/guides/guide-engage-arc-team.html' },
      { id: 'monitor',         title: 'Monitor sales dashboard for issues',                       tip: 'Check KDP and IngramSpark dashboards daily for the first 2 weeks. Once in the morning — don\'t obsess hourly.',                               guide: '/guides/guide-monitor-sales.html' },
      { id: 'collect-reviews', title: 'Collect reviews and screenshots for marketing',            tip: 'Build a swipe file of great quotes for ongoing social content. Screenshot everything — reviews can disappear.',                               guide: null },
      { id: 'plr-alcs',        title: 'Register for PLR and join ALCS (UK secondary income)',     tip: 'Register your titles for Public Lending Right (run by the British Library): it pays UK-based authors when public libraries lend your print, ebook or audiobook editions — worth up to £6,600 a year. The PLR year runs 1 July–30 June; register by 30 June and it captures loans back to the previous July, paid the following February. Miss the deadline and payments are not backdated, so register promptly after launch and re-check every June for new titles and editions. You need an ISBN — ASIN-only Kindle editions do not qualify. Register at https://www.bl.uk/plr. Then join ALCS for secondary uses — photocopying, broadcast and recording, and overseas lending-right equivalents (UK library lending itself falls under PLR) — free to join at https://www.alcs.co.uk, with a small one-off lifetime fee taken from your first payment. Both are money authors routinely leave unclaimed.',                    guide: null },
      { id: 'next-book',       title: 'Plan Book 2 teaser or post-launch promo wave',             tip: 'The best time to market Book 1 is right after it launches — ride the momentum. A Book 2 teaser keeps readers engaged.',                       guide: null },
    ],
  },
];

const TOTAL_TASKS = CHECKLIST.reduce((s, p) => s + p.tasks.length, 0);

/* Pre-populated vault platforms */
const PLATFORM_SUGGESTIONS = [
  { name: 'Amazon KDP',      url: 'https://kdp.amazon.com',          phase: 'Phase 1' },
  { name: 'IngramSpark',     url: 'https://www.ingramspark.com',      phase: 'Phase 1' },
  { name: 'Nielsen ISBN',    url: 'https://www.nielsenisbnstore.com', phase: 'Phase 1' },
  { name: 'BookFunnel',      url: 'https://bookfunnel.com',           phase: 'Phase 3' },
  { name: 'StoryOrigin',     url: 'https://storyoriginapp.com/',       phase: 'Phase 3' },
  { name: 'Canva',           url: 'https://www.canva.com',            phase: 'Phase 1' },
  { name: 'Goodreads',       url: 'https://www.goodreads.com',        phase: 'Phase 2' },
  { name: 'Mailchimp',       url: 'https://mailchimp.com',            phase: 'Phase 2' },
  { name: 'BookTok / TikTok',url: 'https://www.tiktok.com',           phase: 'Phase 4' },
];

// Unified brand palette — phases are differentiated by number/label, not hue,
// so the whole tool reads as one Narratoria product (ink + antique gold).
const PHASE_BRAND = { bg: 'bg-parchment', text: 'text-gold-deep', dot: 'bg-gold', ring: 'ring-gold-soft', header: 'bg-ink' };
const PHASE_COLORS = {
  'Phase 1': PHASE_BRAND, 'Phase 2': PHASE_BRAND, 'Phase 3': PHASE_BRAND,
  'Phase 4': PHASE_BRAND, 'Phase 5': PHASE_BRAND,
};

/* ═══════════════════════════════════════════════════════════
   SIMPLE XOR OBFUSCATION  (convenience, not bank-grade)
═══════════════════════════════════════════════════════════ */
// xorDeobfuscate is retained only to decode legacy vault passwords/notes during
// the one-time migration away from stored passwords (see VaultTab). We no longer
// obfuscate or store passwords, so the encode side has been removed.
function xorDeobfuscate(encoded, key) {
  if (!encoded) return '';
  try {
    const text = atob(encoded);
    let result = '';
    for (let i = 0; i < text.length; i++) {
      result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return result;
  } catch { return ''; }
}

/* useAntiCopy, Watermark, LicenceGate and PricingPlans now live in
   src/licensing/ (shared, product-aware — used by the Roadmap and the Planner). */


/* ═══════════════════════════════════════════════════════════
   ADD BOOK MODAL
═══════════════════════════════════════════════════════════ */
function AddBookModal({ onAdd, onClose }) {
  const [title, setTitle]   = useState('');
  const [series, setSeries] = useState('');
  const [date, setDate]     = useState('');
  const submit = (e) => { e.preventDefault(); if (!title.trim()) return; onAdd({ id:`book-${Date.now()}`, title:title.trim(), series:series.trim(), releaseDate:date }); };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-md overflow-hidden rounded-2xl border border-gold-soft bg-white shadow-2xl p-0">
        <DialogHeader className="border-b border-gold-soft bg-stone-50 px-6 py-4">
          <DialogTitle className="text-base font-bold text-stone-900">Add a Book</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 p-6">
          {[['Book Title *','text',title,setTitle,'e.g. The Ember Gate',true],['Series (optional)','text',series,setSeries,'e.g. The Ember Gate Saga',false]].map(([lbl,type,val,set,ph,req]) => (
            <div key={lbl}>
              <Label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-stone-500">{lbl}</Label>
              <Input type={type} required={req} value={val} onChange={e=>set(e.target.value)} placeholder={ph}
                className="w-full rounded-xl border border-stone-200 px-3 py-2.5 text-sm text-stone-900 outline-none focus:border-gold focus:ring-2 focus:ring-gold/25"
                style={{userSelect:'text',WebkitUserSelect:'text'}} />
            </div>
          ))}
          <div>
            <Label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-stone-500">Target Release Date (optional)</Label>
            <Input type="date" value={date} onChange={e=>setDate(e.target.value)}
              className="w-full rounded-xl border border-stone-200 px-3 py-2.5 text-sm text-stone-900 outline-none focus:border-gold focus:ring-2 focus:ring-gold/25"
              style={{userSelect:'text',WebkitUserSelect:'text'}} />
          </div>
          <div className="flex gap-3 pt-1">
            <Button type="submit" className="flex-1 rounded-xl bg-gold-deep py-2.5 text-sm font-bold text-white hover:bg-gold">Add Book</Button>
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 rounded-xl border border-stone-200 py-2.5 text-sm font-semibold text-stone-700 hover:bg-stone-50">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════
   TAB: TIMELINE  (combined roadmap + table view)
═══════════════════════════════════════════════════════════ */
function TimelineTab({ progress, onToggleTask, taskNotes, onSaveNote, taskDates, onSaveDateForTask, bookTitle }) {
  const [viewMode, setViewMode]        = useState('phases'); // 'phases' or 'table'
  const [editingNote, setEditingNote]   = useState(null);   // taskId
  const [noteValue, setNoteValue]       = useState('');
  const [filterPhase, setFilterPhase]   = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [collapsed, setCollapsed]       = useState({});
  const [expandedTips, setExpandedTips] = useState({});
  const textareaRef = useRef(null);

  const togglePhase = (i) => setCollapsed(p => ({...p,[i]:!p[i]}));
  const toggleTip   = (id) => setExpandedTips(p => ({...p,[id]:!p[id]}));

  const allTasks = CHECKLIST.flatMap(p =>
    p.tasks.map(t => ({ ...t, phase: p.phase, phaseTitle: p.title, timeframe: p.timeframe }))
  );

  const filtered = allTasks.filter(t => {
    if (filterPhase !== 'All' && t.phase !== filterPhase) return false;
    if (filterStatus === 'Done' && !progress[t.id])  return false;
    if (filterStatus === 'Open' && progress[t.id])   return false;
    return true;
  });

  const startEdit = (taskId) => {
    setEditingNote(taskId);
    setNoteValue(taskNotes[taskId] || '');
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const saveNote = (taskId) => {
    onSaveNote(taskId, noteValue);
    setEditingNote(null);
  };

  const done  = allTasks.filter(t=>progress[t.id]).length;
  const total = allTasks.length;
  const pct   = Math.round(done/total*100);

  return (
    <div className="space-y-4">
      {/* Overall progress */}
      <div className="overflow-hidden rounded-2xl border border-gold-soft bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-widest text-gold-deep">Overall Progress</p>
          <div className="text-right"><p className="text-3xl font-bold text-gold-deep">{pct}%</p><p className="text-xs text-stone-400">{done} / {total} tasks</p></div>
        </div>
        <Progress value={pct} className="h-3 bg-stone-100" indicatorClassName="bg-gold" />
        {pct===100 && <div className="mt-3 flex items-center gap-2 rounded-xl bg-stone-50 p-3 text-sm font-semibold text-gold-deep"><Sparkles className="h-4 w-4"/>All tasks complete — you're ready to launch!</div>}
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label:'Total tasks', value:total, color:'text-stone-700' },
          { label:'Completed',   value:done,  color:'text-green-600' },
          { label:'Remaining',   value:total-done, color:'text-gold-deep' },
        ].map(({label,value,color}) => (
          <div key={label} className="rounded-xl border border-gold-soft bg-white p-3 text-center shadow-sm">
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-xs text-stone-400">{label}</p>
          </div>
        ))}
      </div>

      {/* View toggle + Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-xl border border-stone-200 bg-white p-1 shadow-sm">
          <Button variant="ghost" size="sm" onClick={()=>setViewMode('phases')}
            className={`h-auto rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-all ${viewMode==='phases'?'bg-gold-deep text-white shadow-sm':'text-stone-500 hover:text-stone-900'}`}>
            <CheckCircle className="mr-1 h-3 w-3"/>Phases
          </Button>
          <Button variant="ghost" size="sm" onClick={()=>setViewMode('table')}
            className={`h-auto rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-all ${viewMode==='table'?'bg-gold-deep text-white shadow-sm':'text-stone-500 hover:text-stone-900'}`}>
            <Table2 className="mr-1 h-3 w-3"/>Table
          </Button>
        </div>
        {viewMode === 'table' && (
          <>
            <div className="flex max-w-full flex-wrap gap-1 rounded-xl border border-stone-200 bg-white p-1 shadow-sm">
              {['All',...CHECKLIST.map(p=>p.phase)].map(ph => (
                <Button key={ph} variant="ghost" size="sm" onClick={()=>setFilterPhase(ph)}
                  className={`h-auto rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-all ${filterPhase===ph?'bg-gold-deep text-white shadow-sm':'text-stone-500 hover:text-stone-900'}`}>
                  {ph}
                </Button>
              ))}
            </div>
            <div className="flex max-w-full flex-wrap gap-1 rounded-xl border border-stone-200 bg-white p-1 shadow-sm">
              {['All','Open','Done'].map(s => (
                <Button key={s} variant="ghost" size="sm" onClick={()=>setFilterStatus(s)}
                  className={`h-auto rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-all ${filterStatus===s?'bg-stone-700 text-white':'text-stone-500 hover:text-stone-900'}`}>
                  {s}
                </Button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Phases view (accordion) ── */}
      {viewMode === 'phases' && CHECKLIST.map((phase, idx) => {
        const phaseDone = phase.tasks.filter(t=>progress[t.id]).length;
        const phasePct  = Math.round(phaseDone/phase.tasks.length*100);
        const allDone   = phasePct===100;
        const col       = collapsed[idx];
        return (
          <div key={idx} className={`overflow-hidden rounded-2xl border shadow-sm ${allDone?'border-green-200':'border-gold-soft'} bg-white`}>
            <Button variant="ghost" onClick={()=>togglePhase(idx)} className={`h-auto w-full rounded-none p-4 text-left transition-all ${allDone ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-ink hover:bg-ink-800'}`}>
              <div className="flex w-full items-center justify-between">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border font-cinzel text-sm font-bold ${allDone ? 'border-white/40 bg-white/15 text-white' : 'border-gold/40 bg-gold/15 text-gold'}`}>{idx+1}</span>
                  <div className="min-w-0"><p className="truncate font-cinzel text-sm font-bold tracking-wide text-white">{phase.phase}: {phase.title}</p><p className="truncate text-xs text-parchment/60">{phase.timeframe}</p></div>
                </div>
                <div className="flex shrink-0 items-center gap-2 pl-2 sm:gap-4">
                  <div className="text-right"><p className="text-xs font-semibold text-white">{phaseDone}/{phase.tasks.length}</p><div className="mt-1 h-1.5 w-14 overflow-hidden rounded-full bg-white/30 sm:w-20"><div className="h-full bg-white transition-all" style={{width:`${phasePct}%`}}/></div></div>
                  {col ? <ChevronDown className="h-5 w-5 text-white/80"/> : <ChevronUp className="h-5 w-5 text-white/80"/>}
                </div>
              </div>
            </Button>
            {!col && (
              <div className="space-y-2 p-4">
                {phase.tasks.map(task => {
                  const isDone  = !!progress[task.id];
                  const tipOpen = expandedTips[task.id];
                  const hasNote = !!(taskNotes[task.id]);
                  return (
                    <div key={task.id} className={`overflow-hidden rounded-xl border transition-all ${isDone?'border-green-200 bg-green-50/40':'border-stone-200 bg-white hover:border-stone-200 hover:bg-stone-50/20'}`}>
                      <div className="flex items-start gap-2 p-3">
                        <Button variant="ghost" onClick={()=>onToggleTask(task.id)} className="mt-0.5 shrink-0 h-auto p-0">
                          {isDone ? <CheckCircle className="h-5 w-5 text-green-600"/> : <Circle className="h-5 w-5 text-stone-300"/>}
                        </Button>
                        <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                          <Button variant="ghost" onClick={()=>onToggleTask(task.id)} className="h-auto min-w-0 flex-1 whitespace-normal p-0 text-left">
                            <p className={`text-base leading-normal ${isDone?'text-stone-400 line-through':'font-semibold text-stone-900'}`}>{task.title}</p>
                          </Button>
                          <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                            {hasNote && <Badge className="flex h-5 w-5 items-center justify-center rounded-full bg-gold/15 text-gold-deep border-0"><StickyNote className="h-3 w-3"/></Badge>}
                            {task.tip && !isDone && (
                              <Button variant="ghost" size="sm" onClick={()=>toggleTip(task.id)} className="flex h-auto items-center gap-1 rounded-full border border-gold-soft bg-gold/10 px-2.5 py-1.5 text-xs font-semibold text-gold-deep hover:bg-gold/20">
                                {tipOpen ? 'Hide' : <><Sparkles className="h-3 w-3"/>Tip</>}
                              </Button>
                            )}
                            {task.guide && (
                              <a href={task.guide} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()}
                                className="flex items-center gap-1 rounded-full border border-gold-soft bg-parchment px-2.5 py-1.5 text-xs font-semibold text-gold-deep hover:bg-gold/15">
                                Guide <ExternalLink className="h-2.5 w-2.5"/>
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                      {tipOpen && task.tip && (
                        <div className="mx-3 mb-3 flex gap-2 rounded-xl border border-gold-soft bg-gold/5 px-3 py-2 text-sm leading-relaxed text-stone-800"><Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold-deep"/><span>{task.tip}</span></div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* ── Table view ── */}
      {viewMode === 'table' && (
        <div className="overflow-hidden rounded-2xl border border-gold-soft bg-white shadow-sm">
          <div className="overflow-x-auto">
          <div className="min-w-[600px]">
          {/* Header */}
          <div className="grid grid-cols-[28px_1fr_110px_130px_140px] gap-3 border-b border-gold-soft bg-stone-50 px-4 py-2.5">
            {['','Task','Phase','Target Date','Status'].map((h,i) => (
              <p key={i} className="text-xs font-bold uppercase tracking-widest text-stone-400">{h}</p>
            ))}
          </div>

          <div className="divide-y divide-stone-50">
            {filtered.map(task => {
              const isDone   = !!progress[task.id];
              const hasNote  = !!(taskNotes[task.id]);
              const pc       = PHASE_COLORS[task.phase] || PHASE_COLORS['Phase 1'];
              const editing  = editingNote === task.id;
              return (
                <React.Fragment key={task.id}>
                  <div className={`grid grid-cols-[28px_1fr_110px_130px_140px] items-center gap-3 px-4 py-3 transition-colors ${isDone?'bg-green-50/30':''}`}>
                    {/* Tick */}
                    <Button variant="ghost" onClick={()=>onToggleTask(task.id)} className="shrink-0 h-auto p-0">
                      {isDone ? <CheckCircle className="h-5 w-5 text-green-500"/> : <Circle className="h-5 w-5 text-stone-300"/>}
                    </Button>
                    {/* Task title + note toggle */}
                    <div className="min-w-0">
                      <p className={`text-base leading-normal ${isDone?'text-stone-400 line-through':'font-medium text-stone-900'}`}>{task.title}</p>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-xs text-stone-400">{task.timeframe}</span>
                        {task.guide && (
                          <a href={task.guide} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-0.5 text-xs font-semibold text-gold-deep hover:text-[#7c5f13]">
                            Guide <ExternalLink className="h-2.5 w-2.5"/>
                          </a>
                        )}
                      </div>
                    </div>
                    {/* Phase badge */}
                    <div>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${pc.bg} ${pc.text}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${pc.dot}`}/>{task.phase}
                      </span>
                    </div>
                    {/* Target date */}
                    <div>
                      <input type="date" value={taskDates[task.id]||''}
                        onChange={e=>onSaveDateForTask(task.id, e.target.value)}
                        className="w-full rounded-xl border border-stone-200 bg-stone-50 px-2 py-1 text-xs text-stone-700 outline-none focus:border-gold focus:ring-1 focus:ring-gold/25"
                        style={{userSelect:'text',WebkitUserSelect:'text'}} />
                    </div>
                    {/* Status + note button */}
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${isDone?'bg-green-100 text-green-700':'bg-stone-100 text-stone-500'}`}>
                        {isDone?'✓ Done':'○ Open'}
                      </span>
                      <button onClick={()=>editing ? setEditingNote(null) : startEdit(task.id)}
                        title={hasNote?'Edit note':'Add note'}
                        className={`rounded-full p-1 transition-colors ${hasNote?'bg-gold/15 text-gold-deep':'text-stone-300 hover:bg-stone-100 hover:text-stone-600'}`}>
                        <StickyNote className="h-3.5 w-3.5"/>
                      </button>
                    </div>
                  </div>
                  {/* Inline note editor */}
                  {editing && (
                    <div className="border-t border-dashed border-gold-soft bg-parchment/40 px-4 py-3">
                      <p className="mb-1.5 text-xs font-semibold text-gold-deep">Note for this task</p>
                      <Textarea ref={textareaRef} value={noteValue} onChange={e=>setNoteValue(e.target.value)}
                        placeholder="Add notes, reminders, or details for this task…"
                        rows={3}
                        className="w-full resize-none rounded-xl border border-gold-soft bg-white px-3 py-2 text-sm text-stone-800 outline-none focus:border-gold focus:ring-2 focus:ring-gold-soft"
                        style={{userSelect:'text',WebkitUserSelect:'text'}} />
                      <div className="mt-2 flex gap-2">
                        <Button size="sm" onClick={()=>saveNote(task.id)}
                          className="flex items-center gap-1.5 rounded-xl bg-gold-deep px-3 py-1.5 text-xs font-bold text-white hover:bg-gold">
                          <Save className="h-3 w-3"/> Save Note
                        </Button>
                        <Button variant="outline" size="sm" onClick={()=>setEditingNote(null)} className="rounded-xl border border-stone-200 px-3 py-1.5 text-xs font-semibold text-stone-600 hover:bg-stone-50">
                          Cancel
                        </Button>
                        {noteValue && <Button variant="ghost" size="sm" onClick={()=>{setNoteValue('');onSaveNote(task.id,'');setEditingNote(null);}} className="ml-auto rounded-xl px-3 py-1.5 text-xs text-red-400 hover:text-red-600">Clear note</Button>}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
          </div>
          </div>
          {filtered.length===0 && (
            <div className="p-10 text-center text-sm text-stone-400">No tasks match your filters.</div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   TAB: VAULT  (platform credentials)
═══════════════════════════════════════════════════════════ */
function VaultTab({ credentials, onSave, licenceKey }) {
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);  // id or null
  const [copied, setCopied]       = useState({});    // { id+field: bool }
  const [filterPhase, setFilterPhase] = useState('All');

  const blank = { id:'', platform:'', url:'', email:'', username:'', notes:'', phase:'Phase 1' };
  const [form, setForm] = useState({ ...blank });
  const [migrationDone, setMigrationDone] = useState(false);

  // Legacy vault migration: earlier versions stored an XOR-obfuscated password
  // (and obfuscated notes) per credential. We no longer store passwords at all.
  // Decode any legacy passwords so the user can move them to a password manager,
  // then strip them. A credential is "legacy" while it still has a password key.
  const isLegacy = (c) => Object.prototype.hasOwnProperty.call(c, 'password');
  const readNotes = (c) => isLegacy(c) ? xorDeobfuscate(c.notes || '', licenceKey) : (c.notes || '');
  const legacyPasswords = credentials
    .filter(isLegacy)
    .map(c => ({ id: c.id, platform: c.platform, password: xorDeobfuscate(c.password, licenceKey) }))
    .filter(x => x.password);
  const showMigration = !migrationDone && legacyPasswords.length > 0;

  const migrateStripPasswords = () => {
    const cleaned = credentials.map(c => {
      if (!isLegacy(c)) return c;
      const { password, ...rest } = c; // eslint-disable-line no-unused-vars
      return { ...rest, notes: xorDeobfuscate(c.notes || '', licenceKey) };
    });
    onSave(cleaned);
    setMigrationDone(true);
  };

  const startNew = () => { setForm({ ...blank, id:`cred-${Date.now()}` }); setEditing('new'); setShowForm(true); };
  const startEdit = (cred) => {
    const { password, ...rest } = cred; // eslint-disable-line no-unused-vars
    setForm({ ...blank, ...rest, notes: readNotes(cred) });
    setEditing(cred.id); setShowForm(true);
  };
  const cancel = () => { setShowForm(false); setEditing(null); setForm({ ...blank }); };

  const saveForm = () => {
    if (!form.platform.trim()) return;
    // Passwords are intentionally never persisted (see file header). Store only
    // the reference fields; notes are kept in plain text.
    const toSave = {
      id: form.id, platform: form.platform, url: form.url, email: form.email,
      username: form.username, notes: form.notes, phase: form.phase,
      savedAt: new Date().toISOString(),
    };
    let updated;
    if (editing === 'new') updated = [...credentials, toSave];
    else updated = credentials.map(c => c.id === editing ? toSave : c);
    onSave(updated);
    cancel();
  };

  const deleteCred = (id) => {
    if (!window.confirm('Delete this platform entry?')) return;
    onSave(credentials.filter(c => c.id !== id));
  };

  const copyToClipboard = async (text, key) => {
    try { await navigator.clipboard.writeText(text); setCopied(p=>({...p,[key]:true})); setTimeout(()=>setCopied(p=>({...p,[key]:false})),1800); } catch {}
  };

  const filtered = credentials.filter(c => filterPhase==='All' || c.phase===filterPhase);

  return (
    <div className="space-y-4">
      {/* Warning banner */}
      <div className="flex items-start gap-3 rounded-2xl border border-stone-200 bg-stone-50 p-4">
        <Shield className="mt-0.5 h-4 w-4 shrink-0 text-gold-deep"/>
        <div>
          <p className="text-xs font-bold text-stone-800">We deliberately don't store passwords</p>
          <p className="mt-0.5 text-xs text-gold-deep">Keep platform URLs, usernames/emails and non-secret notes here so everything's in one place. Store the actual passwords in a dedicated password manager — never in this tool.</p>
        </div>
      </div>

      {/* One-time migration: surface legacy stored passwords, then strip them */}
      {showMigration && (
        <div className="overflow-hidden rounded-2xl border border-rose-200 bg-rose-50 shadow-sm">
          <div className="flex items-center gap-2 border-b border-rose-100 px-5 py-3">
            <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600"/>
            <p className="text-sm font-bold text-rose-900">Move your saved passwords to a password manager</p>
          </div>
          <div className="p-5">
            <p className="text-xs leading-relaxed text-rose-800">
              This vault used to store passwords — it no longer does. Copy the {legacyPasswords.length} password{legacyPasswords.length>1?'s':''} below into your password manager now. When you click <strong>“I've saved these”</strong>, they'll be permanently removed from the vault.
            </p>
            <div className="mt-3 space-y-1.5">
              {legacyPasswords.map(lp => (
                <div key={lp.id} className="flex items-center gap-2 rounded-lg border border-rose-100 bg-white px-3 py-2">
                  <span className="min-w-0 flex-1 truncate text-xs font-semibold text-stone-700">{lp.platform || 'Platform'}</span>
                  <span className="font-mono text-xs text-stone-900">{lp.password}</span>
                  <button onClick={()=>copyToClipboard(lp.password, `mig-${lp.id}`)} className="rounded p-0.5 text-stone-300 hover:text-stone-600">
                    {copied[`mig-${lp.id}`] ? <Check className="h-3 w-3 text-green-500"/> : <Copy className="h-3 w-3"/>}
                  </button>
                </div>
              ))}
            </div>
            <Button onClick={migrateStripPasswords}
              className="mt-4 flex items-center gap-1.5 rounded-xl bg-rose-600 px-4 py-2 text-xs font-bold text-white hover:bg-rose-700">
              <Trash2 className="h-3.5 w-3.5"/> I've saved these — remove passwords
            </Button>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex max-w-full flex-wrap gap-1 rounded-xl border border-stone-200 bg-white p-1 shadow-sm">
          {['All',...CHECKLIST.map(p=>p.phase)].map(ph => (
            <Button key={ph} variant="ghost" size="sm" onClick={()=>setFilterPhase(ph)}
              className={`h-auto rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-all ${filterPhase===ph?'bg-gold-deep text-white':'text-stone-500 hover:text-stone-900'}`}>
              {ph}
            </Button>
          ))}
        </div>
        <Button onClick={startNew}
          className="flex shrink-0 items-center gap-1.5 self-start rounded-xl bg-gold-deep px-3 py-2 text-xs font-bold text-white shadow-sm hover:bg-gold transition-colors sm:self-auto">
          <Plus className="h-3.5 w-3.5"/> Add Platform
        </Button>
      </div>

      {/* Quick-add suggestions */}
      {credentials.length === 0 && (
        <div className="rounded-2xl border border-gold-soft bg-white p-4 shadow-sm">
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-stone-400">Common platforms to add</p>
          <div className="flex flex-wrap gap-2">
            {PLATFORM_SUGGESTIONS.map(s => (
              <button key={s.name}
                onClick={()=>{ setForm({...blank, id:`cred-${Date.now()}`, platform:s.name, url:s.url, phase:s.phase}); setEditing('new'); setShowForm(true); }}
                className="flex items-center gap-1.5 rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-semibold text-stone-700 hover:border-gold/40 hover:bg-stone-50 hover:text-gold-deep transition-colors">
                <Plus className="h-3 w-3"/>{s.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Add/edit form */}
      {showForm && (
        <div className="overflow-hidden rounded-2xl border border-gold-soft bg-parchment/40 shadow-sm">
          <div className="flex items-center justify-between border-b border-gold-soft bg-gold/10 px-5 py-3">
            <p className="font-cinzel text-sm font-bold tracking-wide text-ink">{editing==='new'?'Add Platform':'Edit Platform'}</p>
            <button onClick={cancel} className="rounded-full p-1 text-gold hover:bg-gold/15"><X className="h-4 w-4"/></button>
          </div>
          <div className="grid gap-4 p-5 sm:grid-cols-2">
            {[
              ['Platform Name *','text','platform',Globe,'e.g. Amazon KDP'],
              ['Website URL','url','url',Link2,'https://kdp.amazon.com'],
              ['Email / Username','email','email',Mail,'account email'],
              ['Username / Display Name','text','username',User,'if different from email'],
            ].map(([lbl,type,field,Icon,ph]) => (
              <div key={field}>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-stone-500">{lbl}</label>
                <div className="relative">
                  <Icon className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-stone-400"/>
                  <input type={type} value={form[field]} onChange={e=>setForm(p=>({...p,[field]:e.target.value}))} placeholder={ph}
                    className="w-full rounded-xl border border-stone-200 bg-white py-2.5 pl-9 pr-3 text-sm text-stone-900 outline-none focus:border-gold focus:ring-2 focus:ring-gold-soft"
                    style={{userSelect:'text',WebkitUserSelect:'text'}} />
                </div>
              </div>
            ))}
            {/* Phase */}
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-stone-500">Publishing Phase</label>
              <select value={form.phase} onChange={e=>setForm(p=>({...p,phase:e.target.value}))}
                className="w-full rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-sm text-stone-900 outline-none focus:border-gold"
                style={{userSelect:'text',WebkitUserSelect:'text'}}>
                {CHECKLIST.map(p=><option key={p.phase} value={p.phase}>{p.phase}: {p.title}</option>)}
              </select>
            </div>
            {/* Notes — full width */}
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-stone-500">Notes (plan type, reminders — never passwords)</label>
              <textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} rows={3} placeholder="Non-secret reminders — plan type, which email, account number… (keep passwords in a password manager)"
                className="w-full resize-none rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-sm text-stone-900 outline-none focus:border-gold focus:ring-2 focus:ring-gold-soft"
                style={{userSelect:'text',WebkitUserSelect:'text'}} />
            </div>
          </div>
          <div className="flex gap-3 border-t border-gold-soft bg-parchment/50 px-5 py-3">
            <Button onClick={saveForm} disabled={!form.platform.trim()}
              className="flex items-center gap-1.5 rounded-xl bg-gold-deep px-4 py-2 text-xs font-bold text-white hover:bg-gold disabled:opacity-50">
              <Save className="h-3.5 w-3.5"/> Save
            </Button>
            <Button variant="outline" onClick={cancel} className="rounded-xl border border-stone-200 bg-white px-4 py-2 text-xs font-semibold text-stone-600 hover:bg-stone-50">Cancel</Button>
          </div>
        </div>
      )}

      {/* Credential cards */}
      {filtered.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {filtered.map(cred => {
            const pc      = PHASE_COLORS[cred.phase] || PHASE_COLORS['Phase 1'];
            const notes   = readNotes(cred);
            return (
              <div key={cred.id} className="overflow-hidden rounded-2xl border border-gold-soft bg-white shadow-sm">
                {/* Card header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-stone-50">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${pc.bg} ${pc.text}`}>
                      {cred.platform.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-stone-900">{cred.platform}</p>
                      <span className={`text-xs font-semibold ${pc.text}`}>{cred.phase}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={()=>startEdit(cred)} className="rounded-full p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"><Pencil className="h-3.5 w-3.5"/></button>
                    <button onClick={()=>deleteCred(cred.id)} className="rounded-full p-1.5 text-stone-300 hover:bg-red-50 hover:text-red-400"><Trash2 className="h-3.5 w-3.5"/></button>
                  </div>
                </div>
                {/* Card body */}
                <div className="divide-y divide-stone-50 px-4">
                  {cred.url && (
                    <div className="flex items-center gap-2 py-2">
                      <Globe className="h-3.5 w-3.5 shrink-0 text-stone-400"/>
                      <a href={cred.url} target="_blank" rel="noopener noreferrer" className="truncate text-xs text-gold-deep hover:underline">{cred.url}</a>
                    </div>
                  )}
                  {cred.email && (
                    <div className="flex items-center gap-2 py-2">
                      <Mail className="h-3.5 w-3.5 shrink-0 text-stone-400"/>
                      <span className="flex-1 truncate text-xs text-stone-700">{cred.email}</span>
                      <button onClick={()=>copyToClipboard(cred.email,`${cred.id}-email`)} className="rounded p-0.5 text-stone-300 hover:text-stone-600">
                        {copied[`${cred.id}-email`] ? <Check className="h-3 w-3 text-green-500"/> : <Copy className="h-3 w-3"/>}
                      </button>
                    </div>
                  )}
                  {cred.username && (
                    <div className="flex items-center gap-2 py-2">
                      <User className="h-3.5 w-3.5 shrink-0 text-stone-400"/>
                      <span className="flex-1 truncate text-xs text-stone-700">{cred.username}</span>
                      <button onClick={()=>copyToClipboard(cred.username,`${cred.id}-user`)} className="rounded p-0.5 text-stone-300 hover:text-stone-600">
                        {copied[`${cred.id}-user`] ? <Check className="h-3 w-3 text-green-500"/> : <Copy className="h-3 w-3"/>}
                      </button>
                    </div>
                  )}
                  {notes && (
                    <div className="flex items-start gap-2 py-2">
                      <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400"/>
                      <p className="text-xs leading-relaxed text-stone-500">{notes}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {filtered.length === 0 && !showForm && (
        <div className="rounded-2xl border border-dashed border-stone-200 bg-white p-10 text-center">
          <Lock className="mx-auto mb-3 h-8 w-8 text-stone-200"/>
          <p className="text-sm font-semibold text-stone-400">No credentials stored yet</p>
          <p className="mt-1 text-xs text-stone-400">Add your platform logins to keep everything in one place</p>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   METADATA AUDIT DATA
═══════════════════════════════════════════════════════════ */
/* ─── Comprehensive BISAC — all FIC & YAF fiction codes ─── */
/* Source: BISG BISAC Subject Headings. Verify latest at bisg.org/page/bisacedition */
const BISAC_GROUPS = [
  {
    group:'Fantasy',
    codes:[
      { code:'FIC009000', label:'FIC009000 — Fantasy / General',               hint:'Use as tertiary if a more specific code fits.' },
      { code:'FIC009010', label:'FIC009010 — Fantasy / Action & Adventure',    hint:'Quest-driven fantasy where pace and action dominate.' },
      { code:'FIC009020', label:'FIC009020 — Fantasy / Contemporary',          hint:'Fantasy set in the present-day real world.' },
      { code:'FIC009030', label:'FIC009030 — Fantasy / Dark Fantasy',          hint:'Blends horror or grim themes with fantasy elements.' },
      { code:'FIC009040', label:'FIC009040 — Fantasy / Epic',                  hint:'Large-scale, world-building-heavy fantasy. Most comparable to Tolkien tradition.' },
      { code:'FIC009050', label:'FIC009050 — Fantasy / Historical',            hint:'Fantasy set in a specific historical period.' },
      { code:'FIC009060', label:'FIC009060 — Fantasy / Humorous',              hint:'Comedy-forward fantasy.' },
      { code:'FIC009070', label:'FIC009070 — Fantasy / Military',              hint:'Military strategy, battles, and warfare central to the plot.' },
      { code:'FIC009080', label:'FIC009080 — Fantasy / Paranormal',            hint:'Includes vampires, werewolves, witches, ghosts in fantasy settings.' },
      { code:'FIC009090', label:'FIC009090 — Fantasy / Urban',                 hint:'Fantasy set in a contemporary city environment.' },
      { code:'FIC009100', label:'FIC009100 — Fantasy / Romantic ★ Romantasy', hint:'THE primary BISAC for romantasy. Fantasy is the primary genre; romance is the subplot.' },
      { code:'FIC009110', label:'FIC009110 — Fantasy / Coming of Age',         hint:'Protagonist\'s growth and identity central to the arc.' },
      { code:'FIC009120', label:'FIC009120 — Fantasy / Mythic',                hint:'Draws heavily on specific mythological traditions.' },
    ],
  },
  {
    group:'Romance',
    codes:[
      { code:'FIC027000', label:'FIC027000 — Romance / General',               hint:'Use when no specific subgenre fits.' },
      { code:'FIC027010', label:'FIC027010 — Romance / Action & Adventure',    hint:'Action-driven romance.' },
      { code:'FIC027020', label:'FIC027020 — Romance / Clean & Wholesome',     hint:'No explicit content; suitable for all ages.' },
      { code:'FIC027030', label:'FIC027030 — Romance / Contemporary',          hint:'Modern-day real-world settings.' },
      { code:'FIC027040', label:'FIC027040 — Romance / Erotica',               hint:'Explicit sexual content is central to the book.' },
      { code:'FIC027050', label:'FIC027050 — Romance / Fantasy ★ Romantasy',  hint:'Romance is the primary genre; fantasy is the setting/element.' },
      { code:'FIC027060', label:'FIC027060 — Romance / Gothic',                hint:'Dark, atmospheric, brooding romantic settings.' },
      { code:'FIC027070', label:'FIC027070 — Romance / Historical / General',  hint:'Romance set in a historical period, no specific era.' },
      { code:'FIC027080', label:'FIC027080 — Romance / Historical / Ancient World', hint:'Set in antiquity: Greece, Rome, Egypt, etc.' },
      { code:'FIC027090', label:'FIC027090 — Romance / Historical / Medieval', hint:'Set in the medieval period.' },
      { code:'FIC027100', label:'FIC027100 — Romance / Historical / Regency',  hint:'Set in Regency England, c.1811–1820.' },
      { code:'FIC027110', label:'FIC027110 — Romance / Historical / Scottish', hint:'Set in Scotland, any period.' },
      { code:'FIC027120', label:'FIC027120 — Romance / Historical / Victorian', hint:'Set in the Victorian era, c.1837–1901.' },
      { code:'FIC027130', label:'FIC027130 — Romance / Holiday',               hint:'Seasonal/holiday-themed romance.' },
      { code:'FIC027140', label:'FIC027140 — Romance / Military & Action',     hint:'Military settings and service members as central.' },
      { code:'FIC027150', label:'FIC027150 — Romance / Multicultural & Interracial', hint:'Romantic relationships across cultural/racial lines featured prominently.' },
      { code:'FIC027160', label:'FIC027160 — Romance / Mystery',               hint:'Romance blended with a mystery plot.' },
      { code:'FIC027170', label:'FIC027170 — Romance / New Adult',             hint:'Protagonists aged 18–25; college/early adulthood themes.' },
      { code:'FIC027180', label:'FIC027180 — Romance / Paranormal',            hint:'Vampires, werewolves, witches, shifters, etc.' },
      { code:'FIC027190', label:'FIC027190 — Romance / Romantic Comedy',       hint:'Humour and lightness central to the romantic arc.' },
      { code:'FIC027200', label:'FIC027200 — Romance / Science Fiction',       hint:'Romance set in a SF/futuristic setting.' },
      { code:'FIC027210', label:'FIC027210 — Romance / Seaside & Small Town',  hint:'Small-town or coastal settings central to tone.' },
      { code:'FIC027220', label:'FIC027220 — Romance / Sports',                hint:'Sports, athletes, or sporting events central.' },
      { code:'FIC027230', label:'FIC027230 — Romance / Suspense',              hint:'Thriller/suspense woven into the romantic arc.' },
      { code:'FIC027240', label:'FIC027240 — Romance / Time Travel',           hint:'Temporal travel as a core plot mechanic.' },
      { code:'FIC027250', label:'FIC027250 — Romance / Western',               hint:'Western/frontier settings.' },
      { code:'FIC027260', label:'FIC027260 — Romance / LGBTQ+ / General',      hint:'LGBTQ+ romance, no specific subgroup.' },
      { code:'FIC027270', label:'FIC027270 — Romance / LGBTQ+ / Gay',          hint:'M/M romance.' },
      { code:'FIC027280', label:'FIC027280 — Romance / LGBTQ+ / Lesbian',      hint:'F/F romance.' },
      { code:'FIC027290', label:'FIC027290 — Romance / LGBTQ+ / Bisexual',     hint:'Bisexual protagonist romance.' },
      { code:'FIC027300', label:'FIC027300 — Romance / LGBTQ+ / Transgender',  hint:'Transgender protagonist romance.' },
    ],
  },
  {
    group:'Mystery & Detective',
    codes:[
      { code:'FIC022000', label:'FIC022000 — Mystery & Detective / General',           hint:'' },
      { code:'FIC022010', label:'FIC022010 — Mystery & Detective / Amateur Sleuth',    hint:'Non-professional detective protagonist.' },
      { code:'FIC022020', label:'FIC022020 — Mystery & Detective / British Detectives',hint:'Tradition of British detective fiction.' },
      { code:'FIC022030', label:'FIC022030 — Mystery & Detective / Hard-Boiled',       hint:'Gritty, cynical, American noir tradition.' },
      { code:'FIC022040', label:'FIC022040 — Mystery & Detective / Historical',        hint:'Historical settings; not contemporary.' },
      { code:'FIC022050', label:'FIC022050 — Mystery & Detective / International Crime',hint:'Non-UK, non-US settings.' },
      { code:'FIC022060', label:'FIC022060 — Mystery & Detective / Cozy / General',    hint:'Gentle, humorous, low-violence mysteries.' },
      { code:'FIC022070', label:'FIC022070 — Mystery & Detective / Police Procedural', hint:'Police or law enforcement investigation central.' },
      { code:'FIC022080', label:'FIC022080 — Mystery & Detective / Private Investigator',hint:'PI as protagonist.' },
      { code:'FIC022090', label:'FIC022090 — Mystery & Detective / Traditional British',hint:'Golden Age / classic British style.' },
      { code:'FIC022100', label:'FIC022100 — Mystery & Detective / Women Sleuths',     hint:'Female protagonist detective.' },
      { code:'FIC022110', label:'FIC022110 — Mystery & Detective / Cozy / Culinary',   hint:'Foodie/culinary-themed cozy mysteries.' },
      { code:'FIC022120', label:'FIC022120 — Mystery & Detective / Cozy / Crafts & Hobbies',hint:'Hobby-themed cozy mysteries.' },
      { code:'FIC022130', label:'FIC022130 — Mystery & Detective / Cozy / Paranormal', hint:'Paranormal cozy mysteries.' },
    ],
  },
  {
    group:'Thrillers',
    codes:[
      { code:'FIC035000', label:'FIC035000 — Thrillers / General',             hint:'' },
      { code:'FIC035010', label:'FIC035010 — Thrillers / Crime',               hint:'Crime-driven thriller.' },
      { code:'FIC035020', label:'FIC035020 — Thrillers / Espionage',           hint:'Spy and intelligence thriller.' },
      { code:'FIC035030', label:'FIC035030 — Thrillers / Legal',               hint:'Courtroom and legal thriller.' },
      { code:'FIC035040', label:'FIC035040 — Thrillers / Medical',             hint:'Medical/pharmaceutical thriller.' },
      { code:'FIC035050', label:'FIC035050 — Thrillers / Military',            hint:'Military action thriller.' },
      { code:'FIC035060', label:'FIC035060 — Thrillers / Psychological',       hint:'Internal/psychological tension drives the thriller.' },
      { code:'FIC035070', label:'FIC035070 — Thrillers / Political',           hint:'Political machinations and power as the thriller focus.' },
      { code:'FIC035080', label:'FIC035080 — Thrillers / Supernatural',        hint:'Supernatural elements woven into thriller tension.' },
      { code:'FIC035090', label:'FIC035090 — Thrillers / Technological',       hint:'Tech/hacking/cyber as thriller driver.' },
    ],
  },
  {
    group:'Science Fiction',
    codes:[
      { code:'FIC028000', label:'FIC028000 — Science Fiction / General',                hint:'' },
      { code:'FIC028010', label:'FIC028010 — Science Fiction / Action & Adventure',     hint:'SF where action and pacing drive the plot.' },
      { code:'FIC028020', label:'FIC028020 — Science Fiction / Alien Contact',          hint:'First contact or extraterrestrial communication.' },
      { code:'FIC028030', label:'FIC028030 — Science Fiction / Apocalyptic & Post-Apocalyptic', hint:'End-of-world or after-collapse settings.' },
      { code:'FIC028040', label:'FIC028040 — Science Fiction / Cyberpunk',              hint:'High-tech, dystopian, hacker culture SF.' },
      { code:'FIC028050', label:'FIC028050 — Science Fiction / Genetic Engineering',   hint:'Biotech, cloning, genetic manipulation.' },
      { code:'FIC028060', label:'FIC028060 — Science Fiction / Hard Science Fiction',  hint:'Scientifically rigorous; extrapolation from real science.' },
      { code:'FIC028070', label:'FIC028070 — Science Fiction / Military',              hint:'Military organisation and combat in SF setting.' },
      { code:'FIC028080', label:'FIC028080 — Science Fiction / Space Exploration',    hint:'Exploration of space and alien worlds.' },
      { code:'FIC028090', label:'FIC028090 — Science Fiction / Space Opera',          hint:'Epic, dramatic SF on a galactic scale.' },
      { code:'FIC028100', label:'FIC028100 — Science Fiction / Steampunk',            hint:'Victorian-era technology and aesthetics in SF setting.' },
      { code:'FIC028110', label:'FIC028110 — Science Fiction / Time Travel',          hint:'Temporal travel as a core mechanic.' },
      { code:'FIC028120', label:'FIC028120 — Science Fiction / Biopunk',              hint:'Biological manipulation and body horror meets SF.' },
      { code:'FIC028130', label:'FIC028130 — Science Fiction / Humorous',             hint:'Comedy-forward SF.' },
    ],
  },
  {
    group:'Historical Fiction',
    codes:[
      { code:'FIC014000', label:'FIC014000 — Historical / General',            hint:'Broad historical fiction; no specific era specified.' },
      { code:'FIC014010', label:'FIC014010 — Historical / Ancient',            hint:'Antiquity: pre-500 CE.' },
      { code:'FIC014020', label:'FIC014020 — Historical / Medieval',           hint:'c.500–1500 CE.' },
      { code:'FIC014030', label:'FIC014030 — Historical / Renaissance',        hint:'c.1300–1700 CE; Italian Renaissance and equivalent periods.' },
    ],
  },
  {
    group:'Horror',
    codes:[
      { code:'FIC015000', label:'FIC015000 — Horror',                          hint:'Fear and dread as primary emotional register.' },
    ],
  },
  {
    group:'Literary & General Fiction',
    codes:[
      { code:'FIC000000', label:'FIC000000 — Fiction / General',               hint:'Broad general fiction; use only if nothing more specific fits.' },
      { code:'FIC002000', label:'FIC002000 — Action & Adventure',              hint:'Action-forward plots not otherwise categorised.' },
      { code:'FIC005000', label:'FIC005000 — Biographical',                    hint:'Fictionalised real people or events.' },
      { code:'FIC006000', label:'FIC006000 — Classics',                        hint:'Canonical literary works; rarely used for new releases.' },
      { code:'FIC007000', label:'FIC007000 — Coming of Age',                   hint:'Bildungsroman; protagonist\'s development from youth to maturity.' },
      { code:'FIC008000', label:'FIC008000 — Crime',                           hint:'Crime fiction not fitting mystery or thriller subgenres.' },
      { code:'FIC012000', label:'FIC012000 — Ghost',                           hint:'Ghost and haunting as central theme.' },
      { code:'FIC013000', label:'FIC013000 — Gothic',                          hint:'Gothic atmosphere, dark romance, psychological dread.' },
      { code:'FIC016000', label:'FIC016000 — Humorous / General',              hint:'Comedy as primary register.' },
      { code:'FIC016010', label:'FIC016010 — Humorous / Black Humor',          hint:'Dark or satirical humour.' },
      { code:'FIC018000', label:'FIC018000 — Legal',                           hint:'Legal proceedings and law as central themes.' },
      { code:'FIC019000', label:'FIC019000 — Literary',                        hint:'Character-driven; prioritises language and theme over plot.' },
      { code:'FIC021000', label:'FIC021000 — Media Tie-In',                    hint:'Fiction based on film, TV, or game IP.' },
      { code:'FIC023000', label:'FIC023000 — Native American & Aboriginal',    hint:'Indigenous North American perspectives.' },
      { code:'FIC024000', label:'FIC024000 — Occult & Supernatural',           hint:'Occult, metaphysical, or supernatural themes.' },
      { code:'FIC026000', label:'FIC026000 — Political',                       hint:'Political power, governance, and intrigue as primary themes.' },
      { code:'FIC029000', label:'FIC029000 — Short Stories (Single Author)',   hint:'Single-author story collection.' },
      { code:'FIC030000', label:'FIC030000 — Anthologies (Multiple Authors)',  hint:'Multi-author collected fiction.' },
      { code:'FIC031000', label:'FIC031000 — Satire',                          hint:'Satirical fiction; social commentary through irony.' },
      { code:'FIC032000', label:'FIC032000 — Sea Stories',                     hint:'Maritime settings and naval adventure.' },
      { code:'FIC033000', label:'FIC033000 — Small Town & Rural',              hint:'Rural or small-town settings central to tone.' },
      { code:'FIC034000', label:'FIC034000 — Sports',                          hint:'Sports and athletics as central subject.' },
      { code:'FIC036000', label:'FIC036000 — Urban',                           hint:'Urban settings and city life as defining element.' },
      { code:'FIC037000', label:'FIC037000 — Visionary & Metaphysical',        hint:'Spiritual, metaphysical, or visionary themes.' },
      { code:'FIC038000', label:'FIC038000 — War & Military',                  hint:'War, combat, and military life as central subject (not genre thriller).' },
      { code:'FIC039000', label:'FIC039000 — Westerns',                        hint:'American frontier and western settings.' },
      { code:'FIC040000', label:'FIC040000 — Women',                           hint:'Women\'s lives, experiences, and perspectives as primary focus.' },
      { code:'FIC043000', label:'FIC043000 — Magical Realism',                 hint:'Realistic world with magical elements woven in; literary in style.' },
      { code:'FIC044000', label:'FIC044000 — Indigenous',                      hint:'Broad indigenous perspectives (non-North American).' },
      { code:'FIC045000', label:'FIC045000 — Dystopian',                       hint:'Oppressive future or alternate societies.' },
      { code:'FIC046000', label:'FIC046000 — Fairy Tales, Folk Tales, Legends & Mythology', hint:'Retellings, reimaginings, or original myth-adjacent fiction.' },
    ],
  },
  {
    group:'LGBTQ+ Fiction',
    codes:[
      { code:'FIC011000', label:'FIC011000 — LGBTQ+ / General',                hint:'Broad LGBTQ+ fiction.' },
      { code:'FIC011010', label:'FIC011010 — LGBTQ+ / Gay',                    hint:'Gay male perspective or M/M.' },
      { code:'FIC011020', label:'FIC011020 — LGBTQ+ / Lesbian',                hint:'Lesbian perspective or F/F.' },
      { code:'FIC011030', label:'FIC011030 — LGBTQ+ / Bisexual',               hint:'Bisexual protagonist or themes.' },
      { code:'FIC011040', label:'FIC011040 — LGBTQ+ / Transgender',            hint:'Transgender protagonist or themes.' },
      { code:'FIC011050', label:'FIC011050 — LGBTQ+ / Intersex',               hint:'Intersex protagonist or themes.' },
      { code:'FIC011060', label:'FIC011060 — LGBTQ+ / Non-binary',             hint:'Non-binary or genderqueer protagonist or themes.' },
    ],
  },
  {
    group:'African American & Black Fiction',
    codes:[
      { code:'FIC004000', label:'FIC004000 — African American & Black / General',          hint:'' },
      { code:'FIC004010', label:'FIC004010 — African American & Black / Christian',        hint:'' },
      { code:'FIC004020', label:'FIC004020 — African American & Black / Mystery & Detective',hint:'' },
      { code:'FIC004030', label:'FIC004030 — African American & Black / Romance',          hint:'' },
      { code:'FIC004040', label:'FIC004040 — African American & Black / Urban & Street Lit',hint:'' },
    ],
  },
  {
    group:'Young Adult Fiction (YAF)',
    codes:[
      { code:'YAF000000', label:'YAF000000 — YA Fiction / General',                hint:'Use only if nothing more specific fits.' },
      { code:'YAF019000', label:'YAF019000 — YA Fiction / Fantasy / General',      hint:'General YA fantasy.' },
      { code:'YAF019010', label:'YAF019010 — YA Fiction / Fantasy / Action & Adventure', hint:'' },
      { code:'YAF019020', label:'YAF019020 — YA Fiction / Fantasy / Contemporary', hint:'YA urban/contemporary fantasy.' },
      { code:'YAF019030', label:'YAF019030 — YA Fiction / Fantasy / Dark Fantasy', hint:'YA dark fantasy.' },
      { code:'YAF019040', label:'YAF019040 — YA Fiction / Fantasy / Historical',   hint:'YA historical fantasy.' },
      { code:'YAF019050', label:'YAF019050 — YA Fiction / Fantasy / Epic',         hint:'YA epic fantasy.' },
      { code:'YAF019060', label:'YAF019060 — YA Fiction / Fantasy / Paranormal',   hint:'YA paranormal fantasy.' },
      { code:'YAF019070', label:'YAF019070 — YA Fiction / Fantasy / Romantic',     hint:'YA romantasy.' },
      { code:'YAF019080', label:'YAF019080 — YA Fiction / Fantasy / Mythic',       hint:'YA mythology-based fantasy.' },
      { code:'YAF052000', label:'YAF052000 — YA Fiction / Romance / General',      hint:'' },
      { code:'YAF052010', label:'YAF052010 — YA Fiction / Romance / Contemporary', hint:'' },
      { code:'YAF052020', label:'YAF052020 — YA Fiction / Romance / Historical',   hint:'' },
      { code:'YAF052030', label:'YAF052030 — YA Fiction / Romance / Paranormal',   hint:'YA paranormal romance.' },
      { code:'YAF052040', label:'YAF052040 — YA Fiction / Romance / Clean',        hint:'' },
      { code:'YAF052050', label:'YAF052050 — YA Fiction / Romance / LGBTQ+',       hint:'' },
      { code:'YAF062000', label:'YAF062000 — YA Fiction / Science Fiction / General', hint:'' },
      { code:'YAF062010', label:'YAF062010 — YA Fiction / Science Fiction / Dystopian', hint:'YA dystopian SF.' },
      { code:'YAF062020', label:'YAF062020 — YA Fiction / Science Fiction / Space Opera', hint:'' },
      { code:'YAF048000', label:'YAF048000 — YA Fiction / Horror',                 hint:'' },
      { code:'YAF022000', label:'YAF022000 — YA Fiction / Coming of Age',          hint:'' },
      { code:'YAF012000', label:'YAF012000 — YA Fiction / Adventure',              hint:'' },
      { code:'YAF025000', label:'YAF025000 — YA Fiction / LGBTQ+',                hint:'' },
      { code:'YAF015000', label:'YAF015000 — YA Fiction / Social Themes / General',hint:'Social issues and contemporary challenges.' },
      { code:'YAF071000', label:'YAF071000 — YA Fiction / Mysteries & Detective Stories', hint:'' },
      { code:'YAF068000', label:'YAF068000 — YA Fiction / Thrillers & Suspense',   hint:'' },
    ],
  },
];

/* Flatten BISAC groups → simple array for search */
const BISAC_FLAT = BISAC_GROUPS.flatMap(g => g.codes.map(c => ({ ...c, group: g.group })));

/* ─── Comprehensive Thema — all major F* fiction codes ─── */
/* Source: EDItEUR Thema v1.5. Verify & browse at ns.editeur.org/thema */
const THEMA_GROUPS = [
  {
    group:'Fantasy',
    codes:[
      { code:'FM',   label:'FM — Fantasy (general)',                hint:'Use when no subcode fits.' },
      { code:'FMD',  label:'FMD — Fantasy: dark fantasy',           hint:'Blends horror or bleakness with fantasy elements.' },
      { code:'FME',  label:'FME — Fantasy: contemporary & urban',   hint:'Fantasy set in the present-day real world.' },
      { code:'FMF',  label:'FMF — Fantasy: epic & high fantasy',    hint:'Large-scale world-building; the classic epic tradition.' },
      { code:'FMG',  label:'FMG — Fantasy: mythological',           hint:'Draws heavily on myth, legend, or folklore traditions.' },
      { code:'FMH',  label:'FMH — Fantasy: historical',             hint:'Fantasy set against a real historical backdrop.' },
      { code:'FMK',  label:'FMK — Fantasy: humorous',               hint:'Comedy-forward fantasy.' },
      { code:'FML',  label:'FML — Fantasy: low fantasy',            hint:'Minimal magic; realistic worldbuilding with fantastical elements.' },
      { code:'FMQ',  label:'FMQ — Fantasy: quest fantasy',          hint:'Journey and quest as the primary plot structure.' },
      { code:'FMR',  label:'FMR — Fantasy: romantic ★ Romantasy',  hint:'THE primary Thema for romantasy. Equivalent of FIC009100.' },
      { code:'FMS',  label:'FMS — Fantasy: sword & sorcery',        hint:'Action-forward, magic and combat.' },
      { code:'FMT',  label:'FMT — Fantasy: teen / coming-of-age',   hint:'Fantasy where protagonist\'s growth is the central arc.' },
      { code:'FMV',  label:'FMV — Fantasy: magic realism',          hint:'Magical elements woven into otherwise realistic narrative.' },
    ],
  },
  {
    group:'Romantic Fiction',
    codes:[
      { code:'FC',   label:'FC — Romantic fiction (general)',        hint:'Use when no subcode fits.' },
      { code:'FCA',  label:'FCA — Romance: historical',              hint:'Romance set in a historical period.' },
      { code:'FCB',  label:'FCB — Romance: fantasy, paranormal & supernatural ★', hint:'Romance-primary, fantasy/paranormal world. Equivalent of FIC027050.' },
      { code:'FCC',  label:'FCC — Romance: contemporary',           hint:'Modern-day real-world setting.' },
      { code:'FCD',  label:'FCD — Erotic fiction',                   hint:'Explicit sexual content central.' },
      { code:'FCF',  label:'FCF — Romance: LGBTQ+',                  hint:'LGBTQ+ romance.' },
      { code:'FCG',  label:'FCG — Romance: romantic suspense',       hint:'Thriller/suspense woven into the romance.' },
      { code:'FCH',  label:'FCH — Romance: holiday',                 hint:'Seasonal / holiday-themed.' },
      { code:'FCK',  label:'FCK — Romance: clean / inspirational',   hint:'No explicit content; often faith-based.' },
      { code:'FCM',  label:'FCM — Romance: multicultural',           hint:'Cross-cultural relationships featured prominently.' },
      { code:'FCQ',  label:'FCQ — Romance: romantic comedy',         hint:'Humour central to the romantic arc.' },
      { code:'FCR',  label:'FCR — Romance: science fiction',         hint:'Romance in a SF or futuristic setting.' },
      { code:'FCT',  label:'FCT — Romance: small town',              hint:'Small-town community central to tone.' },
      { code:'FCW',  label:'FCW — Romance: western',                 hint:'Western/frontier settings.' },
    ],
  },
  {
    group:'Crime, Mystery & Thriller',
    codes:[
      { code:'FF',   label:'FF — Crime & mystery fiction (general)', hint:'' },
      { code:'FFC',  label:'FFC — Crime & mystery: cozy',            hint:'Gentle, low-violence mysteries.' },
      { code:'FFD',  label:'FFD — Crime & mystery: historical',      hint:'Historical setting.' },
      { code:'FFE',  label:'FFE — Crime & mystery: hard-boiled',     hint:'Gritty, cynical, American noir tradition.' },
      { code:'FFG',  label:'FFG — Crime & mystery: police procedural',hint:'Law enforcement investigation central.' },
      { code:'FFH',  label:'FFH — Crime & mystery: amateur sleuth',  hint:'Non-professional detective protagonist.' },
      { code:'FFJ',  label:'FFJ — Crime & mystery: legal thriller',  hint:'Courtroom and legal proceedings.' },
      { code:'FFK',  label:'FFK — Crime & mystery: medical thriller', hint:'Medical/pharmaceutical setting.' },
      { code:'FFL',  label:'FFL — Crime & mystery: political thriller',hint:'Political power as the thriller focus.' },
      { code:'FFM',  label:'FFM — Crime & mystery: espionage/spy',   hint:'Intelligence agencies and espionage.' },
      { code:'FFN',  label:'FFN — Crime & mystery: psychological thriller', hint:'Internal psychological tension.' },
      { code:'FFQ',  label:'FFQ — Crime & mystery: supernatural thriller', hint:'Supernatural elements in a thriller structure.' },
      { code:'FFS',  label:'FFS — Crime & mystery: women detectives', hint:'Female protagonist detective.' },
      { code:'FFT',  label:'FFT — Crime & mystery: private investigator', hint:'PI as protagonist.' },
    ],
  },
  {
    group:'Science Fiction',
    codes:[
      { code:'FK',   label:'FK — Science fiction (general)',          hint:'Use when no subcode fits.' },
      { code:'FKA',  label:'FKA — Science fiction: action & adventure',hint:'Fast-paced, plot-driven SF.' },
      { code:'FKC',  label:'FKC — Science fiction: cyberpunk',        hint:'High-tech, dystopian hacker culture.' },
      { code:'FKG',  label:'FKG — Science fiction: generation ships & colony', hint:'Space colonisation and generation ships.' },
      { code:'FKM',  label:'FKM — Science fiction: military',         hint:'Military organisation and combat in SF.' },
      { code:'FKP',  label:'FKP — Science fiction: apocalyptic & post-apocalyptic', hint:'End-of-world scenarios.' },
      { code:'FKS',  label:'FKS — Science fiction: space opera',      hint:'Epic, dramatic SF on galactic scale.' },
      { code:'FKT',  label:'FKT — Science fiction: time travel',      hint:'Temporal travel as core mechanic.' },
      { code:'FKV',  label:'FKV — Science fiction: virtual realities & simulated worlds', hint:'VR, simulations, uploaded minds.' },
      { code:'FKW',  label:'FKW — Science fiction: dystopian & utopian', hint:'Oppressive or idealised future societies.' },
    ],
  },
  {
    group:'Horror',
    codes:[
      { code:'FH',   label:'FH — Horror & ghost fiction (general)',   hint:'' },
      { code:'FHD',  label:'FHD — Horror: dark fantasy',              hint:'Horror-adjacent dark fantasy.' },
      { code:'FHG',  label:'FHG — Horror: gothic',                    hint:'Gothic atmosphere; psychological dread.' },
      { code:'FHM',  label:'FHM — Horror: monsters',                  hint:'Creature-driven horror.' },
      { code:'FHP',  label:'FHP — Horror: psychological',             hint:'Internal psychological horror.' },
      { code:'FHS',  label:'FHS — Horror: supernatural',              hint:'Ghosts, demons, supernatural entities.' },
    ],
  },
  {
    group:'Historical Fiction',
    codes:[
      { code:'FP',   label:'FP — Historical fiction (general)',       hint:'Broad historical fiction.' },
      { code:'FPC',  label:'FPC — Historical fiction: ancient & classical', hint:'Pre-500 CE.' },
      { code:'FPF',  label:'FPF — Historical fiction: medieval & Renaissance', hint:'c.500–1700 CE.' },
      { code:'FPJ',  label:'FPJ — Historical fiction: 18th century',  hint:'1700–1800s.' },
      { code:'FPL',  label:'FPL — Historical fiction: Victorian & Edwardian', hint:'c.1837–1910.' },
      { code:'FPM',  label:'FPM — Historical fiction: World Wars',    hint:'WWI and WWII era.' },
      { code:'FPN',  label:'FPN — Historical fiction: post-WWII',     hint:'1945 to late 20th century.' },
    ],
  },
  {
    group:'Literary & General Fiction',
    codes:[
      { code:'FA',   label:'FA — Modern & contemporary fiction',      hint:'Literary/general fiction set in the modern era.' },
      { code:'FB',   label:'FB — Biographical fiction',               hint:'Fictionalised real people or events.' },
      { code:'FQ',   label:'FQ — Myths, legends, fairy tales & fables', hint:'Myth, folklore, and retelling traditions.' },
      { code:'FR',   label:'FR — War fiction',                        hint:'War as a central subject.' },
      { code:'FS',   label:'FS — Short stories',                      hint:'Short story collections.' },
      { code:'FU',   label:'FU — Graphic novels & manga (adult)',     hint:'Adult sequential art.' },
      { code:'FV',   label:'FV — Erotica (general)',                   hint:'Explicit content.' },
      { code:'FW',   label:'FW — Westerns',                           hint:'Western/frontier fiction.' },
      { code:'FX',   label:'FX — Humour & satire',                    hint:'Comedy and satirical literary fiction.' },
      { code:'FXS',  label:'FXS — Humour: satirical',                 hint:'Sharp social satire.' },
    ],
  },
  {
    group:'Children\'s / Young Adult (YF)',
    codes:[
      { code:'YF',   label:'YF — Children\'s & young adult fiction (general)', hint:'' },
      { code:'YFB',  label:'YFB — YA / Teen: general fiction',        hint:'' },
      { code:'YFC',  label:'YFC — YA / Teen: adventure',              hint:'' },
      { code:'YFD',  label:'YFD — YA / Teen: mystery & detective',    hint:'' },
      { code:'YFG',  label:'YFG — YA / Teen: horror',                 hint:'' },
      { code:'YFH',  label:'YFH — YA / Teen: fantasy ★',              hint:'Primary Thema for YA fantasy.' },
      { code:'YFK',  label:'YFK — YA / Teen: science fiction',        hint:'' },
      { code:'YFL',  label:'YFL — YA / Teen: historical fiction',     hint:'' },
      { code:'YFM',  label:'YFM — YA / Teen: myths & legends',        hint:'' },
      { code:'YFN',  label:'YFN — YA / Teen: humour',                 hint:'' },
      { code:'YFQ',  label:'YFQ — YA / Teen: issues fiction',         hint:'Social issues, identity, coming-of-age themes.' },
      { code:'YFR',  label:'YFR — YA / Teen: romance ★',              hint:'Primary Thema for YA romance.' },
      { code:'YFS',  label:'YFS — YA / Teen: short stories',          hint:'' },
      { code:'YFT',  label:'YFT — YA / Teen: thriller',               hint:'' },
    ],
  },
];

/* Flatten Thema groups → simple array for search */
const THEMA_FLAT = THEMA_GROUPS.flatMap(g => g.codes.map(c => ({ ...c, group: g.group })));

const AMAZON_CATEGORIES = [
  /* ── Kindle ebooks ── */
  'Kindle Store > Kindle eBooks > Romance > Fantasy',
  'Kindle Store > Kindle eBooks > Romance > Paranormal',
  'Kindle Store > Kindle eBooks > Romance > Gothic',
  'Kindle Store > Kindle eBooks > Romance > New Adult & College',
  'Kindle Store > Kindle eBooks > Romance > Historical',
  'Kindle Store > Kindle eBooks > Romance > Romantic Suspense',
  'Kindle Store > Kindle eBooks > Romance > Romantic Comedy',
  'Kindle Store > Kindle eBooks > Romance > LGBTQ+',
  'Kindle Store > Kindle eBooks > Romance > Contemporary',
  'Kindle Store > Kindle eBooks > Romance > Clean & Wholesome',
  'Kindle Store > Kindle eBooks > Science Fiction & Fantasy > Fantasy > Epic',
  'Kindle Store > Kindle eBooks > Science Fiction & Fantasy > Fantasy > Romantic',
  'Kindle Store > Kindle eBooks > Science Fiction & Fantasy > Fantasy > Dark Fantasy',
  'Kindle Store > Kindle eBooks > Science Fiction & Fantasy > Fantasy > Coming of Age',
  'Kindle Store > Kindle eBooks > Science Fiction & Fantasy > Fantasy > Sword & Sorcery',
  'Kindle Store > Kindle eBooks > Science Fiction & Fantasy > Fantasy > Myths & Legends',
  'Kindle Store > Kindle eBooks > Science Fiction & Fantasy > Fantasy > Historical',
  'Kindle Store > Kindle eBooks > Science Fiction & Fantasy > Fantasy > Urban',
  'Kindle Store > Kindle eBooks > Science Fiction & Fantasy > Fantasy > Action & Adventure',
  'Kindle Store > Kindle eBooks > Science Fiction & Fantasy > Fantasy > Paranormal & Urban',
  'Kindle Store > Kindle eBooks > Science Fiction & Fantasy > Fantasy > Fairy Tales',
  'Kindle Store > Kindle eBooks > Science Fiction & Fantasy > Science Fiction > Dystopian',
  'Kindle Store > Kindle eBooks > Science Fiction & Fantasy > Science Fiction > Space Opera',
  'Kindle Store > Kindle eBooks > Literature & Fiction > Action & Adventure > Fantasy',
  'Kindle Store > Kindle eBooks > Literature & Fiction > Historical Fiction',
  'Kindle Store > Kindle eBooks > Literature & Fiction > Literary Fiction',
  'Kindle Store > Kindle eBooks > Literature & Fiction > Gothic & Romantic',
  'Kindle Store > Kindle eBooks > Horror > Dark Fantasy',
  'Kindle Store > Kindle eBooks > Horror > Supernatural',
  'Kindle Store > Kindle eBooks > Horror > Gothic',
  'Kindle Store > Kindle eBooks > Mystery, Thriller & Suspense > Thrillers > Paranormal',
  'Kindle Store > Kindle eBooks > Mystery, Thriller & Suspense > Mystery > Paranormal',
  'Kindle Store > Kindle eBooks > Teen & Young Adult > Science Fiction & Fantasy > Fantasy > Epic',
  'Kindle Store > Kindle eBooks > Teen & Young Adult > Science Fiction & Fantasy > Fantasy > Romantic',
  'Kindle Store > Kindle eBooks > Teen & Young Adult > Science Fiction & Fantasy > Fantasy > Dark Fantasy',
  'Kindle Store > Kindle eBooks > Teen & Young Adult > Romance > Fantasy & Futuristic',
  'Kindle Store > Kindle eBooks > Teen & Young Adult > Romance > Paranormal',
  /* ── Print Books ── */
  'Books > Romance > Fantasy, Futuristic & Ghost',
  'Books > Romance > Paranormal',
  'Books > Romance > Historical',
  'Books > Romance > Gothic',
  'Books > Romance > New Adult & College',
  'Books > Science Fiction & Fantasy > Fantasy > Epic',
  'Books > Science Fiction & Fantasy > Fantasy > Romantic',
  'Books > Science Fiction & Fantasy > Fantasy > Dark Fantasy',
  'Books > Science Fiction & Fantasy > Fantasy > Sword & Sorcery',
  'Books > Science Fiction & Fantasy > Fantasy > Myths & Legends',
  'Books > Science Fiction & Fantasy > Fantasy > Historical',
  'Books > Science Fiction & Fantasy > Fantasy > Urban',
  'Books > Science Fiction & Fantasy > Fantasy > Coming of Age',
  'Books > Literature & Fiction > Action & Adventure > Fantasy',
  'Books > Literature & Fiction > Gothic & Romantic',
  'Books > Literature & Fiction > Historical Fiction',
  'Books > Literature & Fiction > Literary Fiction',
  'Books > Horror > Dark Fantasy',
  'Books > Teen & Young Adult > Science Fiction & Fantasy > Fantasy > Epic',
  'Books > Teen & Young Adult > Science Fiction & Fantasy > Fantasy > Romantic',
  'Books > Teen & Young Adult > Romance > Fantasy & Futuristic',
];

const TRIM_SIZES = [
  '5" × 8" (most common trade paperback)',
  '5.06" × 7.81" (KDP standard)',
  '5.25" × 8" (US trade standard)',
  '5.5" × 8.5" (popular for longer books)',
  '6" × 9" (large trade / nonfiction common)',
  '4.25" × 6.87" (mass market paperback)',
  '8.5" × 11" (large format / workbooks)',
  'Custom',
];

const DISTRIBUTORS = [
  'Self-distributed',
  'Amazon KDP (ebook + print)',
  'IngramSpark (print + ebook)',
  'Draft2Digital (ebook)',
  'PublishDrive (ebook wide)',
  'StreetLib (ebook wide)',
  'Smashwords / Draft2Digital',
  'Barnes & Noble Press (ebook + print)',
  'Kobo Writing Life (ebook)',
  'Apple Books for Authors (ebook)',
  'Google Play Books (ebook)',
  'Overdrive / Libby (library)',
  'Baker & Taylor (library / trade)',
];

// Audiobook distribution is a separate ecosystem — when the format is an
// audiobook, the distributor list shows only audio platforms. "Self-distributed"
// leads every list.
const AUDIOBOOK_DISTRIBUTORS = [
  'Self-distributed',
  'ACX',
  'Findaway Voices',
  "Author's Republic",
  'Speechify',
  'Kobo Writing Life (audio)',
  'Google Play Books',
  'Libro.fm',
  'Scribd',
];

const distributorsForType = (type) => (type === 'audiobook' ? AUDIOBOOK_DISTRIBUTORS : DISTRIBUTORS);

const META_SECTIONS = [
  { id:'project',    icon:'📋', title:'Project Overview',          color:'amber'  },
  { id:'formats',    icon:'📚', title:'Format & ISBN Tracking',    color:'violet' },
  { id:'bisac',      icon:'🗂️', title:'BISAC Subject Classification', color:'sky' },
  { id:'thema',      icon:'🌐', title:'Thema Classification (UK/Global)', color:'teal' },
  { id:'consistency',icon:'🔗', title:'Cross-Platform Consistency', color:'rose'  },
  { id:'categories', icon:'🏷️', title:'Retailer Category Mapping',  color:'indigo'},
  { id:'keywords',   icon:'🔍', title:'Keyword Strategy',           color:'emerald'},
  { id:'description',icon:'✍️', title:'Description (Blurb) Audit', color:'amber'  },
  { id:'series',     icon:'📖', title:'Series Metadata',            color:'violet' },
  { id:'edition',    icon:'🔄', title:'Edition Control',            color:'sky'    },
  { id:'distribution',icon:'🚀',title:'Distribution Strategy',     color:'rose'   },
  { id:'pricing',     icon:'💷', title:'Pricing & Territory Planning',color:'teal'   },
  { id:'propagation', icon:'✅', title:'Platform Propagation Tracker',color:'emerald'},
  { id:'launch',     icon:'✅', title:'Final Check Before Ads',     color:'green'  },
  { id:'positioning',icon:'🎯', title:'Positioning Clarity',        color:'amber'  },
];

// Metadata section header bands — unified ink/gold brand (see PHASE_COLORS).
const SECTION_BRAND = { bg:'bg-parchment', border:'border-gold-soft', header:'bg-ink', badge:'bg-parchment-dark text-gold-deep', dot:'bg-gold' };
const SECTION_COLORS = {
  amber: SECTION_BRAND, violet: SECTION_BRAND, sky: SECTION_BRAND, teal: SECTION_BRAND,
  rose: SECTION_BRAND, indigo: SECTION_BRAND, emerald: SECTION_BRAND, green: SECTION_BRAND,
};

const BLANK_FORMAT = (type = 'paperback') => ({
  id:          `fmt-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
  type,                                          // ebook | paperback | hardback | largePrint | audiobook | boxset
  label:       '',                               // custom label e.g. "Collector's Edition"
  isbn:        type === 'ebook' ? 'N/A' : '',
  ownership:   type === 'ebook' ? 'Platform Assigned' : 'Owned (Nielsen/Bowker)',
  publisher:   '',
  trimSize:    type === 'paperback' ? '5" × 8" (most common trade paperback)'
             : type === 'hardback'  ? '6" × 9" (large trade / nonfiction common)'
             : type === 'largePrint'? '6" × 9" (large trade / nonfiction common)'
             : 'N/A',
  distributors: [],                              // array — multiple allowed
  notes:       '',
});

const DEFAULT_FORMATS = [
  { ...BLANK_FORMAT('ebook'),     id:'fmt-default-ebook',     isbn:'N/A', ownership:'Platform Assigned' },
  { ...BLANK_FORMAT('paperback'), id:'fmt-default-paperback' },
  { ...BLANK_FORMAT('hardback'),  id:'fmt-default-hardback'  },
];

const BLANK_METADATA = {
  project: { title:'', series:'', seriesNum:'', authorName:'', imprintName:'', primaryMarket:'US / UK / Global', strategy:'Wide Digital' },
  formats: DEFAULT_FORMATS,
  bisac: { primary:'', secondary:'', tertiary:'', reasoning:'', genreFirst:'Fantasy-first', ageGroup:'Adult' },
  thema: { primary:'', secondary:'', tertiary:'', aligned:'' },
  consistency: { bowker:'', nielsen:'', amazon:'', ingram:'', d2d:'' },
  categories: { amazon:['','',''], ingram:['','',''], d2d:['','',''] },
  keywords: { primary:'', secondary:'', tropes:'', kdp:['','','','','','',''] },
  description: { genre:false, tone:false, stakes:false, tropes:false, audience:false, heatLevel:false, compVibe:false, searchable:false },
  series: { consistent:'', amazon:'', ingram:'', d2d:'', nielsen:'', signals:'', linked:'' },
  edition: { cover:false, trim:false, layout:false, content:false, newISBN:'', labelType:'' },
  distribution: { strategy:'Wide Digital', isbnsMatch:'', leaveAmazon:'' },
  launch: { systemsAligned:'', categoriesAccurate:'', classificationIntentional:'', reviewMetaFirst:'' },
  positioning: { genreType:'Fantasy with romance', statement:'' },
  pricing: {
    ebookGBP:'', ebookUSD:'', ebookEUR:'', ebookCAD:'', ebookAUD:'',
    paperbackGBP:'', paperbackUSD:'', paperbackEUR:'', paperbackCAD:'', paperbackAUD:'',
    hardbackGBP:'', hardbackUSD:'', hardbackEUR:'', hardbackCAD:'', hardbackAUD:'',
    specialEditionGBP:'', specialEditionUSD:'', specialEditionEUR:'', specialEditionCAD:'', specialEditionAUD:'',
    audiobookGBP:'', audiobookUSD:'', audiobookEUR:'', audiobookCAD:'', audiobookAUD:'',
    largePrintGBP:'', largePrintUSD:'', largePrintEUR:'', largePrintCAD:'', largePrintAUD:'',
    ebookGBP_kdp:false,   ebookGBP_kobo:false,  ebookGBP_google:false, ebookGBP_d2d:false,  ebookGBP_bn:false,    ebookGBP_payhip:false,
    ebookUSD_kdp:false,   ebookUSD_kobo:false,  ebookUSD_google:false, ebookUSD_d2d:false,  ebookUSD_bn:false,
    ebookEUR_kdp:false,   ebookEUR_kobo:false,  ebookEUR_google:false, ebookEUR_d2d:false,
    paperbackGBP_kdp:false, paperbackGBP_ingram:false, paperbackGBP_bookvault:false, paperbackGBP_nielsen:false,
    paperbackUSD_kdp:false, paperbackUSD_ingram:false, paperbackUSD_bookvault:false, paperbackUSD_bn:false,
    hardbackGBP_ingram:false, hardbackGBP_bookvault:false, hardbackGBP_nielsen:false,
    hardbackUSD_ingram:false, hardbackUSD_bn:false,
  },
  propagation: {
    authorBio:'', shortDesc:'', longDesc:'', contentWarnings:'',
    bisacPrimary_nielsen:false,   bisacPrimary_kdp:false,   bisacPrimary_ingram:false,   bisacPrimary_bookvault:false, bisacPrimary_kobo:false, bisacPrimary_google:false, bisacPrimary_d2d:false, bisacPrimary_bn:false,
    bisacSecondary_nielsen:false, bisacSecondary_kdp:false, bisacSecondary_ingram:false, bisacSecondary_bookvault:false, bisacSecondary_kobo:false, bisacSecondary_google:false, bisacSecondary_d2d:false, bisacSecondary_bn:false,
    thema_nielsen:false,   thema_ingram:false,   thema_bookvault:false,
    seriesName_nielsen:false, seriesName_kdp:false, seriesName_ingram:false, seriesName_bookvault:false, seriesName_kobo:false, seriesName_google:false, seriesName_d2d:false, seriesName_bn:false,
    seriesNum_nielsen:false,  seriesNum_kdp:false,  seriesNum_ingram:false,  seriesNum_bookvault:false,  seriesNum_kobo:false,  seriesNum_google:false,  seriesNum_d2d:false,  seriesNum_bn:false,
    authorName_nielsen:false, authorName_kdp:false, authorName_ingram:false, authorName_bookvault:false, authorName_kobo:false, authorName_google:false, authorName_d2d:false, authorName_bn:false, authorName_payhip:false,
    authorBio_kdp:false,   authorBio_ingram:false,   authorBio_bookvault:false, authorBio_kobo:false, authorBio_google:false, authorBio_d2d:false, authorBio_bn:false, authorBio_payhip:false,
    shortDesc_nielsen:false, shortDesc_ingram:false, shortDesc_bookvault:false, shortDesc_payhip:false,
    longDesc_nielsen:false, longDesc_kdp:false, longDesc_ingram:false, longDesc_bookvault:false, longDesc_kobo:false, longDesc_google:false, longDesc_d2d:false, longDesc_bn:false, longDesc_payhip:false,
    cover_nielsen:false,  cover_kdp:false,  cover_ingram:false,  cover_bookvault:false, cover_kobo:false, cover_google:false, cover_d2d:false, cover_bn:false, cover_payhip:false,
    priceGBP_nielsen:false, priceGBP_kdp:false, priceGBP_ingram:false, priceGBP_bookvault:false, priceGBP_kobo:false, priceGBP_google:false, priceGBP_d2d:false, priceGBP_bn:false, priceGBP_payhip:false,
    pubDate_nielsen:false, pubDate_kdp:false, pubDate_ingram:false, pubDate_bookvault:false, pubDate_kobo:false, pubDate_google:false, pubDate_d2d:false, pubDate_bn:false, pubDate_payhip:false,
  },
};

function deepMerge(base, override) {
  const result = { ...base };
  for (const key of Object.keys(override || {})) {
    // Migrate old formats object → new array format
    if (key === 'formats') {
      if (Array.isArray(override[key])) {
        result[key] = override[key]; // already new format
      } else if (override[key] && typeof override[key] === 'object') {
        // Old format: { ebook: {...}, paperback: {...}, ... } → convert to array
        const typeMap = { ebook:'ebook', paperback:'paperback', hardback:'hardback', largePrint:'largePrint', specialEdition:'hardback' };
        const migrated = Object.entries(override[key]).map(([k, f]) => ({
          ...BLANK_FORMAT(typeMap[k] || 'paperback'),
          id: `fmt-migrated-${k}`,
          label: k === 'specialEdition' ? "Special / Collector's Edition" : '',
          isbn: f.isbn || '',
          ownership: f.ownership || '',
          publisher: f.publisher || '',
          trimSize: f.trimSize || '',
          distributors: f.distributor ? [f.distributor] : [],
        }));
        result[key] = migrated.length > 0 ? migrated : DEFAULT_FORMATS;
      } else {
        result[key] = base[key];
      }
    } else if (override[key] && typeof override[key] === 'object' && !Array.isArray(override[key])) {
      result[key] = deepMerge(base[key] || {}, override[key]);
    } else {
      result[key] = override[key];
    }
  }
  return result;
}

/* ═══════════════════════════════════════════════════════════
   SEARCHABLE CODE SELECT  (BISAC & Thema)
═══════════════════════════════════════════════════════════ */
function CodeSelect({ value, onChange, groups, flat, placeholder }) {
  const [query, setQuery]   = useState('');
  const [open, setOpen]     = useState(false);
  const wrapRef             = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.length >= 2
    ? flat.filter(c => c.code.toLowerCase().includes(query.toLowerCase()) || c.label.toLowerCase().includes(query.toLowerCase()))
    : [];

  const selected = flat.find(c => c.code === value);

  const groupsToShow = query.length >= 2
    ? [{ group:'Search results', codes: filtered.slice(0, 30) }]
    : groups;

  return (
    <div ref={wrapRef} className="relative">
      {/* Trigger */}
      <button type="button" onClick={() => { setOpen(o => !o); setQuery(''); }}
        className="flex w-full items-center justify-between rounded-xl border border-stone-200 bg-white px-3 py-2 text-left text-sm outline-none hover:border-gold/40 focus:border-gold focus:ring-2 focus:ring-gold/20">
        <span className={selected ? 'text-stone-900' : 'text-stone-400'}>
          {selected ? (
            <span className="flex items-center gap-2">
              <span className="rounded-md bg-stone-100 px-1.5 py-0.5 font-mono text-xs font-bold text-gold-deep">{selected.code}</span>
              <span className="truncate text-xs text-stone-600">{selected.label.split(' — ')[1]}</span>
            </span>
          ) : (placeholder || '— search or browse codes —')}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-stone-400 transition-transform ${open ? 'rotate-180' : ''}`}/>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-2xl" style={{ maxHeight:'380px', overflowY:'auto' }}>
          {/* Search input */}
          <div className="sticky top-0 border-b border-gold-soft bg-white p-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-stone-400"/>
              <input autoFocus type="text" value={query} onChange={e => setQuery(e.target.value)}
                placeholder="Type code or keyword (e.g. FIC009 or romantic…)"
                className="w-full rounded-xl border border-stone-200 bg-stone-50 py-2 pl-8 pr-3 text-sm outline-none focus:border-gold focus:ring-1 focus:ring-gold/25"
                style={{ userSelect:'text', WebkitUserSelect:'text' }} />
            </div>
            {value && (
              <button onClick={() => { onChange(''); setOpen(false); }}
                className="mt-1.5 w-full rounded-lg py-1 text-xs text-red-400 hover:bg-red-50 hover:text-red-600">
                ✕ Clear selection
              </button>
            )}
          </div>

          {/* Results */}
          {query.length >= 2 && filtered.length === 0 && (
            <p className="p-4 text-center text-xs text-stone-400">No codes match "{query}"</p>
          )}
          {query.length === 1 && (
            <p className="p-4 text-center text-xs text-stone-400">Type at least 2 characters to search</p>
          )}

          {groupsToShow.map(grp => (
            <div key={grp.group}>
              <div className="sticky top-[88px] bg-stone-50 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-stone-500 border-b border-gold-soft">
                {grp.group}
              </div>
              {grp.codes.map(c => (
                <button key={c.code} type="button"
                  onClick={() => { onChange(c.code); setOpen(false); setQuery(''); }}
                  className={`flex w-full flex-col gap-0.5 px-3 py-2.5 text-left transition-colors hover:bg-stone-50 ${value === c.code ? 'bg-stone-50' : ''}`}>
                  <div className="flex items-center gap-2">
                    {value === c.code && <Check className="h-3 w-3 shrink-0 text-gold-deep"/>}
                    <span className="rounded-md bg-stone-100 px-1.5 py-0.5 font-mono text-xs font-semibold text-stone-700">{c.code}</span>
                    {c.label.includes('★') && <span className="rounded-full bg-stone-100 px-1.5 py-0.5 text-xs font-bold uppercase tracking-wider text-gold-deep">Recommended</span>}
                  </div>
                  <p className="pl-0 text-xs text-stone-600">{c.label.split(' — ')[1]?.replace(' ★ Romantasy','').replace(' ★','')}</p>
                  {c.hint && <p className="text-xs leading-relaxed text-stone-400">{c.hint}</p>}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   METADATA SUMMARY CARD
═══════════════════════════════════════════════════════════ */
function MetadataSummaryCard({ data }) {
  const [open, setOpen] = useState(false);

  const bisacEntry  = (c) => BISAC_FLAT.find(b => b.code === c);
  const themaEntry  = (c) => THEMA_FLAT.find(t => t.code === c);

  const formats = (data.formats || [])
    .filter(f => f.isbn && f.isbn !== '' && f.isbn !== 'N/A');

  const fmtLabel = (f) => {
    const base = { ebook:'eBook', paperback:'Paperback', hardback:'Hardback', largePrint:'Large Print', audiobook:'Audiobook', boxset:'Box Set' }[f.type] || f.type;
    return f.label ? `${base} — ${f.label}` : base;
  };
  const fmtColor = { ebook:'bg-parchment-dark text-gold-deep', paperback:'bg-parchment-dark text-gold-deep', hardback:'bg-stone-100 text-gold-deep', largePrint:'bg-emerald-100 text-emerald-700', audiobook:'bg-rose-100 text-rose-700', boxset:'bg-stone-100 text-stone-700' };

  const descCount = Object.entries(data.description)
    .filter(([k, v]) => k !== 'blurbNotes' && v === true).length;

  const keywords = [data.keywords.primary, data.keywords.secondary]
    .filter(Boolean)
    .concat((data.keywords.tropes || '').split(',').map(k => k.trim()).filter(Boolean));

  const allCats = [
    ...(data.categories.amazon || []),
    ...(data.categories.ingram || []),
    ...(data.categories.d2d    || []),
  ].filter(Boolean);

  const empty = !data.project.title && !data.bisac.primary && !data.thema.primary;
  if (empty) return (
    <div className="flex items-center gap-3 rounded-2xl border border-dashed border-stone-200 bg-stone-50/40 p-4">
      <Database className="h-5 w-5 shrink-0 text-gold"/>
      <p className="text-sm text-stone-400">Fill in the sections below to see your metadata summary here.</p>
    </div>
  );

  return (
    <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
      <button onClick={() => setOpen(o => !o)}
        className="flex w-full items-center justify-between bg-ink px-5 py-3.5 text-left">
        <div className="flex items-center gap-2">
          <Database className="h-5 w-5 text-white"/>
          <div>
            <p className="font-cinzel text-sm font-bold tracking-wide text-white">Metadata Summary Card</p>
            <p className="text-xs text-parchment/60">Live preview of your entered metadata</p>
          </div>
        </div>
        {open ? <ChevronUp className="h-5 w-5 text-white/80"/> : <ChevronDown className="h-5 w-5 text-white/80"/>}
      </button>

      {open && (
        <div className="p-5 space-y-5">

          {/* Book identity */}
          {(data.project.title || data.project.authorName) && (
            <div className="flex items-start gap-4 rounded-2xl border border-gold-soft bg-stone-50 p-4">
              <div className="flex h-12 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gold text-white shadow-md">
                <BookOpen className="h-5 w-5"/>
              </div>
              <div className="min-w-0">
                {data.project.title && <p className="text-base font-bold leading-tight text-stone-900">{data.project.title}</p>}
                {(data.project.series || data.project.seriesNum) && (
                  <p className="text-xs text-stone-500">{data.project.series}{data.project.seriesNum ? ` · Book ${data.project.seriesNum}` : ''}</p>
                )}
                {data.project.authorName && <p className="mt-0.5 text-sm text-stone-600">by {data.project.authorName}</p>}
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {data.project.imprintName && <span className="rounded-full bg-stone-200 px-2 py-0.5 text-xs font-semibold text-stone-600">{data.project.imprintName}</span>}
                  {data.project.strategy && <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-semibold text-gold-deep">{data.project.strategy}</span>}
                  {data.project.primaryMarket && <span className="rounded-full bg-parchment-dark px-2 py-0.5 text-xs font-semibold text-gold-deep">{data.project.primaryMarket}</span>}
                </div>
              </div>
            </div>
          )}

          {/* Subject codes */}
          {(data.bisac.primary || data.thema.primary) && (
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-stone-400">Subject Classification</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {/* BISAC */}
                {(data.bisac.primary || data.bisac.secondary || data.bisac.tertiary) && (
                  <div className="rounded-xl border border-gold-soft bg-parchment p-3">
                    <p className="mb-2 text-xs font-bold uppercase tracking-widest text-gold-deep">BISAC (US)</p>
                    {[data.bisac.primary, data.bisac.secondary, data.bisac.tertiary].filter(Boolean).map((code, i) => {
                      const e = bisacEntry(code);
                      return (
                        <div key={code} className="mb-1 flex items-start gap-2">
                          <span className={`mt-0.5 shrink-0 rounded-full px-1.5 py-0.5 text-xs font-bold ${i===0?'bg-gold-deep text-white':'bg-gold/20 text-gold-deep'}`}>{i===0?'P':'S'}</span>
                          <div>
                            <span className="font-mono text-xs font-semibold text-gold-deep">{code}</span>
                            <p className="text-xs text-stone-600">{e ? e.label.split(' — ')[1]?.replace(' ★ Romantasy','').replace(' ★','') : code}</p>
                          </div>
                        </div>
                      );
                    })}
                    {data.bisac.genreFirst && <p className="mt-2 text-xs italic text-stone-500">{data.bisac.genreFirst} · {data.bisac.ageGroup}</p>}
                  </div>
                )}
                {/* Thema */}
                {(data.thema.primary || data.thema.secondary || data.thema.tertiary) && (
                  <div className="rounded-xl border border-gold-soft bg-parchment p-3">
                    <p className="mb-2 text-xs font-bold uppercase tracking-widest text-gold-deep">Thema (UK / Global)</p>
                    {[data.thema.primary, data.thema.secondary, data.thema.tertiary].filter(Boolean).map((code, i) => {
                      const e = themaEntry(code);
                      return (
                        <div key={code} className="mb-1 flex items-start gap-2">
                          <span className={`mt-0.5 shrink-0 rounded-full px-1.5 py-0.5 text-xs font-bold ${i===0?'bg-gold-deep text-white':'bg-gold/20 text-gold-deep'}`}>{i===0?'P':'S'}</span>
                          <div>
                            <span className="font-mono text-xs font-semibold text-gold-deep">{code}</span>
                            <p className="text-xs text-stone-600">{e ? e.label.split(' — ')[1]?.replace(' ★ Romantasy','').replace(' ★','') : code}</p>
                          </div>
                        </div>
                      );
                    })}
                    {data.thema.aligned && <p className="mt-2 text-xs italic text-stone-500">BISAC/Thema aligned: {data.thema.aligned}</p>}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Formats & ISBNs */}
          {formats.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-stone-400">Formats & ISBNs</p>
              <div className="flex flex-wrap gap-2">
                {formats.map(f => (
                  <div key={f.id} className="rounded-xl border border-stone-200 bg-white px-3 py-2 min-w-[160px]">
                    <span className={`rounded-full px-1.5 py-0.5 text-xs font-bold uppercase tracking-wider ${fmtColor[f.type] || 'bg-stone-100 text-stone-600'}`}>{fmtLabel(f)}</span>
                    <p className="mt-1 font-mono text-xs font-semibold text-stone-800">{f.isbn}</p>
                    <p className="text-xs text-stone-400">{f.ownership}</p>
                    {(f.distributors || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(f.distributors || []).map(d => (
                          <span key={d} className="rounded-full bg-stone-50 border border-stone-200 px-1.5 py-0.5 text-xs font-semibold text-gold-deep">{d.split(' ')[0]}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Categories */}
          {allCats.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-stone-400">Retailer Categories</p>
              <div className="space-y-1">
                {data.categories.amazon.filter(Boolean).map((cat, i) => (
                  <div key={`az-${i}`} className="flex items-start gap-2 rounded-lg bg-orange-50 px-2 py-1.5">
                    <span className="shrink-0 rounded-full bg-orange-500 px-1.5 py-0.5 text-xs font-bold text-white">AMZ</span>
                    <p className="text-xs text-stone-700">{cat}</p>
                  </div>
                ))}
                {data.categories.ingram.filter(Boolean).map((cat, i) => (
                  <div key={`ing-${i}`} className="flex items-start gap-2 rounded-lg bg-indigo-50 px-2 py-1.5">
                    <span className="shrink-0 rounded-full bg-indigo-500 px-1.5 py-0.5 text-xs font-bold text-white">ING</span>
                    <p className="text-xs text-stone-700">{cat}</p>
                  </div>
                ))}
                {data.categories.d2d.filter(Boolean).map((cat, i) => (
                  <div key={`d2d-${i}`} className="flex items-start gap-2 rounded-lg bg-emerald-50 px-2 py-1.5">
                    <span className="shrink-0 rounded-full bg-emerald-500 px-1.5 py-0.5 text-xs font-bold text-white">D2D</span>
                    <p className="text-xs text-stone-700">{cat}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Keywords */}
          {keywords.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-stone-400">Keywords</p>
              <div className="flex flex-wrap gap-1.5">
                {keywords.map((kw, i) => (
                  <span key={i} className={`rounded-full px-2.5 py-1 text-xs font-semibold ${i < 2 ? 'bg-stone-100 text-stone-800' : 'bg-stone-100 text-stone-600'}`}>
                    {kw}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Blurb audit */}
          {descCount > 0 && (
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-stone-400">Blurb Audit ({descCount}/8 elements)</p>
              <div className="h-2 overflow-hidden rounded-full bg-stone-100">
                <div className="h-full rounded-full bg-gold" style={{ width:`${descCount/8*100}%` }}/>
              </div>
            </div>
          )}

          {/* Positioning */}
          {data.positioning.statement && (
            <div className="rounded-xl border border-gold-soft bg-stone-50 p-3">
              <p className="mb-1 text-xs font-bold uppercase tracking-widest text-gold-deep">Positioning Statement</p>
              <p className="text-sm italic text-stone-700">"{data.positioning.statement}"</p>
              {data.positioning.compTitles && (
                <p className="mt-2 text-xs text-stone-500">Comps: {data.positioning.compTitles}</p>
              )}
            </div>
          )}

        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   METADATA CONTEXT  — shared by all sub-components so they
   can be defined at module level (prevents remount-on-render)
═══════════════════════════════════════════════════════════ */
const MetaCtx = createContext(null);

/* ── Pure layout helpers ── */
const MLabel = ({ children, hint }) => (
  <div className="mb-1">
    <label className="text-xs font-semibold uppercase tracking-widest text-stone-500">{children}</label>
    {hint && <p className="text-xs text-stone-400 mt-0.5">{hint}</p>}
  </div>
);
const MGrid2    = ({ children }) => <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>;
const MField    = ({ children }) => <div>{children}</div>;
const MTipBox   = ({ children }) => (
  <div className="flex items-start gap-2 rounded-xl border border-gold-soft bg-stone-50 p-3">
    <Info className="mt-0.5 h-4 w-4 shrink-0 text-stone-500"/>
    <p className="text-xs leading-relaxed text-stone-800">{children}</p>
  </div>
);
const MDivider  = ({ label }) => (
  <div className="flex items-center gap-2">
    <div className="h-px flex-1 bg-stone-200"/>
    {label && <span className="text-xs font-bold uppercase tracking-widest text-stone-400">{label}</span>}
    <div className="h-px flex-1 bg-stone-200"/>
  </div>
);

/* ── Context-driven form controls ── */
const MTextInput = ({ path, value, placeholder, wide }) => {
  const { update } = useContext(MetaCtx);
  return (
    <input type="text" value={value ?? ''} onChange={e => update(path, e.target.value)}
      placeholder={placeholder}
      className={`${wide ? 'w-full' : ''} rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-900 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20`}
      style={{ userSelect:'text', WebkitUserSelect:'text' }} />
  );
};
// Fixed, APPROXIMATE conversion rates from GBP. Deliberately hard-coded — this is
// a planning aid, not a live FX feed. Always shown as "estimated, verify before
// publishing". GBP is the source of truth; the rest auto-fill and stay editable.
const GBP_CONVERSION = { USD: 1.27, EUR: 1.18, CAD: 1.74, AUD: 1.95 };
const CURRENCY_FIELDS = [
  { code: 'GBP', label: 'GBP (£)', placeholder: 'e.g. £4.99' },
  { code: 'USD', label: 'USD ($)', placeholder: 'e.g. $5.99' },
  { code: 'EUR', label: 'EUR (€)', placeholder: 'e.g. €5.99' },
  { code: 'CAD', label: 'CAD (C$)', placeholder: 'e.g. C$8.99' },
  { code: 'AUD', label: 'AUD (A$)', placeholder: 'e.g. A$9.99' },
];

// A price row: GBP + USD/EUR/CAD/AUD. Typing a GBP price auto-fills the other
// four at the fixed estimated rates; every field is independently editable, so a
// manual override sticks until GBP is changed again. `data` is passed in (the
// pricing section has it in scope); `update` comes from context.
const MCurrencyRow = ({ fmtKey, data, hints = {} }) => {
  const { update } = useContext(MetaCtx);
  const fieldKey = (code) => `${fmtKey}${code}`;
  const val = (code) => data.pricing?.[fieldKey(code)] ?? '';

  const onGbpChange = (raw) => {
    update(`pricing.${fieldKey('GBP')}`, raw);
    const n = parseFloat(String(raw).replace(/[^0-9.]/g, ''));
    if (Number.isFinite(n)) {
      update(`pricing.${fieldKey('USD')}`, (n * GBP_CONVERSION.USD).toFixed(2));
      update(`pricing.${fieldKey('EUR')}`, (n * GBP_CONVERSION.EUR).toFixed(2));
      update(`pricing.${fieldKey('CAD')}`, (n * GBP_CONVERSION.CAD).toFixed(2));
      update(`pricing.${fieldKey('AUD')}`, (n * GBP_CONVERSION.AUD).toFixed(2));
    }
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {CURRENCY_FIELDS.map(({ code, label, placeholder }) => (
          <MField key={code}>
            <MLabel hint={hints[code]}>{label}</MLabel>
            <input type="text"
              value={val(code)}
              onChange={e => code === 'GBP' ? onGbpChange(e.target.value) : update(`pricing.${fieldKey(code)}`, e.target.value)}
              placeholder={placeholder}
              className="w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-900 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20"
              style={{ userSelect:'text', WebkitUserSelect:'text' }} />
          </MField>
        ))}
      </div>
      <p className="mt-1.5 text-xs text-stone-400">
        USD/EUR/CAD/AUD auto-fill from GBP at fixed rates (×1.27, ×1.18, ×1.74, ×1.95) —{' '}
        <span className="font-semibold text-stone-500">estimated, verify before publishing</span>. Edit any field to override.
      </p>
    </>
  );
};
const MSelect = ({ path, value, options }) => {
  const { update } = useContext(MetaCtx);
  return (
    <select value={value ?? ''} onChange={e => update(path, e.target.value)}
      className="w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-900 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20"
      style={{ userSelect:'text', WebkitUserSelect:'text' }}>
      <option value="">— select —</option>
      {options.map(o => typeof o === 'string'
        ? <option key={o} value={o}>{o}</option>
        : <option key={o.code} value={o.code}>{o.label}</option>
      )}
    </select>
  );
};
const MBisacPick = ({ path, value }) => {
  const { update } = useContext(MetaCtx);
  return <CodeSelect value={value ?? ''} onChange={v => update(path, v)} groups={BISAC_GROUPS} flat={BISAC_FLAT} placeholder="— search or browse BISAC codes —" />;
};
const MThemaPick = ({ path, value }) => {
  const { update } = useContext(MetaCtx);
  return <CodeSelect value={value ?? ''} onChange={v => update(path, v)} groups={THEMA_GROUPS} flat={THEMA_FLAT} placeholder="— search or browse Thema codes —" />;
};
const MYesNo = ({ path, value }) => {
  const { update } = useContext(MetaCtx);
  return (
    <div className="flex gap-2">
      {['Yes','No','N/A'].map(v => (
        <button key={v} onClick={() => update(path, v)}
          className={`rounded-lg border px-3 py-1 text-xs font-semibold transition-all ${value === v
            ? v==='Yes' ? 'border-green-500 bg-green-100 text-green-700' : v==='No' ? 'border-red-400 bg-red-50 text-red-600' : 'border-stone-400 bg-stone-100 text-stone-600'
            : 'border-stone-200 bg-white text-stone-400 hover:border-stone-300'}`}>
          {v}
        </button>
      ))}
    </div>
  );
};
const MCheckItem = ({ path, value, label, hint }) => {
  const { update } = useContext(MetaCtx);
  return (
    <button onClick={() => update(path, !value)}
      className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left transition-all ${value ? 'border-green-200 bg-green-50/50' : 'border-stone-200 bg-white hover:border-stone-200 hover:bg-stone-50/20'}`}>
      {value ? <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-600"/> : <Circle className="mt-0.5 h-4 w-4 shrink-0 text-stone-300"/>}
      <div>
        <p className={`text-sm font-semibold leading-snug ${value ? 'text-stone-500 line-through' : 'text-stone-800'}`}>{label}</p>
        {hint && <p className="mt-0.5 text-xs leading-relaxed text-stone-400">{hint}</p>}
      </div>
    </button>
  );
};

/* ── Save bar (shown at bottom of each section) ── */
const MSaveBar = ({ secId }) => {
  const { isDirty, savedSection, saveSection } = useContext(MetaCtx);
  const justSaved = savedSection === secId;
  return (
    <div className="flex items-center justify-between border-t border-stone-200/60 pt-3 mt-1">
      <p className="text-xs text-stone-400">
        {isDirty && !justSaved ? '● Unsaved changes' : ''}
      </p>
      <Button onClick={() => saveSection(secId)}
        className={`flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-bold transition-all shadow-sm ${
          justSaved
            ? 'bg-green-100 text-green-700 border border-green-200'
            : isDirty
            ? 'bg-gold-deep text-white hover:bg-gold border border-gold'
            : 'bg-stone-100 text-stone-400 border border-stone-200 cursor-default'
        }`}>
        {justSaved
          ? <><Check className="h-3.5 w-3.5"/>Saved</>
          : <><Save className="h-3.5 w-3.5"/>{isDirty ? 'Save Changes' : 'Up to date'}</>
        }
      </Button>
    </div>
  );
};

/* ── Section wrapper ── */
const MSection = ({ sec, children }) => {
  const { collapsed, toggleSection, isDirty } = useContext(MetaCtx);
  const col    = SECTION_COLORS[sec.color] || SECTION_COLORS.amber;
  const isOpen = !collapsed[sec.id];
  return (
    <div className={`overflow-hidden rounded-2xl border ${col.border} shadow-sm`}>
      <Button variant="ghost" onClick={() => toggleSection(sec.id)}
        className={`h-auto w-full rounded-none rounded-t-2xl ${col.header} p-4 text-left transition-all hover:opacity-90`}>
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-gold"/>
            <p className="font-cinzel text-sm font-bold tracking-wide text-white">{sec.title}</p>
          </div>
          <div className="flex items-center gap-2">
            {isDirty && isOpen && (
              <Badge className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold text-white border-0">unsaved</Badge>
            )}
            {isOpen ? <ChevronUp className="h-4 w-4 text-white/80"/> : <ChevronDown className="h-4 w-4 text-white/80"/>}
          </div>
        </div>
      </Button>
      {isOpen && (
        <div className={`${col.bg} px-5 pt-5 pb-4 space-y-4`}>
          {children}
          <MSaveBar secId={sec.id} />
        </div>
      )}
    </div>
  );
};

/* ── Format entry card ── */
const FORMAT_TYPE_LABELS = {
  ebook:'eBook', paperback:'Paperback', hardback:'Hardback',
  largePrint:'Large Print', audiobook:'Audiobook', boxset:'Box Set',
};
const FORMAT_TYPE_COLORS = {
  ebook:'bg-parchment-dark text-gold-deep', paperback:'bg-parchment-dark text-gold-deep',
  hardback:'bg-stone-100 text-gold-deep', largePrint:'bg-emerald-100 text-emerald-700',
  audiobook:'bg-rose-100 text-rose-700', boxset:'bg-stone-100 text-stone-700',
};
const isPrint = (type) => ['paperback','hardback','largePrint','boxset'].includes(type);

const MFormatEntryCard = ({ fmt }) => {
  const { updateFormat, removeFormat } = useContext(MetaCtx);
  const [expanded, setExpanded] = useState(true);
  const col       = FORMAT_TYPE_COLORS[fmt.type] || FORMAT_TYPE_COLORS.paperback;
  const isDefault = fmt.id.startsWith('fmt-default');

  return (
    <div className="rounded-xl border border-stone-200 bg-white shadow-sm">
      <div className="flex items-center gap-2 rounded-t-xl border-b border-gold-soft bg-stone-50 px-4 py-2.5">
        <span className={`rounded-full px-2 py-0.5 text-xs font-bold uppercase tracking-wider ${col}`}>
          {FORMAT_TYPE_LABELS[fmt.type] || fmt.type}
        </span>
        {fmt.label && <span className="text-xs font-semibold text-stone-600">{fmt.label}</span>}
        {fmt.isbn && fmt.isbn !== 'N/A' && (
          <span className="font-mono text-xs text-stone-400">{fmt.isbn}</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setExpanded(e => !e)}
            className="rounded-lg border border-stone-200 px-2 py-1 text-xs font-semibold text-stone-500 hover:bg-stone-100">
            {expanded ? 'Collapse' : 'Expand'}
          </button>
          {!isDefault && (
            <button onClick={() => removeFormat(fmt.id)}
              className="rounded-lg border border-red-100 px-2 py-1 text-xs font-semibold text-red-400 hover:bg-red-50 hover:text-red-600">
              Remove
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="space-y-3 p-4">
          <div className="grid grid-cols-2 gap-3">
            <MField>
              <MLabel hint="What kind of format is this?">Format Type</MLabel>
              <select value={fmt.type} onChange={e => updateFormat(fmt.id, 'type', e.target.value)}
                className="w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-900 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20"
                style={{ userSelect:'text', WebkitUserSelect:'text' }}>
                {Object.entries(FORMAT_TYPE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </MField>
            <MField>
              <MLabel hint={"Optional — e.g. \"Standard Edition\", \"Collector's Edition\", \"Sprayed Edges\""}>Custom Label</MLabel>
              <input type="text" value={fmt.label} onChange={e => updateFormat(fmt.id, 'label', e.target.value)}
                placeholder="e.g. Collector's Edition"
                className="w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-900 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20"
                style={{ userSelect:'text', WebkitUserSelect:'text' }} />
            </MField>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <MField>
              <MLabel hint="Each format and each edition needs its own unique ISBN">ISBN</MLabel>
              <input type="text" value={fmt.isbn} onChange={e => updateFormat(fmt.id, 'isbn', e.target.value)}
                placeholder={fmt.type === 'ebook' ? 'N/A or 978-…' : '978-…'}
                className="w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-900 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20"
                style={{ userSelect:'text', WebkitUserSelect:'text' }} />
            </MField>
            <MField>
              <MLabel hint="Owned = registered via Nielsen/Bowker. Platform Assigned = the platform owns it.">Ownership</MLabel>
              <select value={fmt.ownership} onChange={e => updateFormat(fmt.id, 'ownership', e.target.value)}
                className="w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-900 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20"
                style={{ userSelect:'text', WebkitUserSelect:'text' }}>
                <option value="">— select —</option>
                {['Owned (Nielsen/Bowker)','Owned (Bowker/US)','Owned (Nielsen/UK)','Platform Assigned','KDP Assigned','IngramSpark Assigned'].map(o => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </MField>
            <MField>
              <MLabel hint="Who appears as publisher on all retail pages for this format">Publisher of Record</MLabel>
              <input type="text" value={fmt.publisher} onChange={e => updateFormat(fmt.id, 'publisher', e.target.value)}
                placeholder="Your Imprint Name"
                className="w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-900 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20"
                style={{ userSelect:'text', WebkitUserSelect:'text' }} />
            </MField>
          </div>

          <div className={`grid gap-3 ${isPrint(fmt.type) ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
            {isPrint(fmt.type) && (
              <MField>
                <MLabel hint="Must match your formatted interior file and cover template exactly">Trim Size</MLabel>
                <select value={fmt.trimSize} onChange={e => updateFormat(fmt.id, 'trimSize', e.target.value)}
                  className="w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-900 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20"
                  style={{ userSelect:'text', WebkitUserSelect:'text' }}>
                  <option value="">— select —</option>
                  {TRIM_SIZES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </MField>
            )}
            <MField>
              <MLabel hint="Select every platform this format is distributed through — tick all that apply">
                Distributors <span className="normal-case font-normal text-stone-400">(select all that apply)</span>
              </MLabel>
              <DistributorMultiSelect
                type={fmt.type}
                values={fmt.distributors || []}
                onChange={vals => updateFormat(fmt.id, 'distributors', vals)}
              />
            </MField>
          </div>

          {(fmt.distributors || []).length > 0 && (
            <div className="rounded-xl border border-gold-soft bg-stone-50/60 px-3 py-2">
              <p className="mb-1.5 text-xs font-bold uppercase tracking-widest text-gold-deep">Distribution Coverage</p>
              <div className="flex flex-wrap gap-1.5">
                {(fmt.distributors || []).map(d => (
                  <span key={d} className="flex items-center gap-1 rounded-full bg-white border border-stone-200 px-2 py-0.5 text-xs font-semibold text-stone-800">
                    <span className="h-1.5 w-1.5 rounded-full bg-stone-500 shrink-0"/>
                    {d}
                  </span>
                ))}
              </div>
            </div>
          )}

          <MField>
            <MLabel>Notes for this format</MLabel>
            <textarea value={fmt.notes || ''} onChange={e => updateFormat(fmt.id, 'notes', e.target.value)}
              placeholder="e.g. Sprayed edges, foil cover, exclusive to Bookshop.org, special binding…"
              rows={2} className="w-full resize-none rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-800 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20"
              style={{ userSelect:'text', WebkitUserSelect:'text' }} />
          </MField>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   DISTRIBUTOR MULTI-SELECT
═══════════════════════════════════════════════════════════ */
function DistributorMultiSelect({ values = [], onChange, type }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const options = distributorsForType(type);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (d, e) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(values.includes(d) ? values.filter(x => x !== d) : [...values, d]);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="flex min-h-[38px] w-full flex-wrap items-center gap-1.5 rounded-xl border border-stone-200 bg-white px-3 py-1.5 text-left outline-none hover:border-gold/40 focus:border-gold focus:ring-2 focus:ring-gold/20">
        {values.length === 0
          ? <span className="text-sm text-stone-400">— select all that apply —</span>
          : values.map(v => (
              <span key={v} className="flex items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-xs font-semibold text-stone-800">
                {v}
                <button type="button" onClick={e => { e.stopPropagation(); onChange(values.filter(x => x !== v)); }}
                  className="ml-0.5 rounded-full text-stone-500 hover:text-stone-800">×</button>
              </span>
            ))
        }
        <ChevronDown className={`ml-auto h-4 w-4 shrink-0 text-stone-400 transition-transform ${open ? 'rotate-180' : ''}`}/>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-2xl" style={{ maxHeight:'440px', overflowY:'auto' }}>
          {options.map(d => {
            const checked = values.includes(d);
            return (
              <div key={d}
                onMouseDown={e => toggle(d, e)}
                className={`flex cursor-pointer items-center gap-3 px-3 py-2.5 transition-colors hover:bg-stone-50 select-none ${checked ? 'bg-stone-50/60' : ''}`}>
                <div className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 transition-all ${checked ? 'border-gold-deep bg-gold-deep' : 'border-stone-300'}`}>
                  {checked && <Check className="h-2.5 w-2.5 text-white"/>}
                </div>
                <span className={`text-sm ${checked ? 'font-semibold text-stone-900' : 'text-stone-600'}`}>{d}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PROPAGATION TRACKER CONSTANTS
═══════════════════════════════════════════════════════════ */
const PROP_PLATFORMS = [
  { id:'nielsen',   label:'Nielsen',     color:'bg-stone-700   text-white' },
  { id:'kdp',       label:'KDP',         color:'bg-orange-500  text-white' },
  { id:'ingram',    label:'Ingram',      color:'bg-indigo-600  text-white' },
  { id:'bookvault', label:'BookVault',   color:'bg-gold-deep    text-white' },
  { id:'kobo',      label:'Kobo',        color:'bg-blue-500    text-white' },
  { id:'google',    label:'Google Play', color:'bg-green-600   text-white' },
  { id:'d2d',       label:'D2D',         color:'bg-ink  text-white' },
  { id:'bn',        label:'B&N Press',   color:'bg-stone-500   text-white' },
  { id:'payhip',    label:'Payhip',      color:'bg-purple-500  text-white' },
];
const PROP_FIELDS = [
  { id:'bisacPrimary',   label:'Primary BISAC',    hint:'FIC027250 etc.',  getVal:(d)=>d.bisac?.primary,            pids:['nielsen','kdp','ingram','bookvault','kobo','google','d2d','bn'] },
  { id:'bisacSecondary', label:'Secondary BISAC',  hint:'FIC009100 etc.',  getVal:(d)=>d.bisac?.secondary,          pids:['nielsen','kdp','ingram','bookvault','kobo','google','d2d','bn'] },
  { id:'thema',          label:'Thema Code',       hint:'FRF, FRD etc.',   getVal:(d)=>d.thema?.primary,            pids:['nielsen','ingram','bookvault'] },
  { id:'seriesName',     label:'Series Name',      hint:'exact match',     getVal:(d)=>d.project?.series,           pids:['nielsen','kdp','ingram','bookvault','kobo','google','d2d','bn'] },
  { id:'seriesNum',      label:'Series No.',       hint:'1, 2, 3…',        getVal:(d)=>d.project?.seriesNum,        pids:['nielsen','kdp','ingram','bookvault','kobo','google','d2d','bn'] },
  { id:'authorName',     label:'Author Name',      hint:'pen name exact',  getVal:(d)=>d.project?.authorName,       pids:['nielsen','kdp','ingram','bookvault','kobo','google','d2d','bn','payhip'] },
  { id:'authorBio',      label:'Author Bio',       hint:'150–200 words',   getVal:(d)=>d.propagation?.authorBio,    pids:['kdp','ingram','bookvault','kobo','google','d2d','bn','payhip'] },
  { id:'shortDesc',      label:'Short Desc',       hint:'250 chars',       getVal:(d)=>d.propagation?.shortDesc,    pids:['nielsen','ingram','bookvault','payhip'] },
  { id:'longDesc',       label:'Long Desc',        hint:'full blurb',      getVal:(d)=>d.propagation?.longDesc,     pids:['nielsen','kdp','ingram','bookvault','kobo','google','d2d','bn','payhip'] },
  { id:'cover',          label:'Cover Image',      hint:'uploaded',        getVal:()=>null,                         pids:['nielsen','kdp','ingram','bookvault','kobo','google','d2d','bn','payhip'] },
  { id:'priceGBP',       label:'Price GBP',        hint:'ebook RRP',       getVal:(d)=>d.pricing?.ebookGBP,         pids:['nielsen','kdp','kobo','google','d2d','bn','payhip'] },
  { id:'pubDate',        label:'Pub Date',         hint:'your pub date',   getVal:(d)=>d.project?.pubDate || '',    pids:['nielsen','kdp','ingram','bookvault','kobo','google','d2d','bn','payhip'] },
  { id:'language',       label:'Language',         hint:'en (English)',    getVal:()=>'English (en)',               pids:['nielsen','kdp','ingram','bookvault','kobo','google','d2d','bn','payhip'] },
];

/* ═══════════════════════════════════════════════════════════
   PLATFORM REQUIREMENTS REFERENCE DATA
═══════════════════════════════════════════════════════════ */
const PLATFORM_REF_DATA = [
  { id:'nielsen',   name:'Nielsen Title Editor', codes:'BISAC + Thema + BIC', cats:'Up to 10 Thema', keywords:'—', shortDesc:'250 chars', longDesc:'4,000 chars', html:'No',  cover:'JPG/PNG 300dpi 1:1.5', ebook:'N/A', print:'N/A',            url:'https://www.nielsentitleeditor.com/titleeditor/' },
  { id:'kdp',       name:'Amazon KDP',           codes:'BISAC only',          cats:'2 categories',   keywords:'7 × 50 chars', shortDesc:'—',     longDesc:'4,000 chars (HTML)', html:'Yes', cover:'JPG min 1000px',   ebook:'EPUB/MOBI',   print:'PDF press-ready', url:'https://kdp.amazon.com' },
  { id:'ingram',    name:'IngramSpark',           codes:'BISAC + Thema',       cats:'3 BISAC + 3 Thema', keywords:'—', shortDesc:'250 chars', longDesc:'4,000 chars', html:'No',  cover:'PDF/JPG 300dpi',    ebook:'EPUB',        print:'PDF press-ready', url:'https://www.ingramspark.com' },
  { id:'bookvault', name:'BookVault',             codes:'BISAC + Thema',       cats:'3 BISAC + Thema', keywords:'—',  shortDesc:'250 chars', longDesc:'4,000 chars', html:'No',  cover:'PDF/JPG 300dpi',    ebook:'N/A',         print:'PDF press-ready', url:'https://www.bookvault.app' },
  { id:'apple',     name:'Apple Books (via D2D)', codes:'BISAC',               cats:'2 categories',   keywords:'—', shortDesc:'—',          longDesc:'4,000 chars', html:'Limited', cover:'JPG min 1400px', ebook:'EPUB',        print:'N/A',            url:'https://www.draft2digital.com' },
  { id:'kobo',      name:'Kobo Writing Life',     codes:'BISAC',               cats:'2 categories',   keywords:'—', shortDesc:'—',          longDesc:'4,000 chars', html:'No',  cover:'JPG min 800×600',  ebook:'EPUB',        print:'N/A',            url:'https://writinglife.kobo.com' },
  { id:'google',    name:'Google Play Books',     codes:'BISAC',               cats:'2 categories',   keywords:'—', shortDesc:'—',          longDesc:'No hard limit', html:'No', cover:'JPG 5MB+ recommended', ebook:'EPUB', print:'N/A',            url:'https://play.google.com/books/publish' },
  { id:'d2d',       name:'Draft2Digital',         codes:'BISAC',               cats:'2 BISAC',        keywords:'—', shortDesc:'—',          longDesc:'No hard limit', html:'No', cover:'Inherits',         ebook:'EPUB',        print:'N/A',            url:'https://www.draft2digital.com' },
  { id:'bn',        name:'B&N Press',             codes:'BISAC',               cats:'2 categories',   keywords:'—', shortDesc:'—',          longDesc:'4,000 chars', html:'No',  cover:'JPG/PNG',          ebook:'EPUB',        print:'PDF press-ready', url:'https://press.barnesandnoble.com' },
  { id:'payhip',    name:'Payhip',                codes:'None (tags)',          cats:'Unlimited tags', keywords:'—', shortDesc:'Yes',        longDesc:'No limit',   html:'Yes', cover:'JPG/PNG any size', ebook:'EPUB or PDF', print:'N/A',            url:'https://payhip.com' },
];

/* ═══════════════════════════════════════════════════════════
   PLATFORM REQUIREMENTS REFERENCE CARD
   (replaces Excel "Metadata Reference" sheet)
═══════════════════════════════════════════════════════════ */
function PlatformRefCard() {
  const [open, setOpen] = useState(false);
  const cols = [
    { key:'codes',     label:'Codes' },
    { key:'cats',      label:'Categories' },
    { key:'keywords',  label:'Keywords' },
    { key:'shortDesc', label:'Short Desc' },
    { key:'longDesc',  label:'Long Desc' },
    { key:'html',      label:'HTML?' },
    { key:'cover',     label:'Cover Spec' },
    { key:'ebook',     label:'eBook File' },
    { key:'print',     label:'Print File' },
  ];
  return (
    <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
      <button onClick={() => setOpen(o => !o)}
        className="flex w-full items-center justify-between bg-gradient-to-r from-stone-700 to-stone-800 px-5 py-3.5 text-left">
        <div className="flex items-center gap-2">
          <Info className="h-5 w-5 text-stone-300"/>
          <div>
            <p className="text-sm font-bold text-white">Platform Requirements Reference</p>
            <p className="text-xs text-stone-400">Char limits, code systems, file specs per platform — collapsed by default</p>
          </div>
        </div>
        {open ? <ChevronUp className="h-5 w-5 text-white/60"/> : <ChevronDown className="h-5 w-5 text-white/60"/>}
      </button>
      {open && (
        <div className="w-full overflow-x-auto p-1">
          <table className="w-full min-w-[860px] text-xs">
            <thead>
              <tr className="border-b border-gold-soft bg-stone-50">
                <th className="px-3 py-2.5 text-left text-xs font-bold uppercase tracking-widest text-stone-500 w-36">Platform</th>
                {cols.map(c => (
                  <th key={c.key} className="px-2 py-2.5 text-left text-xs font-bold uppercase tracking-widest text-stone-400">{c.label}</th>
                ))}
                <th className="px-2 py-2.5 text-left text-xs font-bold uppercase tracking-widest text-stone-400">Link</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {PLATFORM_REF_DATA.map(p => (
                <tr key={p.id} className="hover:bg-stone-50/60">
                  <td className="px-3 py-2 font-semibold text-stone-800">{p.name}</td>
                  {cols.map(c => (
                    <td key={c.key} className={`px-2 py-2 text-stone-600 leading-snug ${
                      c.key === 'html' && p[c.key] === 'Yes' ? 'text-green-600 font-semibold' :
                      c.key === 'html' && p[c.key] === 'No'  ? 'text-stone-400' : ''
                    }`}>{p[c.key] || '—'}</td>
                  ))}
                  <td className="px-2 py-2">
                    {p.url && <a href={p.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-gold-deep hover:text-[#7c5f13] text-xs font-semibold">Open <ExternalLink className="h-2.5 w-2.5"/></a>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="p-3 text-xs text-stone-400 text-right">Based on platform specs as of March 2026. Verify current limits before final upload.</p>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   TAB: METADATA AUDIT
═══════════════════════════════════════════════════════════ */
function MetadataTab({ metadata, onSave }) {
  const [data, setData]         = useState(() => deepMerge(BLANK_METADATA, metadata || {}));
  const [collapsed, setCollapsed] = useState({});
  const [isDirty, setIsDirty]   = useState(false);
  const saveTimerRef = useRef(null);

  const update = (path, value) => {
    setData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let node = next;
      for (let i = 0; i < parts.length - 1; i++) {
        const arrMatch = parts[i].match(/^(.+)\[(\d+)\]$/);
        if (arrMatch) {
          node = node[arrMatch[1]][parseInt(arrMatch[2])];
        } else {
          node = node[parts[i]];
        }
      }
      const lastKey = parts[parts.length - 1];
      const arrMatch = lastKey.match(/^(.+)\[(\d+)\]$/);
      if (arrMatch) { node[arrMatch[1]][parseInt(arrMatch[2])] = value; }
      else { node[lastKey] = value; }
      return next;
    });
    setIsDirty(true);
  };

  useEffect(() => () => clearTimeout(saveTimerRef.current), []);

  const saveNow = () => { onSave(data); setIsDirty(false); };

  /* ── Format array helpers ── */
  const addFormat = (type) => {
    const newFmt = BLANK_FORMAT(type);
    setData(prev => {
      const next = { ...prev, formats: [...(prev.formats || []), newFmt] };
      onSave(next); return next;
    });
    setIsDirty(false);
  };

  const removeFormat = (id) => {
    if (!window.confirm('Remove this format entry?')) return;
    setData(prev => {
      const next = { ...prev, formats: prev.formats.filter(f => f.id !== id) };
      onSave(next); return next;
    });
    setIsDirty(false);
  };

  const updateFormat = (id, field, value) => {
    setData(prev => ({
      ...prev,
      formats: prev.formats.map(f => f.id === id ? { ...f, [field]: value } : f),
    }));
    setIsDirty(true);
  };

  // Count total completable fields vs filled
  const completenessScore = (() => {
    const fmts = data.formats || [];
    const hasPaperback = fmts.some(f => f.type === 'paperback' && f.isbn);
    const hasDistributor = fmts.some(f => (f.distributors || []).length > 0);
    const checks = [
      data.project.title, data.project.authorName, data.project.imprintName,
      hasPaperback ? 'ok' : '', hasDistributor ? 'ok' : '',
      data.bisac.primary, data.bisac.secondary, data.bisac.reasoning,
      data.thema.primary,
      data.consistency.bowker, data.consistency.nielsen, data.consistency.amazon, data.consistency.ingram,
      data.categories.amazon[0], data.categories.ingram[0], data.categories.d2d[0],
      data.keywords.primary, data.keywords.secondary, data.keywords.tropes,
      Object.values(data.description).filter(Boolean).length >= 5 ? 'ok' : '',
      data.series.consistent,
      data.positioning.statement,
    ];
    const filled = checks.filter(v => v && v !== '').length;
    return Math.round(filled / checks.length * 100);
  })();

  const toggleSection = (id) => setCollapsed(p => ({ ...p, [id]: !p[id] }));

  const [savedSection, setSavedSection] = useState(null);
  const saveSection = (secId) => {
    onSave(data);
    setIsDirty(false);
    setSavedSection(secId);
    setTimeout(() => setSavedSection(s => s === secId ? null : s), 2000);
  };

  const descChecks = [
    { key:'genre',    label:'Genre is clearly stated', hint:'Reader knows immediately if this is fantasy, romance, or romantasy.' },
    { key:'tone',     label:'Tone is conveyed', hint:'Dark, lyrical, fast-paced, witty — make it clear.' },
    { key:'stakes',   label:'Stakes are present', hint:'What does the protagonist risk? World-ending, personal, or both?' },
    { key:'tropes',   label:'Key tropes are signalled', hint:'Enemies-to-lovers, chosen one, forbidden romance — tropes drive discovery.' },
    { key:'audience', label:'Target audience is implied', hint:'Adult, NA, or YA? Signals word choice and heat.' },
    { key:'heatLevel',label:'Heat level is communicated', hint:'Especially important for romantasy. "Steamy" or "closed door" in the blurb or categories.' },
    { key:'compVibe', label:'Comparative vibe or comp title present', hint:'"Fans of Fourth Wing will love…" gives readers instant context.' },
    { key:'searchable',label:'Language is search-friendly, not only poetic', hint:'Beautiful prose blurbs can tank discoverability. Balance artistry with keywords.' },
  ];

  const seriesChecks = [
    { key:'consistent', label:'Series name is consistent across all platforms', hint:'Inconsistent series names break reader-to-series linking on Amazon, Goodreads, and retailers.' },
    { key:'amazon',     label:'Series number correctly entered on Amazon KDP', hint:'Use the Series field in KDP, not just the subtitle.' },
    { key:'ingram',     label:'Series number correctly entered on IngramSpark', hint:'IngramSpark uses the Series field on the title detail page.' },
    { key:'d2d',        label:'Series number correctly entered on Draft2Digital', hint:'D2D propagates series data to Apple, Kobo, B&N.' },
    { key:'nielsen',    label:'Series number correctly entered on Nielsen/Bowker', hint:'This feeds into the official book trade catalogue. Librarians use it.' },
    { key:'signals',    label:'Book 1 clearly signals it is Book 1', hint:'Both title/subtitle and categories/series field should indicate first in series.' },
    { key:'linked',     label:'Retail pages correctly link to series page', hint:'Test by searching the series name on Amazon. All books should appear together.' },
  ];

  return (
    <MetaCtx.Provider value={{ update, updateFormat, removeFormat, isDirty, savedSection, saveSection, collapsed, toggleSection }}>
    <div className="space-y-5">

      {/* ── Summary Card ── */}
      <MetadataSummaryCard data={data} />

      {/* ── Platform Requirements Reference (replaces Excel Metadata Reference sheet) ── */}
      <PlatformRefCard />

      {/* ── Score banner ── */}
      <div className="overflow-hidden rounded-2xl border border-gold-soft bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-gold-deep">Metadata Completeness</p>
            <p className="mt-0.5 text-xs text-stone-400">Based on key fields across all sections</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-gold-deep">{completenessScore}%</p>
            <button onClick={saveNow}
              className={`mt-1 flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold transition-all ${isDirty ? 'bg-stone-100 text-gold-deep hover:bg-stone-200' : 'bg-green-100 text-green-700'}`}>
              {isDirty ? <><Save className="h-2.5 w-2.5"/>Save All</> : <><Check className="h-2.5 w-2.5"/>All Saved</>}
            </button>
          </div>
        </div>
        <Progress value={completenessScore} className="h-3 bg-stone-100" indicatorClassName="bg-gold" />
        {completenessScore === 100 && (
          <div className="mt-3 flex items-center gap-2 rounded-xl bg-stone-50 p-3 text-sm font-semibold text-gold-deep">
            <Sparkles className="h-4 w-4"/>Metadata audit complete — you're ready to run ads with confidence!
          </div>
        )}
        <MTipBox>
          Complete this worksheet <strong>before</strong> running paid advertising. Misaligned metadata is one of the most common reasons ads underperform — the retailer algorithm and the ad algorithm need to agree on what your book is.
        </MTipBox>
      </div>

      {/* ── S1: Project Overview ── */}
      <MSection sec={META_SECTIONS[0]}>
        <MGrid2>
          <MField><MLabel>Book Title</MLabel><MTextInput path="project.title" value={data.project.title} placeholder="e.g. The Ember Gate" wide /></MField>
          <MField><MLabel>Series Name (if applicable)</MLabel><MTextInput path="project.series" value={data.project.series} placeholder="e.g. The Ember Gate Saga" wide /></MField>
          <MField><MLabel hint="Use ordinals: 1, 2, 3 — not 'One'">Series Number</MLabel><MTextInput path="project.seriesNum" value={data.project.seriesNum} placeholder="1" wide /></MField>
          <MField><MLabel hint="Exact name as it appears everywhere — consistency matters">Author Name</MLabel><MTextInput path="project.authorName" value={data.project.authorName} placeholder="e.g. L. R. Hart" wide /></MField>
          <MField><MLabel hint="The name registered on ISBNs and appearing as Publisher of Record">Imprint / Publisher Name</MLabel><MTextInput path="project.imprintName" value={data.project.imprintName} placeholder="e.g. Emberlight Press" wide /></MField>
          <MField>
            <MLabel>Primary Market</MLabel>
            <MSelect path="project.primaryMarket" value={data.project.primaryMarket} options={['US / UK / Global','US Primary','UK Primary','ANZ Primary','Global Wide']} />
          </MField>
          <MField>
            <MLabel hint="This affects ISBN strategy and distributor choices">Publishing Strategy</MLabel>
            <MSelect path="project.strategy" value={data.project.strategy}
              options={['Amazon Only (KDP Select)','Wide Digital (KDP + D2D)','Wide Digital + Print (KDP + Ingram)','Full Trade (Wide + Library + Wholesale)','Hybrid (Amazon KU + Wide Print)']} />
          </MField>
        </MGrid2>
        <MTipBox>
          Your imprint name is your publishing brand. It should appear identically on your copyright page, ISBN registration, and all retailer publisher fields. Inconsistency here causes cataloguing errors in the book trade.
        </MTipBox>
      </MSection>

      {/* ── S2: Format & ISBN Tracking ── */}
      <MSection sec={META_SECTIONS[1]}>
        <MTipBox>
          You need <strong>one unique ISBN per format and per edition</strong>. A standard paperback and a Collector's Edition paperback require separate ISBNs. Multiple distributors per format are normal — tick every platform this specific format reaches. Platform-assigned ISBNs (KDP, IngramSpark) make the platform the Publisher of Record, not you.
        </MTipBox>

        {/* Format cards */}
        <div className="space-y-3">
          {(data.formats || DEFAULT_FORMATS).map((fmt, idx) => (
            <MFormatEntryCard key={fmt.id} fmt={fmt} idx={idx} />
          ))}
        </div>

        {/* Add Format button row */}
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-widest text-stone-400">Add another format</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries({ paperback:'+ Paperback', hardback:'+ Hardback', ebook:'+ eBook', largePrint:'+ Large Print', audiobook:'+ Audiobook', boxset:'+ Box Set' }).map(([type, label]) => (
              <button key={type} onClick={() => addFormat(type)}
                className="rounded-xl border border-dashed border-gold/40 bg-stone-50 px-3 py-1.5 text-xs font-semibold text-gold-deep hover:border-gold hover:bg-stone-100 transition-all">
                {label}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-stone-400">Adding a Special Edition or Collector's Edition? Add a Paperback or Hardback and give it a custom label.</p>
        </div>

        <div className="space-y-2">
          <MDivider label="ISBN Audit Questions" />
          <MField>
            <MLabel hint="Important for wide publishing — the same ISBN should appear on all retailers for a given format">Are you using the same ISBN for the same format across all platforms?</MLabel>
            <MYesNo path="consistency.sameISBN" value={data.consistency?.sameISBN} />
          </MField>
          <MField>
            <MLabel hint="If not, note your reasoning — sometimes platform-specific ISBNs are intentional">If not, why?</MLabel>
            <MTextInput path="consistency.sameISBNNotes" value={data.consistency?.sameISBNNotes} placeholder="e.g. Using KDP-assigned for Kindle, owned ISBN for print wide…" wide />
          </MField>
          <MField>
            <MLabel>Are you comfortable with who is listed as Publisher of Record on each format?</MLabel>
            <MYesNo path="consistency.publisherComfort" value={data.consistency?.publisherComfort} />
          </MField>
        </div>
      </MSection>

      {/* ── S3: BISAC ── */}
      <MSection sec={META_SECTIONS[2]}>
        <MTipBox>
          BISAC codes are the US industry standard for subject classification. They're used by Bowker, Baker & Taylor, libraries, and wholesalers. For romantasy, you have a genuine choice: <strong>FIC009100 (Fantasy / Romantic)</strong> prioritises the fantasy readership; <strong>FIC027050 (Romance / Fantasy)</strong> prioritises the romance readership. Most indie romantasy authors go fantasy-first.
        </MTipBox>
        <MGrid2>
          <MField>
            <MLabel hint="Most important — this drives discoverability in library catalogues and wholesale">Primary BISAC Code</MLabel>
            <MBisacPick path="bisac.primary" value={data.bisac.primary} />
          </MField>
          <MField>
            <MLabel>Secondary BISAC Code</MLabel>
            <MBisacPick path="bisac.secondary" value={data.bisac.secondary} />
          </MField>
          <MField>
            <MLabel>Optional Third BISAC Code</MLabel>
            <MBisacPick path="bisac.tertiary" value={data.bisac.tertiary} />
          </MField>
        </MGrid2>
        <MField>
          <MLabel hint="Why did you choose this primary? What signal does it send?">Reasoning for Primary Choice</MLabel>
          <textarea value={data.bisac.reasoning || ''} onChange={e => update('bisac.reasoning', e.target.value)}
            placeholder="e.g. My book leads with a political fantasy plot and romantic subplot — readers will find it through fantasy first…"
            rows={3} className="w-full resize-none rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-800 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20"
            style={{ userSelect:'text', WebkitUserSelect:'text' }} />
        </MField>
        <MDivider label="Classification Decisions" />
        <MGrid2>
          <MField>
            <MLabel hint="This affects which shelf a book lands on in retail and library systems">This book is primarily…</MLabel>
            <MSelect path="bisac.genreFirst" value={data.bisac.genreFirst}
              options={['Fantasy-first (with romance)','Romance-first (in a fantasy world)','Equal dual genre','Dark fantasy with romantic tension','Political fantasy with romantic subplot']} />
          </MField>
          <MField>
            <MLabel>Target Age Group</MLabel>
            <MSelect path="bisac.ageGroup" value={data.bisac.ageGroup}
              options={['Adult (18+)','New Adult (NA, 18–25)','Young Adult (YA, 14–18)','Upper Middle Grade (12–14)']} />
          </MField>
        </MGrid2>
      </MSection>

      {/* ── S4: Thema ── */}
      <MSection sec={META_SECTIONS[3]}>
        <MTipBox>
          Thema codes are the international standard used by Nielsen (UK), Ingram globally, and most non-US retailers. If you're publishing wide, you need both BISAC <em>and</em> Thema. Your primary Thema code should match the intent of your primary BISAC code. Verify codes at <strong>ns.editeur.org/thema</strong> or your Nielsen account.
        </MTipBox>
        <MGrid2>
          <MField>
            <MLabel hint="Used by Nielsen UK, Ingram globally, and most European retailers">Primary Thema Code</MLabel>
            <MThemaPick path="thema.primary" value={data.thema.primary} />
          </MField>
          <MField>
            <MLabel>Secondary Thema Code</MLabel>
            <MThemaPick path="thema.secondary" value={data.thema.secondary} />
          </MField>
          <MField>
            <MLabel>Optional Third Thema Code</MLabel>
            <MThemaPick path="thema.tertiary" value={data.thema.tertiary} />
          </MField>
        </MGrid2>
        <MField>
          <MLabel>Does your primary Thema match your primary BISAC in intent?</MLabel>
          <MYesNo path="thema.aligned" value={data.thema.aligned} />
        </MField>
        <MField>
          <MLabel>If not, explain why:</MLabel>
          <MTextInput path="thema.alignedNotes" value={data.thema?.alignedNotes} placeholder="e.g. US market leans fantasy-romance, UK market leans fantasy — adjusted accordingly…" wide />
        </MField>
      </MSection>

      {/* ── S5: Cross-Platform Consistency ── */}
      <MSection sec={META_SECTIONS[4]}>
        <MTipBox>
          Metadata inconsistency across platforms is invisible to readers but catastrophic for discoverability. Your subject codes, series name, author name, and publisher of record should be identical everywhere you can control them.
        </MTipBox>
        <div className="space-y-3">
          {[
            { key:'bowker',  label:'Bowker (US ISBN record — myidentifiers.com)',     hint:'US Publisher of Record and BISAC codes registered here' },
            { key:'nielsen', label:'Nielsen (UK ISBN record — nielsenisbnstore.com)', hint:'UK Publisher of Record and Thema codes registered here' },
            { key:'amazon',  label:'Amazon / KDP (categories and series)',            hint:'Up to 3 browse node categories, series field, author page' },
            { key:'ingram',  label:'IngramSpark (subject codes, series, trim)',       hint:'BISAC codes, series field, and edition type' },
            { key:'d2d',     label:'Draft2Digital (subjects, categories)',            hint:'Propagates to Apple Books, Kobo, B&N, OverDrive, and others' },
          ].map(({ key, label, hint }) => (
            <div key={key} className="overflow-hidden rounded-xl border border-stone-200 bg-white">
              <div className="border-b border-gold-soft bg-stone-50 px-4 py-2">
                <p className="text-xs font-bold text-stone-700">{label}</p>
                <p className="text-xs text-stone-400">{hint}</p>
              </div>
              <div className="flex items-center gap-4 px-4 py-3">
                <p className="text-xs font-semibold text-stone-600">Data aligned?</p>
                <MYesNo path={`consistency.${key}`} value={data.consistency[key]} />
              </div>
            </div>
          ))}
        </div>
        <MField>
          <MLabel hint="Note any platform where your metadata differs and why">List any known mismatches:</MLabel>
          <textarea value={data.consistency?.mismatches || ''} onChange={e => update('consistency.mismatches', e.target.value)}
            placeholder="e.g. IngramSpark still showing old series name from first upload — need to submit correction…"
            rows={3} className="w-full resize-none rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-800 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20"
            style={{ userSelect:'text', WebkitUserSelect:'text' }} />
        </MField>
      </MSection>

      {/* ── S6: Retailer Category Mapping ── */}
      <MSection sec={META_SECTIONS[5]}>
        <MTipBox>
          Amazon allows up to <strong>3 browse node categories</strong> (2 during upload, a 3rd via KDP support request or the Categories tool). IngramSpark and D2D use BISAC codes which map to their internal categories. Aim for category selections that accurately represent the book AND have achievable bestseller list competition.
        </MTipBox>
        {[
          { platform:'amazon',  label:'Amazon Browse Categories', hint:'Full paths e.g. "Kindle Store > Kindle eBooks > Romance > Fantasy"', type:'amazon' },
          { platform:'ingram',  label:'IngramSpark Subjects',     hint:'IngramSpark uses BISAC codes — search below', type:'bisac' },
          { platform:'d2d',     label:'Draft2Digital Subjects',   hint:'D2D uses genre tags that map to Apple, Kobo, B&N categories', type:'d2d' },
        ].map(({ platform, label, hint, type }) => (
          <div key={platform}>
            <MDivider label={label} />
            <p className="mb-2 text-xs text-stone-400">{hint}</p>
            <div className="space-y-2">
              {[0,1,2].map(i => (
                type === 'bisac'
                  ? <CodeSelect key={i} value={data.categories[platform][i] ?? ''}
                      onChange={v => update(`categories.${platform}[${i}]`, v)}
                      groups={BISAC_GROUPS} flat={BISAC_FLAT} placeholder="— search BISAC code —" />
                  : <MSelect key={i} path={`categories.${platform}[${i}]`}
                      value={data.categories[platform][i]}
                      options={type === 'amazon' ? AMAZON_CATEGORIES : ['Fantasy / Fantasy Romance','Fantasy / Epic Fantasy','Fantasy / Dark Fantasy','Fantasy / Historical','Fantasy / Mythology','Fantasy / Urban & Paranormal','Romance / Paranormal Romance','Romance / Fantasy Romance','Romance / Historical','Romance / Romantic Suspense','Romance / New Adult','Romance / LGBTQ+','Science Fiction & Fantasy / Fantasy','Science Fiction & Fantasy / Science Fiction','Horror / Dark Fantasy','Horror / Supernatural','Historical Fiction','Literary Fiction','Teens & YA / Fantasy','Teens & YA / Romance']} />
              ))}
            </div>
          </div>
        ))}
        <MField>
          <MLabel>Are your categories aligned in genre intent across platforms?</MLabel>
          <MYesNo path="categories.aligned" value={data.categories?.aligned} />
        </MField>
      </MSection>

      {/* ── S7: Keywords ── */}
      <MSection sec={META_SECTIONS[6]}>
        <MTipBox>
          Amazon allows 7 keyword fields (up to 50 characters each). Prioritise <strong>long-tail phrases</strong> — specific 3–5 word combinations that describe exactly what a reader is searching for. Trope-based keywords are particularly powerful in romantasy right now. Avoid putting your genre or author name in keywords — these are already indexed.
        </MTipBox>

        {/* KDP 7 keyword fields */}
        <MDivider label="KDP Keyword Fields (copy directly into Amazon)" />
        <MTipBox>
          Each field is a separate Amazon keyword slot — up to 50 characters each. Use the full 50 where possible. Don't repeat words across fields; Amazon reads them together. Counter turns red if you exceed the limit.
        </MTipBox>
        <MTipBox>
          Each format of your book (ebook, paperback, hardback) is a separate KDP entry with its own seven keyword fields — they don't have to match. Since Amazon usually merges formats onto one product page, using different keywords per format (for example, descriptive phrases on the ebook and tropes on the paperback) widens your search coverage for the same book.
        </MTipBox>
        <div className="space-y-2">
          {[0,1,2,3,4,5,6].map(i => {
            const val = (data.keywords.kdp || [])[i] || '';
            const len = val.length;
            const over = len > 50;
            const kdpPlaceholders = [
              'e.g. enemies to lovers epic fantasy romance',
              'e.g. dark romantasy morally grey king',
              'e.g. fae courts political intrigue romance',
              'e.g. fantasy romance series complete',
              'e.g. BookTok romantasy slow burn',
              'e.g. indie romantasy 2026 debut',
              'e.g. fourth wing vibes fantasy romance',
            ];
            return (
              <div key={i} className="flex items-center gap-2">
                <span className="w-5 shrink-0 text-center text-xs font-bold text-stone-400">{i+1}</span>
                <div className="relative flex-1">
                  <input type="text" value={val}
                    onChange={e => {
                      const arr = [...((data.keywords.kdp) || ['','','','','','',''])];
                      arr[i] = e.target.value.slice(0, 60); // allow slightly over so counter is visible
                      update('keywords.kdp', arr);
                    }}
                    placeholder={kdpPlaceholders[i]}
                    maxLength={60}
                    className={`w-full rounded-xl border px-3 py-2 pr-12 text-sm text-stone-900 outline-none focus:ring-2 ${over ? 'border-red-400 focus:border-red-500 focus:ring-red-100' : 'border-stone-200 focus:border-gold focus:ring-gold/20'}`}
                    style={{ userSelect:'text', WebkitUserSelect:'text' }} />
                  <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold tabular-nums ${over ? 'text-red-500' : len > 40 ? 'text-stone-500' : 'text-stone-300'}`}>
                    {len}/50
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <MDivider label="Keyword Strategy Notes" />
        <MField>
          <MLabel hint="Your single most important search phrase — what does your ideal reader type into Amazon?">Primary Search Phrase (long-tail)</MLabel>
          <MTextInput path="keywords.primary" value={data.keywords.primary} placeholder="e.g. enemies to lovers epic fantasy romance" wide />
        </MField>
        <MField>
          <MLabel>Secondary Search Phrase</MLabel>
          <MTextInput path="keywords.secondary" value={data.keywords.secondary} placeholder="e.g. dark romantasy morally grey king" wide />
        </MField>
        <MField>
          <MLabel hint="Additional keyword concepts and tropes for reference">Trope-Based & Additional Phrases (reference notes)</MLabel>
          <textarea value={data.keywords.tropes || ''} onChange={e => update('keywords.tropes', e.target.value)}
            placeholder={`e.g. dragon rider fantasy romance, political fantasy slow burn, fae courts enemies to lovers, dark fantasy morally grey heroine…`}
            rows={3} className="w-full resize-none rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-800 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20"
            style={{ userSelect:'text', WebkitUserSelect:'text' }} />
        </MField>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {[
            { label:'Reflect tone?',              path:'keywords.checkTone',     value: data.keywords?.checkTone },
            { label:'Reflect reader expectation?', path:'keywords.checkReader',  value: data.keywords?.checkReader },
            { label:'Match your categories?',      path:'keywords.checkCats',    value: data.keywords?.checkCats },
          ].map(({ label, path, value }) => (
            <div key={path} className="rounded-xl border border-stone-200 bg-white p-3">
              <p className="mb-2 text-xs font-semibold text-stone-600">Do your keywords…</p>
              <p className="mb-2 text-xs text-stone-500">{label}</p>
              <MYesNo path={path} value={value} />
            </div>
          ))}
        </div>
      </MSection>

      {/* ── S8: Description Audit ── */}
      <MSection sec={META_SECTIONS[7]}>
        <MTipBox>
          Your blurb is working metadata, not just a marketing asset. Retailers use it for semantic search indexing. A beautiful but vague blurb can rank for nothing. Every element below directly affects discoverability — not just click-through.
        </MTipBox>
        <div className="space-y-2">
          {descChecks.map(({ key, label, hint }) => (
            <MCheckItem key={key} path={`description.${key}`} value={data.description[key]} label={label} hint={hint} />
          ))}
        </div>
        <MField>
          <MLabel hint="Rewrite notes, or paste current blurb here for reference">Blurb Draft / Notes</MLabel>
          <textarea value={data.description?.blurbNotes || ''} onChange={e => update('description.blurbNotes', e.target.value)}
            placeholder="Paste your current blurb here, or leave notes for what needs to change…"
            rows={5} className="w-full resize-none rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-800 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20"
            style={{ userSelect:'text', WebkitUserSelect:'text' }} />
        </MField>
      </MSection>

      {/* ── S9: Series Metadata ── */}
      <MSection sec={META_SECTIONS[8]}>
        <MTipBox>
          Series metadata errors are silent killers of sell-through. If Book 1 doesn't clearly signal it's Book 1, or if series names don't match across platforms, Amazon's also-bought and recommendation engine can't link your books together — and neither can readers.
        </MTipBox>
        <div className="space-y-2">
          {seriesChecks.map(({ key, label, hint }) => (
            <MCheckItem key={key} path={`series.${key}`} value={data.series[key]} label={label} hint={hint} />
          ))}
        </div>
      </MSection>

      {/* ── S10: Edition Control ── */}
      <MSection sec={META_SECTIONS[9]}>
        <MTipBox>
          A new ISBN is required when: the title changes, the format changes, the content changes substantially (more than cosmetic corrections), or a "Special Edition" label is added. A new cover alone does <em>not</em> require a new ISBN — but it does require updated cover files on all platforms.
        </MTipBox>
        <div className="mb-3">
          <MLabel>Which of the following has changed in this edition?</MLabel>
          <div className="mt-2 space-y-2">
            {[
              { key:'cover',   label:'New cover design' },
              { key:'trim',    label:'Different trim size' },
              { key:'layout',  label:'New interior layout or formatting' },
              { key:'content', label:'Substantial content changes (rewrites, additions, removals)' },
            ].map(({ key, label }) => (
              <button key={key} onClick={() => update(`edition.${key}`, !data.edition[key])}
                className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left ${data.edition[key] ? 'border-gold/40 bg-parchment' : 'border-stone-200 bg-white hover:border-gold-soft'}`}>
                {data.edition[key] ? <CheckCircle className="h-4 w-4 shrink-0 text-gold-deep"/> : <Circle className="h-4 w-4 shrink-0 text-stone-300"/>}
                <span className="text-sm text-stone-700">{label}</span>
              </button>
            ))}
          </div>
        </div>
        <MGrid2>
          <MField>
            <MLabel hint="Does this edition require a new ISBN based on what changed?">Does this require a new ISBN?</MLabel>
            <MYesNo path="edition.newISBN" value={data.edition.newISBN} />
          </MField>
          <MField>
            <MLabel hint="Clearly label editions to avoid reader confusion and returns">Edition Label</MLabel>
            <MSelect path="edition.labelType" value={data.edition.labelType}
              options={['First Edition','Second Edition','Revised Edition','Special Edition','Collector\'s Edition','Anniversary Edition','Author\'s Preferred Edition','No edition label']} />
          </MField>
        </MGrid2>
      </MSection>

      {/* ── S11: Distribution Strategy ── */}
      <MSection sec={META_SECTIONS[10]}>
        <MTipBox>
          Your ISBN ownership structure should reflect your long-term publishing strategy. If you're in KDP Select, you're exclusive to Amazon and cannot distribute wide. If you own your ISBNs and publish wide, you can leave any platform without losing your publishing identity. This is a business decision — not just a technical one.
        </MTipBox>
        <MField>
          <MLabel>Current Distribution Strategy</MLabel>
          <MSelect path="distribution.strategy" value={data.distribution.strategy}
            options={['Amazon Only (KDP Select / KU)','Wide Digital (KDP non-exclusive + D2D)','Wide Digital + Print (KDP + IngramSpark wide)','Full Trade (Wide + Library + Wholesale + Bookshops)','Hybrid (Amazon KU for ebook, Wide for print)','Audiobook Wide (ACX non-exclusive + Findaway)','Print only — no ebook']} />
        </MField>
        <div className="space-y-3">
          <MField>
            <MLabel>Does your ISBN ownership match your long-term distribution plan?</MLabel>
            <MYesNo path="distribution.isbnsMatch" value={data.distribution.isbnsMatch} />
          </MField>
          <MField>
            <MLabel hint="If you left Amazon tomorrow, would your ISBN structure and publishing identity survive?">If you left Amazon tomorrow, would your ISBN structure still make sense?</MLabel>
            <MYesNo path="distribution.leaveAmazon" value={data.distribution.leaveAmazon} />
          </MField>
          <MField>
            <MLabel>Notes on distribution decisions:</MLabel>
            <MTextInput path="distribution.notes" value={data.distribution?.notes} placeholder="e.g. Staying in KU for 90 days post-launch, then going wide…" wide />
          </MField>
        </div>
      </MSection>

      {/* ── S12 (new): Pricing & Territory Planning ── */}
      <MSection sec={META_SECTIONS[11]}>
        <MTipBox>
          Your ebook price must sit within the 70% royalty bracket on most platforms: <strong>£1.99–£9.99 / $2.99–$9.99</strong>. Set GBP as your primary, then convert. Keep prices consistent across platforms to avoid price-matching complications. Track which platforms you've entered each price into below.
        </MTipBox>

        {/* Ebook pricing */}
        <MDivider label="Ebook Prices" />
        <MCurrencyRow fmtKey="ebook" data={data} hints={{ GBP:'70% bracket: £1.99–£9.99', USD:'70% bracket: $2.99–$9.99' }} />
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[380px] text-xs">
            <thead><tr className="border-b border-gold-soft">
              <th className="py-1.5 text-left font-bold text-stone-400 uppercase tracking-widest text-xs">Platform</th>
              <th className="py-1.5 text-center font-bold text-stone-400 uppercase tracking-widest text-xs">GBP</th>
              <th className="py-1.5 text-center font-bold text-stone-400 uppercase tracking-widest text-xs">USD</th>
              <th className="py-1.5 text-center font-bold text-stone-400 uppercase tracking-widest text-xs">EUR</th>
            </tr></thead>
            <tbody className="divide-y divide-stone-50">
              {[
                { label:'Amazon KDP',   gbp:'ebookGBP_kdp',  usd:'ebookUSD_kdp',  eur:'ebookEUR_kdp'  },
                { label:'Kobo',         gbp:'ebookGBP_kobo', usd:'ebookUSD_kobo', eur:'ebookEUR_kobo' },
                { label:'Google Play',  gbp:'ebookGBP_google',usd:'ebookUSD_google',eur:'ebookEUR_google'},
                { label:'D2D',          gbp:'ebookGBP_d2d',  usd:'ebookUSD_d2d',  eur:'ebookEUR_d2d'  },
                { label:'B&N Press',    gbp:'ebookGBP_bn',   usd:'ebookUSD_bn',   eur:null            },
                { label:'Payhip',       gbp:'ebookGBP_payhip',usd:null,           eur:null            },
              ].map(({ label, gbp, usd, eur }) => (
                <tr key={label} className="hover:bg-stone-50/60">
                  <td className="py-2 text-xs font-semibold text-stone-700">{label}</td>
                  {[gbp, usd, eur].map((key, ci) => (
                    <td key={ci} className="py-2 text-center">
                      {key ? (
                        <button onClick={() => update(`pricing.${key}`, !data.pricing?.[key])}
                          className={`mx-auto flex h-5 w-5 items-center justify-center rounded border-2 transition-all ${data.pricing?.[key] ? 'border-emerald-500 bg-emerald-500' : 'border-stone-300 hover:border-emerald-400'}`}>
                          {data.pricing?.[key] && <Check className="h-3 w-3 text-white"/>}
                        </button>
                      ) : <span className="text-stone-200">—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Print pricing */}
        <MDivider label="Print Prices (Paperback)" />
        <MCurrencyRow fmtKey="paperback" data={data} />
        <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
          {[
            { label:'Nielsen RRP', key:'paperbackGBP_nielsen' },
            { label:'KDP',         key:'paperbackGBP_kdp'     },
            { label:'IngramSpark', key:'paperbackGBP_ingram'  },
            { label:'BookVault',   key:'paperbackGBP_bookvault'},
          ].map(({ label, key }) => (
            <button key={key} onClick={() => update(`pricing.${key}`, !data.pricing?.[key])}
              className={`flex items-center gap-1.5 rounded-lg border p-2 text-left transition-all ${data.pricing?.[key] ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-stone-200 bg-white text-stone-500 hover:border-emerald-200'}`}>
              <div className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 ${data.pricing?.[key] ? 'border-emerald-500 bg-emerald-500' : 'border-stone-300'}`}>
                {data.pricing?.[key] && <Check className="h-2.5 w-2.5 text-white"/>}
              </div>
              {label}
            </button>
          ))}
        </div>

        <MDivider label="Print Prices (Hardback)" />
        <MCurrencyRow fmtKey="hardback" data={data} />
        <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
          {[
            { label:'Nielsen RRP', key:'hardbackGBP_nielsen' },
            { label:'IngramSpark', key:'hardbackGBP_ingram'  },
            { label:'BookVault',   key:'hardbackGBP_bookvault'},
            { label:'B&N Press',   key:'hardbackUSD_bn'      },
          ].map(({ label, key }) => (
            <button key={key} onClick={() => update(`pricing.${key}`, !data.pricing?.[key])}
              className={`flex items-center gap-1.5 rounded-lg border p-2 text-left transition-all ${data.pricing?.[key] ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-stone-200 bg-white text-stone-500 hover:border-emerald-200'}`}>
              <div className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 ${data.pricing?.[key] ? 'border-emerald-500 bg-emerald-500' : 'border-stone-300'}`}>
                {data.pricing?.[key] && <Check className="h-2.5 w-2.5 text-white"/>}
              </div>
              {label}
            </button>
          ))}
        </div>

        <MDivider label="Special Edition Prices" />
        <MCurrencyRow fmtKey="specialEdition" data={data} />

        <MDivider label="Audiobook Prices" />
        <MCurrencyRow fmtKey="audiobook" data={data} />

        <MDivider label="Large Print Prices" />
        <MCurrencyRow fmtKey="largePrint" data={data} />
      </MSection>

      {/* ── S13 (new): Platform Propagation Tracker ── */}
      <MSection sec={META_SECTIONS[12]}>
        <MTipBox>
          This is your single source of truth. For each metadata field, enter your value once (or it pulls automatically from earlier sections), then tick off each platform as you enter it there. When every checkbox in a row is ticked, that field is fully propagated. This replaces the Excel Metadata Entry sheet.
        </MTipBox>

        {/* Text value fields (author bio, descriptions, content warnings) */}
        <MDivider label="Content Fields — Enter Once, Propagate Everywhere" />
        <MField>
          <MLabel hint="150–200 words. Used by KDP Author Central, IngramSpark, Kobo, Google Play, B&N, and Payhip.">Author Bio</MLabel>
          <textarea value={data.propagation?.authorBio || ''} onChange={e => update('propagation.authorBio', e.target.value)}
            placeholder="Write your author bio here — 150–200 words. Same text goes to all platforms…"
            rows={4} className="w-full resize-none rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-800 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20"
            style={{ userSelect:'text', WebkitUserSelect:'text' }} />
        </MField>
        <MField>
          <MLabel hint="Max 250 characters. Used by Nielsen, IngramSpark, BookVault, and Payhip.">Short Description (250 chars)</MLabel>
          <div className="relative">
            <textarea value={data.propagation?.shortDesc || ''} onChange={e => update('propagation.shortDesc', e.target.value)}
              placeholder="Your punchy 250-character blurb — think back-cover sell copy, not full synopsis…"
              rows={3} maxLength={260}
              className={`w-full resize-none rounded-xl border px-3 py-2 pr-16 text-sm text-stone-800 outline-none focus:ring-2 ${(data.propagation?.shortDesc||'').length > 250 ? 'border-red-400 focus:border-red-500 focus:ring-red-100' : 'border-stone-200 focus:border-gold focus:ring-gold/20'}`}
              style={{ userSelect:'text', WebkitUserSelect:'text' }} />
            <span className={`absolute bottom-2 right-3 text-xs font-bold tabular-nums ${(data.propagation?.shortDesc||'').length > 250 ? 'text-red-500' : (data.propagation?.shortDesc||'').length > 220 ? 'text-stone-500' : 'text-stone-300'}`}>
              {(data.propagation?.shortDesc||'').length}/250
            </span>
          </div>
        </MField>
        <MField>
          <MLabel hint="Full blurb / back cover copy. Goes to all platforms. KDP accepts HTML — use <b>, <i>, <br>.">Long Description (full blurb)</MLabel>
          <textarea value={data.propagation?.longDesc || ''} onChange={e => update('propagation.longDesc', e.target.value)}
            placeholder="Paste your full back-cover copy here. KDP accepts basic HTML — <b>bold</b>, <i>italic</i>, <br> for line breaks…"
            rows={6} className="w-full resize-none rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-800 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20"
            style={{ userSelect:'text', WebkitUserSelect:'text' }} />
          <p className="mt-1 text-xs text-stone-400">{(data.propagation?.longDesc||'').length} chars — most platforms allow 4,000</p>
        </MField>
        <MField>
          <MLabel hint="Payhip has a dedicated content warnings field. For other platforms, include in description.">Content Warnings (Payhip + description reference)</MLabel>
          <MTextInput path="propagation.contentWarnings" value={data.propagation?.contentWarnings} placeholder="e.g. adult themes, violence, sexual content, morally grey characters, grief…" wide />
        </MField>

        {/* Propagation matrix */}
        <MDivider label="Propagation Checklist — Tick When Entered on Each Platform" />
        <div className="rounded-xl border border-gold-soft bg-white shadow-sm w-full overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gold-soft bg-stone-50">
                <th className="px-3 py-2.5 text-left text-xs font-bold uppercase tracking-widest text-stone-400 w-28">Field</th>
                <th className="px-2 py-2.5 text-left text-xs font-bold uppercase tracking-widest text-stone-400 w-24">Value</th>
                {PROP_PLATFORMS.map(p => (
                  <th key={p.id} className="px-1 py-2.5 text-center w-8">
                    <span className={`inline-flex h-6 w-6 items-center justify-center rounded text-xs font-bold ${p.color}`} title={p.label}>
                      {p.label.charAt(0)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {PROP_FIELDS.map(field => {
                const val = field.getVal(data);
                const totalCells = field.pids.length;
                const checkedCells = field.pids.filter(pid => data.propagation?.[`${field.id}_${pid}`]).length;
                const allDone = totalCells > 0 && checkedCells === totalCells;
                return (
                  <tr key={field.id} className={allDone ? 'bg-emerald-50/40' : 'hover:bg-stone-50/60'}>
                    <td className="px-3 py-2">
                      <p className={`text-xs font-semibold leading-snug ${allDone ? 'text-emerald-700' : 'text-stone-700'}`}>{field.label}</p>
                      <p className="text-xs text-stone-400">{field.hint}</p>
                    </td>
                    <td className="px-2 py-2">
                      {val ? (
                        <span className="block truncate max-w-[90px] rounded bg-stone-50 px-1.5 py-0.5 font-mono text-xs font-semibold text-stone-800" title={val}>{val}</span>
                      ) : (
                        <span className="text-xs italic text-stone-300">—</span>
                      )}
                    </td>
                    {PROP_PLATFORMS.map(plat => {
                      const key = `${field.id}_${plat.id}`;
                      const applies = field.pids.includes(plat.id);
                      const checked = !!data.propagation?.[key];
                      return (
                        <td key={plat.id} className="px-1 py-2 text-center">
                          {applies ? (
                            <button onClick={() => update(`propagation.${key}`, !checked)}
                              className={`mx-auto flex h-5 w-5 items-center justify-center rounded border-2 transition-all ${checked ? 'border-emerald-500 bg-emerald-500' : 'border-stone-300 hover:border-emerald-400'}`}>
                              {checked && <Check className="h-3 w-3 text-white"/>}
                            </button>
                          ) : (
                            <span className="text-stone-200 text-xs">·</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 pt-1">
          {PROP_PLATFORMS.map(p => (
            <span key={p.id} className="flex items-center gap-1 text-xs text-stone-500">
              <span className={`inline-flex h-4 w-4 items-center justify-center rounded text-xs font-bold ${p.color}`}>{p.label.charAt(0)}</span>
              {p.label}
            </span>
          ))}
        </div>
      </MSection>

      {/* ── S14: Final Check Before Ads ── */}
      <MSection sec={META_SECTIONS[13]}>
        <MTipBox>
          Paid advertising sends the retailer algorithm a signal about who buys your book. If your metadata is misaligned, the algorithm interprets those buyers incorrectly — and your ad performance suffers. Metadata before marketing is not optional.
        </MTipBox>
        <div className="space-y-3">
          {[
            { path:'launch.systemsAligned',         label:'All metadata systems are aligned (ISBN records, retailer pages, and categories match)', hint:'Bowker/Nielsen, Amazon, IngramSpark, D2D — all consistent.' },
            { path:'launch.categoriesAccurate',      label:'Retailer categories accurately represent the book', hint:'Not just the most competitive — the most accurate for your actual readership.' },
            { path:'launch.classificationIntentional',label:'Primary subject classification is intentional (not defaulted)', hint:'You consciously chose fantasy-first or romance-first, not whatever the upload form defaulted to.' },
            { path:'launch.proofChecked',            label:'Physical proof copies reviewed and approved', hint:'Print metadata (spine, copyright page, ISBN barcode) matches digital records.' },
            { path:'launch.descriptionFinalised',    label:'Blurb is final and optimised for both reader appeal and search', hint:'Last chance before launch — changes after ads start running reset the algorithm.' },
            { path:'launch.seriesLinked',            label:'Series page confirmed live and all books linked', hint:'On Amazon: check the series page by searching the series name. All books should group together.' },
          ].map(({ path, label, hint }) => {
            const keys = path.split('.');
            const val = keys.reduce((o, k) => o?.[k], data);
            return <MCheckItem key={path} path={path} value={val} label={label} hint={hint} />;
          })}
        </div>
        <MField>
          <MLabel hint="If sales are slow, would metadata be the first thing you audit?">If early sales are slow, is metadata your first audit point?</MLabel>
          <MYesNo path="launch.reviewMetaFirst" value={data.launch.reviewMetaFirst} />
        </MField>
      </MSection>

      {/* ── S15: Positioning Clarity ── */}
      <MSection sec={META_SECTIONS[14]}>
        <MTipBox>
          One-sentence positioning is the test of whether your metadata is coherent. If you can't finish this sentence clearly, your categories, keywords, and blurb probably aren't aligned either. This is the gut-check before everything goes live.
        </MTipBox>
        <MField>
          <MLabel hint="This affects which readership your metadata targets first">This book is…</MLabel>
          <MSelect path="positioning.genreType" value={data.positioning.genreType}
            options={[
              'Fantasy with romance (fantasy-first)',
              'Romance set in a fantasy world (romance-first)',
              'Epic fantasy with a significant romantic subplot',
              'Dark fantasy with romantic tension (not full romance arc)',
              'Political fantasy with romantic subplot',
              'Romantasy — equal weight fantasy plot and romance arc',
              'Fae / Court fantasy romance',
              'Dragon / Rider fantasy romance',
              'Slow-burn romantasy with morally grey leads',
              'Fantasy of manners with romantic elements',
              'Mythology-based romantic fantasy',
              'Romantic fantasy adventure / quest',
            ]} />
        </MField>
        <MField>
          <MLabel hint="Complete this: 'My book is [genre] for readers who love [comp/trope], featuring [hook/element]'">One-Sentence Positioning Statement</MLabel>
          <textarea value={data.positioning.statement || ''} onChange={e => update('positioning.statement', e.target.value)}
            placeholder="e.g. The Ember Gate is an epic political romantasy for readers who love morally grey kings and enemies-to-lovers tension, set in a world where memory is power and loyalty costs everything."
            rows={3} className="w-full resize-none rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-800 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20"
            style={{ userSelect:'text', WebkitUserSelect:'text' }} />
        </MField>
        <MField>
          <MLabel hint="Who are your reader's other favourite books? Use these as comp titles in blurb, categories, and keywords.">Comp Titles (for internal reference)</MLabel>
          <MTextInput path="positioning.compTitles" value={data.positioning?.compTitles} placeholder="e.g. Fourth Wing, From Blood and Ash, A Court of Thorns and Roses, The Bridge Kingdom…" wide />
        </MField>
      </MSection>

    </div>
    </MetaCtx.Provider>
  );
}

/* ═══════════════════════════════════════════════════════════
   DISTRIBUTION TAB — ISBN & Platform Launch Tracker
═══════════════════════════════════════════════════════════ */

/* Format labels + accent colours for the "Your ISBNs" panel and platform chips.
   The actual ISBNs come from the user's own Metadata tab, not from here. */
const FORMAT_META = {
  ebook:     { label:'eBook',     color:'amber'  },
  paperback: { label:'Paperback', color:'violet' },
  hardback:  { label:'Hardback',  color:'sky'    },
  audiobook: { label:'Audiobook', color:'teal'   },
};
const FORMAT_COLORS = ['violet','sky','teal','rose','amber'];

const PLATFORM_DATA = [
  /* ── SET UP FIRST ── */
  {
    id:'kdp-ebook', group:'today', groupLabel:'Set up first',
    name:'Amazon KDP — Ebook', icon:'📦',
    url:'https://kdp.amazon.com',
    urlLabel:'KDP Dashboard',
    formats:['ebook'],
    tip:'Add your Kindle ebook from your Bookshelf — it takes about 20 minutes.',
    alert:'⚠️ If you enrol in KDP Select you CANNOT publish the ebook anywhere else until you unenroll. Going wide? Save it as a draft and do not submit to other ebook platforms until you have unenrolled.',
    steps:[
      'From the KDP Bookshelf click + Kindle eBook',
      'Enter your ebook ISBN — or take a free KDP-assigned one if this edition is KDP-only',
      'Enter your title, series name and series number',
      'Enter your author name and paste your full description',
      'Add up to 7 keyword phrases (50 chars max each) and 2 BISAC categories',
      'Upload your EPUB and cover JPG. Price £2.99–£9.99 for the 70% royalty band',
      'Going wide? Save as a draft — you cannot publish elsewhere while in KDP Select',
    ],
  },
  {
    id:'nielsen-email', group:'today', groupLabel:'Set up first',
    name:'Nielsen — Email Helpdesk', icon:'🇬🇧',
    url:'mailto:pubhelp.book@nielseniq.com',
    urlLabel:'Email Nielsen',
    formats:['ebook','paperback','hardback'],
    tip:'Nielsen Title Editor registration can take up to 10 days — email to expedite if your publication date is close.',
    steps:[
      'Email pubhelp.book@nielseniq.com — subject: "Title Editor account setup"',
      'Include your publisher name, all your ISBNs and your publication date',
      'Ask them to confirm when your account will be active',
    ],
  },
  /* ── CORE PLATFORMS ── */
  {
    id:'ingramspark', group:'week1', groupLabel:'Core platforms — prepare as drafts',
    name:'IngramSpark — Print', icon:'🌐',
    url:'https://myaccount.ingramspark.com/Account/Signup',
    urlLabel:'Create Account',
    formats:['paperback','hardback'],
    tip:'Global trade distribution — reaches Waterstones, libraries and indie bookshops worldwide. Allow 3–5 days for file approval.',
    steps:[
      'Create an account — your publisher name must match your imprint exactly everywhere else',
      'Click Add Title. Add each print ISBN as a separate title',
      'Upload a press-ready interior PDF + cover PDF for each',
      'Set your trade discount (55% for full bookshop stocking) and returns policy',
      'Set pricing in GBP, USD and EUR per territory',
      'No setup fee to budget for — IngramSpark abolished title setup and revision fees in 2023',
      'Submit — allow 3–5 business days for review and approval',
    ],
  },
  {
    id:'kobo', group:'week1', groupLabel:'Core platforms — prepare as drafts',
    name:'Kobo Writing Life — Ebook', icon:'☕',
    url:'https://writinglife.kobo.com',
    urlLabel:'Kobo Writing Life',
    formats:['ebook'],
    tip:'Strong in the UK, Canada and EU. Set up the account and prepare the draft now.',
    steps:[
      'Create an account at writinglife.kobo.com',
      'Click Add a Book · enter your ebook ISBN',
      'Upload your EPUB + cover · fill in all metadata fields',
      'Match your Amazon price',
      'Optionally opt into Kobo Plus (non-exclusive subscription — extra revenue)',
      'In KDP Select? Save as a draft until you unenroll',
    ],
  },
  {
    id:'google-play', group:'week1', groupLabel:'Core platforms — prepare as drafts',
    name:'Google Play Books — Ebook', icon:'▶️',
    url:'https://play.google.com/books/publish',
    urlLabel:'Publisher Centre',
    formats:['ebook'],
    tip:'Strong global reach, especially on Android. Going direct earns a better royalty than via an aggregator.',
    steps:[
      'Go to play.google.com/books/publish · sign in with a Google account',
      'Accept the Partner Programme Agreement',
      'Click Add Book · enter your ebook ISBN',
      'Upload your EPUB · upload the cover separately as a JPG (5MB+ recommended)',
      'Set pricing by territory',
      'In KDP Select? Save as a draft until you unenroll',
    ],
  },
  {
    id:'d2d', group:'week1', groupLabel:'Core platforms — prepare as drafts',
    name:'Draft2Digital — Ebook (Apple, B&N, Scribd, OverDrive + more)', icon:'⚡',
    url:'https://www.draft2digital.com/register/',
    urlLabel:'Create Free Account',
    formats:['ebook'],
    tip:'One upload covers Apple Books, B&N Nook, Scribd, OverDrive/Libby, Hoopla, Tolino, Vivlio and the Palace Marketplace.',
    steps:[
      'Create a free account at draft2digital.com',
      'Click Add a Book · enter your ebook ISBN',
      'Upload your EPUB + cover · fill in metadata and pricing',
      'On the Distribution page, deselect any channel you are going direct on (e.g. Amazon, Kobo, Google Play)',
      'Select all remaining channels',
      'In KDP Select? Save as a draft until you unenroll',
    ],
  },
  {
    id:'bookvault', group:'week1', groupLabel:'Core platforms — prepare as drafts',
    name:'BookVault — Print (UK POD)', icon:'🌿',
    url:'https://www.bookvault.app',
    urlLabel:'Create Account',
    formats:['paperback','hardback'],
    tip:'UK-owned eco print-on-demand — excellent for UK/EU supply and the Gardners, Bertrams and OverDrive library networks.',
    steps:[
      'Create a free account at bookvault.app',
      'Click New Title · enter your ISBN · upload interior PDF + cover PDF',
      'Fill in metadata: title, author, BISAC, description, pricing in GBP + EUR',
      'Enable Distribution to activate UK/EU trade and library supply',
    ],
  },
  /* ── ADDITIONAL CHANNELS ── */
  {
    id:'nielsen-records', group:'week2', groupLabel:'Additional channels',
    name:'Nielsen Title Editor — Enter Your Title Records', icon:'📋',
    url:'http://www.nielsentitleeditor.com/titleeditor/',
    urlLabel:'Nielsen Title Editor',
    formats:['ebook','paperback','hardback'],
    tip:'Enter your records as soon as access is granted. Waterstones and UK library systems pull from Nielsen.',
    steps:[
      'Log in at nielsentitleeditor.com/titleeditor/ once your account is active',
      'Create one title record per ISBN/format',
      'For each: title, series name, series number, author, format and publication date',
      'Add your BISAC and Thema codes',
      'Paste your short description (250 chars) and long description (4,000 chars)',
      'Upload the cover: JPG/PNG, 300dpi, 1:1.5 ratio',
      'Set the RRP in GBP for each format · submit all records',
    ],
  },
  {
    id:'bn-press', group:'week2', groupLabel:'Additional channels',
    name:'Barnes & Noble Press — Ebook + Print', icon:'📚',
    url:'https://press.barnesandnoble.com',
    urlLabel:'B&N Press',
    formats:['ebook','hardback'],
    tip:'US Nook + print market. Set up the draft now; publish the ebook once you are out of KDP Select.',
    steps:[
      'Create a free account at press.barnesandnoble.com',
      'Add the ebook: enter your ebook ISBN · upload EPUB + cover · set USD price',
      'Add the print edition: enter your print ISBN · upload interior PDF + cover',
      'Save the ebook as a draft until you are out of KDP Select · print can publish when ready',
    ],
  },
  {
    id:'payhip', group:'week2', groupLabel:'Additional channels',
    name:'Payhip — Direct Ebook Sales', icon:'💜',
    url:'https://payhip.com/auth/register',
    urlLabel:'Create Free Account',
    formats:['ebook'],
    tip:'Sell direct from your own site — 97% royalty (3% fee only). Pair with BookFunnel for delivery.',
    steps:[
      'Create a free account at payhip.com',
      'Click Add Product → Digital Download · upload your EPUB · set a price',
      'Add your full description and content warnings (Payhip allows free-text warnings)',
      'Connect Stripe or PayPal for payments',
      'Pair with BookFunnel for smooth reader delivery — embed the buy button on your own site',
      'In KDP Select? Keep the product hidden until you unenroll',
    ],
  },
  {
    id:'author-central', group:'week2', groupLabel:'Additional channels',
    name:'Amazon Author Central', icon:'👤',
    url:'https://author.amazon.co.uk',
    urlLabel:'Author Central UK',
    formats:[],
    tip:'Claim your author page once your KDP ebook is live. Add a bio, photo and social links — it significantly improves discoverability.',
    steps:[
      'Go to author.amazon.co.uk · sign in with your Amazon account',
      'Click Add a Book · search by title or ASIN',
      'Add your author bio (150–200 words), author photo and social/website links',
      'Connect your blog RSS if you have one',
    ],
  },
  /* ── LAUNCH DAY & POST-LAUNCH ── */
  {
    id:'kdp-unenroll', group:'launch', groupLabel:'Launch day & post-launch',
    name:'KDP Select — Unenroll (only if you enrolled)', icon:'🔓',
    url:'https://kdp.amazon.com/en_US/bookshelf',
    urlLabel:'KDP Bookshelf',
    formats:['ebook'],
    tip:'Only relevant if you enrolled in KDP Select. The moment the book goes live, go to Bookshelf → … → Manage KDP Select → uncheck auto-renewal. Wait for KDP to confirm unenrollment before publishing elsewhere.',
    alert:'🚨 In KDP Select? Do NOT publish the ebook anywhere else until you receive the KDP unenrollment confirmation. Select is exclusive — listing the ebook elsewhere while enrolled can put your account at risk.',
    steps:[
      'Go to the KDP Bookshelf · find your Kindle ebook',
      'Click … → Manage KDP Select → Manage KDP Select Enrollment',
      'Uncheck "Keep this book enrolled in KDP Select"',
      'Wait for the KDP confirmation message — it tells you exactly when unenrollment is processed',
      'Only after that message: publish to Kobo, Google Play, D2D, Payhip and B&N',
    ],
  },
  {
    id:'publish-wide', group:'launch', groupLabel:'Launch day & post-launch',
    name:'Publish Wide — All Ebook Drafts', icon:'🚀',
    url:'',
    urlLabel:'',
    formats:['ebook'],
    tip:'Once any KDP Select exclusivity is cleared, go through each platform and hit publish. Most go live within 24–72 hours.',
    steps:[
      'Confirm you are clear of KDP Select exclusivity (if you ever enrolled)',
      'Kobo Writing Life → find draft → Publish',
      'Google Play Books → find draft → Publish',
      'Draft2Digital → find draft → Publish to all selected channels',
      'Barnes & Noble Press → find ebook draft → Publish',
      'Payhip → set product from Hidden → Live',
      'All platforms typically live within 1–3 days',
    ],
  },
  {
    id:'legal-deposit', group:'launch', groupLabel:'Launch day & post-launch',
    name:'British Library — Legal Deposit (launch week)', icon:'⚖️',
    url:'https://www.bl.uk/services/legal-deposit',
    urlLabel:'BL Legal Deposit',
    formats:['paperback','hardback'],
    tip:'UK law (Legal Deposit Libraries Act 2003): you MUST send one copy of each print edition to the British Library within one month of publication. This applies with or without an ISBN. Do it in launch week so it is not forgotten.',
    steps:[
      'Post one copy of each print edition to the British Library',
      'Include a delivery note with each ISBN (if any), title, author, publisher and publication date',
      'Address: Legal Deposit Office · British Library · Boston Spa · Wetherby · LS23 7BY',
      'The other five legal-deposit libraries (Bodleian, Cambridge, Trinity College Dublin, National Library of Scotland, National Library of Wales) may claim a copy — they request it in writing, within one year, via the Agency for the Legal Deposit Libraries',
    ],
  },
];

const STATUS_OPTIONS = [
  { value:'not_started', label:'Not Started', color:'bg-stone-100 text-stone-500', dot:'bg-stone-400' },
  { value:'in_progress', label:'In Progress', color:'bg-stone-100 text-gold-deep', dot:'bg-stone-500' },
  { value:'draft_ready', label:'Draft Ready', color:'bg-blue-100 text-blue-700',   dot:'bg-blue-500'  },
  { value:'live',        label:'Live ✓',      color:'bg-green-100 text-green-700', dot:'bg-green-500' },
];

const GROUPS = [
  { id:'today', label:'Set up first',                       color:'border-red-200   bg-red-50/30'    },
  { id:'week1', label:'Core platforms — prepare as drafts', color:'border-stone-200 bg-stone-50/30'  },
  { id:'week2', label:'Additional channels',                color:'border-gold-soft  bg-parchment/40'   },
  { id:'launch',label:'Launch day & post-launch',           color:'border-purple-200 bg-purple-50/30'},
];

const ISBN_BRAND = { bg:'bg-parchment-dark', text:'text-gold-deep', dot:'bg-gold' };
const ISBN_COLORS = {
  violet: ISBN_BRAND, sky: ISBN_BRAND, amber: ISBN_BRAND, rose: ISBN_BRAND, teal: ISBN_BRAND,
};

function DistributionTab({ distData, onSave, book, formats }) {
  const LAUNCH_DATE = book?.releaseDate ? new Date(book.releaseDate) : null;

  const [statuses, setStatuses] = useState(() => distData?.statuses || {});
  const [notes,    setNotes]    = useState(() => distData?.notes    || {});
  const [expanded, setExpanded] = useState({});
  const [dirty,    setDirty]    = useState(false);
  const [saved,    setSaved]    = useState(false);

  // Countdown
  const [countdown, setCountdown] = useState('');
  useEffect(() => {
    if (!LAUNCH_DATE) { setCountdown(''); return; }
    const target = LAUNCH_DATE.getTime();
    const tick = () => {
      const diff = target - new Date().getTime();
      if (diff <= 0) { setCountdown('🎉 Book is live!'); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000)  / 60000);
      setCountdown(`${d}d ${h}h ${m}m`);
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [book?.releaseDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = (id, val) => {
    const s = { ...statuses, [id]: val };
    setStatuses(s); setDirty(true); setSaved(false);
  };
  const setNote = (id, val) => {
    const n = { ...notes, [id]: val };
    setNotes(n); setDirty(true); setSaved(false);
  };
  const toggle = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const handleSave = () => {
    onSave({ statuses, notes });
    setDirty(false); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const totalPlatforms  = PLATFORM_DATA.length;
  const livePlatforms   = Object.values(statuses).filter(s => s === 'live').length;
  const draftPlatforms  = Object.values(statuses).filter(s => s === 'draft_ready').length;
  const progressPct     = Math.round((livePlatforms / totalPlatforms) * 100);

  return (
    <div className="space-y-5">

      {/* ── Countdown Banner ── */}
      <div className="overflow-hidden rounded-2xl bg-ink p-5 shadow-lg">
        <div className="flex flex-col items-center gap-1 text-center sm:flex-row sm:justify-between sm:text-left">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-gold">{book?.title ? `${book.title} · ` : ''}Launch Countdown</p>
            <p className="mt-0.5 text-2xl font-bold text-white">{LAUNCH_DATE ? countdown : 'No release date set'}</p>
            <p className="text-xs text-stone-400">{LAUNCH_DATE ? `Publication date: ${LAUNCH_DATE.toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' })}` : 'Add a target release date in the Timeline tab'}</p>
          </div>
          <div className="flex gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-green-400">{livePlatforms}</p>
              <p className="text-xs text-stone-400 uppercase tracking-widest">Live</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gold">{draftPlatforms}</p>
              <p className="text-xs text-stone-400 uppercase tracking-widest">Draft Ready</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gold">{progressPct}%</p>
              <p className="text-xs text-stone-400 uppercase tracking-widest">Complete</p>
            </div>
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
          <div className="h-full rounded-full bg-gold transition-all" style={{width:`${progressPct}%`}}/>
        </div>
      </div>

      {/* ── KDP Select Alert ── */}
      <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600"/>
        <div>
          <p className="text-sm font-bold text-red-800">⚠️ In KDP Select? Wide ebook distribution is blocked until you unenroll</p>
          <p className="mt-1 text-xs text-red-700 leading-relaxed">
            If your Kindle ebook is enrolled in KDP Select it is exclusive to Amazon — you cannot list the ebook on any other
            platform until you unenroll, and a pre-order can't unenroll early (it clears when the book goes live).
            <strong> Set your other ebook platforms to "Draft Ready" now</strong> and publish them as soon as you receive the KDP
            unenrollment confirmation. If you never enrolled in Select, you can ignore this.
          </p>
        </div>
      </div>

      {/* ── ISBN Reference ── */}
      <div className="overflow-hidden rounded-2xl border border-gold-soft bg-white shadow-sm">
        <div className="bg-ink px-5 py-3">
          <p className="font-cinzel text-sm font-bold tracking-wide text-white">Your ISBNs{book?.title ? `: ${book.title}` : ''}</p>
          <p className="text-xs text-parchment/50">Pulled from your Metadata tab — add each format's ISBN there</p>
        </div>
        <div className="grid grid-cols-1 gap-2 p-4 sm:grid-cols-2 lg:grid-cols-3">
          {(formats && formats.length > 0) ? formats.map((f, i) => {
            const c = ISBN_COLORS[FORMAT_COLORS[i % FORMAT_COLORS.length]];
            const label = f.label || f.type || 'Format';
            const isbn  = f.isbn && f.isbn !== 'N/A' && f.isbn !== '' ? f.isbn : '— not set —';
            return (
              <div key={f.id || i} className={`flex items-center gap-2.5 rounded-xl border p-3 ${c.bg} border-current/10`}>
                <div className={`h-2 w-2 shrink-0 rounded-full ${c.dot}`}/>
                <div className="min-w-0">
                  <p className={`text-xs font-bold uppercase tracking-widest ${c.text}`}>{label}</p>
                  <p className="font-mono text-xs text-stone-700">{isbn}</p>
                </div>
              </div>
            );
          }) : (
            <p className="col-span-full py-2 text-xs italic text-stone-400">Add your formats and ISBNs in the Metadata tab to see them here.</p>
          )}
        </div>
      </div>

      {/* ── Platform Groups ── */}
      {GROUPS.map(group => {
        const platforms = PLATFORM_DATA.filter(p => p.group === group.id);
        return (
          <div key={group.id} className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-widest text-gold-deep pl-1">{group.label}</p>
            {platforms.map(platform => {
              const status    = statuses[platform.id] || 'not_started';
              const statusOpt = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
              const isOpen    = !!expanded[platform.id];
              const noteVal   = notes[platform.id] || '';

              return (
                <div key={platform.id} className={`overflow-hidden rounded-2xl border ${group.color} shadow-sm`}>
                  {/* Row header */}
                  <div className="flex items-center gap-3 px-4 py-3">
                    <span className="h-2 w-2 shrink-0 rounded-full bg-gold"/>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-stone-900 leading-snug">{platform.name}</p>
                      {platform.formats.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {platform.formats.map(ft => {
                            const m = FORMAT_META[ft] || { label: ft, color: 'amber' };
                            const c = ISBN_COLORS[m.color];
                            return (
                              <span key={ft} className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${c.bg} ${c.text}`}>
                                {m.label}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {/* Status selector */}
                    <select value={status} onChange={e => setStatus(platform.id, e.target.value)}
                      className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-bold outline-none cursor-pointer ${statusOpt.color}`}
                      style={{userSelect:'text',WebkitUserSelect:'text'}}>
                      {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {/* Expand toggle */}
                    <button onClick={() => toggle(platform.id)} className="shrink-0 rounded-full p-1 text-stone-400 hover:bg-stone-100">
                      {isOpen ? <ChevronUp className="h-4 w-4"/> : <ChevronDown className="h-4 w-4"/>}
                    </button>
                  </div>

                  {/* Expanded content */}
                  {isOpen && (
                    <div className="border-t border-gold-soft bg-white/60 px-4 pb-4 pt-3 space-y-3">
                      {/* Tip */}
                      <p className="text-xs text-stone-600 leading-relaxed italic">{platform.tip}</p>

                      {/* Alert banner */}
                      {platform.alert && (
                        <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3">
                          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600"/>
                          <p className="text-xs text-red-700 leading-relaxed">{platform.alert}</p>
                        </div>
                      )}

                      {/* Link */}
                      {platform.url && platform.urlLabel && (
                        <a href={platform.url} target={platform.url.startsWith('http') ? '_blank' : '_self'}
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-xl bg-stone-900 px-4 py-2 text-xs font-bold text-white hover:bg-ink transition-colors">
                          <ExternalLink className="h-3.5 w-3.5"/> {platform.urlLabel}
                        </a>
                      )}

                      {/* Steps */}
                      <div>
                        <p className="mb-2 text-xs font-bold uppercase tracking-widest text-stone-400">Steps</p>
                        <ol className="space-y-1.5" style={{listStyle:'none',counterReset:'step-c'}}>
                          {platform.steps.map((step, i) => (
                            <li key={i} className="flex items-start gap-2.5">
                              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-stone-200 font-mono text-xs font-bold text-stone-600">{i+1}</span>
                              <p className={`text-xs leading-relaxed ${step.startsWith('⏸') ? 'font-semibold text-blue-700' : step.startsWith('⚠️') || step.startsWith('🚨') ? 'font-semibold text-red-700' : 'text-stone-700'}`}>{step}</p>
                            </li>
                          ))}
                        </ol>
                      </div>

                      {/* Notes field */}
                      <div>
                        <p className="mb-1 text-xs font-bold uppercase tracking-widest text-stone-400">Notes / Account Details</p>
                        <textarea value={noteVal} onChange={e => setNote(platform.id, e.target.value)}
                          placeholder="Add account details, reference numbers, login info, or anything else…"
                          rows={2}
                          className="w-full resize-none rounded-xl border border-stone-200 bg-white px-3 py-2 text-xs text-stone-800 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20"
                          style={{userSelect:'text',WebkitUserSelect:'text'}} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {/* ── Save bar ── */}
      <div className="flex items-center justify-between rounded-2xl border border-gold-soft bg-white p-4 shadow-sm">
        <p className="text-xs text-stone-400">{dirty ? '● Unsaved changes' : 'All changes saved'}</p>
        <Button onClick={handleSave}
          className={`flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-bold transition-all ${
            saved   ? 'bg-green-100 text-green-700 border border-green-200'
          : dirty   ? 'bg-gold-deep text-white hover:bg-gold'
          : 'bg-stone-100 text-stone-400 border border-stone-200'}`}>
          {saved ? <><Check className="h-3.5 w-3.5"/>Saved</> : <><Save className="h-3.5 w-3.5"/>{dirty ? 'Save Progress' : 'Up to date'}</>}
        </Button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN TOOL
═══════════════════════════════════════════════════════════ */
/* Parchment load skeleton shown while licence progress is fetched, so the shell
   never flashes an empty "No books yet" state before data arrives. */
function RoadmapSkeleton() {
  return (
    <div className="animate-pulse space-y-5" aria-hidden="true">
      <div className="overflow-hidden rounded-2xl border border-gold-soft bg-white shadow-sm">
        <div className="h-14 bg-ink/90" />
        <div className="divide-y divide-stone-100">
          {[0, 1, 2].map(i => (
            <div key={i} className="flex items-center gap-3 px-5 py-3.5">
              <div className="h-2 w-2 shrink-0 rounded-full bg-stone-200" />
              <div className="h-3 flex-1 rounded bg-stone-200" />
              <div className="h-3 w-10 shrink-0 rounded bg-stone-200" />
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-3 rounded-2xl border border-gold-soft bg-white p-5 shadow-sm">
        <div className="h-3 w-32 rounded bg-stone-200" />
        <div className="h-2.5 w-full rounded bg-stone-100" />
        <div className="h-2.5 w-5/6 rounded bg-stone-100" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[0, 1, 2].map(i => <div key={i} className="h-16 rounded-xl border border-gold-soft bg-white" />)}
      </div>
    </div>
  );
}

function ChecklistTool({ licenceData, expired = false, signedIn = false, onSignOut }) {
  useAntiCopy();

  const [readOnly, setReadOnly]               = useState(!!expired);
  const [showRenew, setShowRenew]             = useState(false);
  const isTrial = licenceData?.term === 'trial';
  // Formatted expiry (en-GB long date, e.g. "24 July 2027"); null for a
  // legacy/lifetime licence with no expiry, so callers omit the line entirely.
  const accessUntilDate = licenceData?.expiresAtMs
    ? new Date(licenceData.expiresAtMs).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  // Remaining-time chip (H). Whole days, TZ-safe (absolute-ms comparison).
  // Trial: always shown (gold → rose ≤3d). Paid: silent until ≤30d, then a
  // subtle "Expires in N days" that brightens to brand gold ≤7d and opens the renew flow.
  // Copy is deliberately expiry-based (NOT "Renews soon") — this product does
  // not auto-renew, so the wording must match the pricing promise. Expired
  // shows the existing EXPIRED badge, no chip.
  const countdown = (() => {
    const ms = licenceData?.expiresAtMs;
    if (readOnly || ms == null) return null;
    const left = ms - Date.now();
    if (left <= 0) return null;
    const days = Math.floor(left / 86400000);
    const today = days <= 0;
    if (isTrial) {
      return {
        text: today ? 'Trial — expires today' : `Trial — ${days} day${days === 1 ? '' : 's'} left`,
        tone: days <= 3 ? 'rose' : 'gold',
        clickable: false,
      };
    }
    if (days > 30) return null;
    return {
      text: today ? 'Expires today' : `Expires in ${days} day${days === 1 ? '' : 's'}`,
      tone: days <= 7 ? 'gold' : 'muted',
      clickable: true,
    };
  })();
  const countdownTone = {
    gold:  'border-gold/40 bg-gold/10 text-gold',
    rose:  'border-rose-400/50 bg-rose-500/15 text-rose-300',
    muted: 'border-parchment/20 bg-parchment/5 text-parchment/60',
  };
  const [activeTab, setActiveTab]             = useState('timeline');
  const [books, setBooks]                     = useState([]);
  const [selectedId, setSelectedId]           = useState(null);
  const [allProgress, setAllProgress]         = useState({});   // { bookId: { taskId: bool } }
  const [allNotes, setAllNotes]               = useState({});   // { bookId: { taskId: string } }
  const [allTaskDates, setAllTaskDates]       = useState({});   // { bookId: { taskId: date } }
  const [allMetadata, setAllMetadata]         = useState({});   // { bookId: metadataObj }
  const [credentials, setCredentials]         = useState([]);   // shared across books
  const [distData, setDistData]               = useState({});   // distribution tracking
  const [saveState, setSaveState]             = useState('idle'); // idle | saving | saved | error
  const [showAddBook, setShowAddBook]         = useState(false);
  const [loading, setLoading]                 = useState(true);

  const book     = books.find(b => b.id === selectedId);
  const progress = allProgress[selectedId] || {};
  const notes    = allNotes[selectedId]    || {};
  const taskDates = allTaskDates[selectedId] || {};
  const bookMetadata = allMetadata[selectedId] || {};

  /* ── Load ── */
  useEffect(() => {
    (async () => {
      try {
        const res = await getLicenceProgressFn({ key: licenceData.key, email: licenceData.email });
        if (res.data?.expired) setReadOnly(true);
        const d = res.data?.progress;
        if (d) {
          const lb = d.books || [];
          setBooks(lb);
          setAllProgress(d.progress   || {});
          setAllNotes(d.notes         || {});
          setAllTaskDates(d.taskDates || {});
          setCredentials(d.credentials || []);
          setAllMetadata(d.metadata    || {});
          setDistData(d.distData       || {});
          if (lb.length > 0) setSelectedId(lb[0].id);
        }
      } catch (err) {
        /* leave defaults; the real empty state shows once loading resolves */
      } finally {
        setLoading(false);
      }
    })();
  }, [licenceData.key, licenceData.email]);

  /* ── Save ── */
  const save = useCallback(async (overrides = {}) => {
    if (readOnly) return; // expired licence: read-only, never persist
    setSaveState('saving');
    try {
      await saveLicenceProgressFn({
        key:   licenceData.key,
        email: licenceData.email,
        data: {
          books:       overrides.books       ?? books,
          progress:    overrides.progress    ?? allProgress,
          notes:       overrides.notes       ?? allNotes,
          taskDates:   overrides.taskDates   ?? allTaskDates,
          credentials: overrides.credentials ?? credentials,
          metadata:    overrides.metadata    ?? allMetadata,
          distData:    overrides.distData    ?? distData,
        },
      });
      setSaveState('saved');
    } catch (err) {
      // Never a silent no-op — surface loudly so ticks can't be lost quietly.
      console.error('Roadmap save failed:', err?.message || err);
      setSaveState('error');
    }
  }, [readOnly, licenceData, books, allProgress, allNotes, allTaskDates, credentials, allMetadata, distData]);

  // Never lose the last few ticks: warn on close while a save is pending or
  // failed, and retry a failed save when the tab is hidden (best effort).
  useEffect(() => {
    const warnOnLeave = (e) => {
      if (saveState === 'saving' || saveState === 'error') { e.preventDefault(); e.returnValue = ''; }
    };
    const onHide = () => { if (document.visibilityState === 'hidden' && saveState === 'error') save(); };
    window.addEventListener('beforeunload', warnOnLeave);
    document.addEventListener('visibilitychange', onHide);
    return () => { window.removeEventListener('beforeunload', warnOnLeave); document.removeEventListener('visibilitychange', onHide); };
  }, [saveState, save]);

  /* ── Handlers ── (all no-op when read-only, i.e. the licence has expired) */
  const addBook = (nb) => {
    if (readOnly || books.length >= 5) return;
    const updated = [...books, nb];
    setBooks(updated); setSelectedId(nb.id); setShowAddBook(false);
    save({ books: updated });
  };

  const deleteBook = (id) => {
    if (readOnly) return;
    if (!window.confirm('Remove this book and all its progress?')) return;
    const updated = books.filter(b => b.id !== id);
    const prog    = { ...allProgress }; delete prog[id];
    const n       = { ...allNotes };    delete n[id];
    const td      = { ...allTaskDates };delete td[id];
    setBooks(updated); setAllProgress(prog); setAllNotes(n); setAllTaskDates(td);
    setSelectedId(updated.length ? updated[0].id : null);
    save({ books: updated, progress: prog, notes: n, taskDates: td });
  };

  const toggleTask = (taskId) => {
    if (readOnly) return;
    const prog    = { ...(allProgress[selectedId]||{}), [taskId]: !progress[taskId] };
    const newProg = { ...allProgress, [selectedId]: prog };
    setAllProgress(newProg);
    save({ progress: newProg });
  };

  const saveNote = (taskId, value) => {
    if (readOnly) return;
    const n    = { ...(allNotes[selectedId]||{}), [taskId]: value };
    const newN = { ...allNotes, [selectedId]: n };
    setAllNotes(newN);
    save({ notes: newN });
  };

  const saveDateForTask = (taskId, date) => {
    if (readOnly) return;
    const td    = { ...(allTaskDates[selectedId]||{}), [taskId]: date };
    const newTd = { ...allTaskDates, [selectedId]: td };
    setAllTaskDates(newTd);
    save({ taskDates: newTd });
  };

  const saveCredentials = (updated) => {
    if (readOnly) return;
    setCredentials(updated);
    save({ credentials: updated });
  };

  const saveDistData = (updated) => {
    if (readOnly) return;
    setDistData(updated);
    save({ distData: updated });
  };

  const saveBookMetadata = (updatedMeta) => {
    if (readOnly) return;
    const newMeta = { ...allMetadata, [selectedId]: updatedMeta };
    setAllMetadata(newMeta);
    save({ metadata: newMeta });
  };

  const updateReleaseDate = (date) => {
    if (readOnly) return;
    const updated = books.map(b => b.id === selectedId ? { ...b, releaseDate: date } : b);
    setBooks(updated);
    save({ books: updated });
  };

  const bookPercent = (bookId) => {
    const p = allProgress[bookId] || {};
    return Math.round(CHECKLIST.flatMap(ph=>ph.tasks).filter(t=>p[t.id]).length / TOTAL_TASKS * 100);
  };

  const daysLeft = (() => {
    if (!book?.releaseDate) return null;
    return Math.ceil((new Date(book.releaseDate) - new Date()) / 86400000);
  })();

  const tabs = [
    { id:'timeline',     label:'Timeline',     Icon:Table2      },
    { id:'distribution', label:'Distribution', Icon:Rocket      },
    { id:'metadata',     label:'Metadata',     Icon:Database    },
    { id:'vault',        label:'Vault',        Icon:Lock        },
  ];

  if (showRenew) return <PricingPlans product={licenceData.scope || 'roadmap'} renewalKey={licenceData.key} defaultEmail={licenceData.email} fromTrial={isTrial} onBack={() => setShowRenew(false)} />;

  return (
    <div className="min-h-screen bg-parchment-tint overflow-x-hidden"
      style={{fontFamily:"'Cormorant Garamond',Georgia,serif",userSelect:'none',WebkitUserSelect:'none'}}>
      <Watermark email={licenceData.email} />

      {/* ── Header ── */}
      <div className="sticky top-0 z-40 border-b border-gold-soft bg-ink/95 shadow-sm backdrop-blur-md">
        {/* THE HEADER MUST WRAP. The controls group used to be `shrink-0`, which
            was fine when it held two chips — once the toolkit nav joined it, the
            group could no longer fit and overflowed straight across the title
            ("Indie Publishing Roadmap" with the trial chip mashed over it).
            Now the row wraps and the controls group shrinks, so items reflow
            onto a second line instead of colliding. */}
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-3">
          <div className="flex min-w-[11rem] flex-1 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gold/40 bg-gold/15 shadow-sm">
              <BookOpen className="h-5 w-5 text-gold"/>
            </div>
            <div className="min-w-0">
              <h1 className="truncate font-cinzel text-sm font-bold leading-tight tracking-wide text-gold">Indie Publishing Roadmap</h1>
              <p className="truncate text-xs text-parchment/40">Narratoria Ltd</p>
            </div>
          </div>
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5 sm:gap-2">
            {saveState === 'saving' && <span className="text-xs font-semibold uppercase tracking-widest text-parchment/50">Saving…</span>}
            {saveState === 'saved'  && <span className="text-xs font-semibold uppercase tracking-widest text-green-400">Saved</span>}
            {saveState === 'error'  && (
              <button type="button" onClick={() => save()} title="Your last change didn't save — click to retry"
                className="flex items-center gap-1 rounded-full border border-rose-400/50 bg-rose-500/15 px-2.5 py-1 text-xs font-bold uppercase tracking-widest text-rose-300 hover:bg-rose-500/25">
                <AlertTriangle className="h-3 w-3"/> Not saved — retry
              </button>
            )}
            {countdown && (countdown.clickable ? (
              <button type="button" onClick={() => setShowRenew(true)} title="Renew your licence"
                className={`whitespace-nowrap rounded-full border px-2 py-1 text-xs font-semibold uppercase tracking-widest transition-all hover:brightness-110 sm:px-3 ${countdownTone[countdown.tone]}`}>
                {countdown.text}
              </button>
            ) : (
              <span className={`whitespace-nowrap rounded-full border px-2 py-1 text-xs font-semibold uppercase tracking-widest sm:px-3 ${countdownTone[countdown.tone]}`}>
                {countdown.text}
              </span>
            ))}
            <div title={!readOnly && accessUntilDate ? `Access until ${accessUntilDate}` : undefined}
              className={`flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-1 sm:px-3 ${readOnly ? 'border-rose-400/40 bg-rose-500/10' : 'border-gold/40 bg-gold/10'}`}>
              <Shield className={`h-3 w-3 ${readOnly ? 'text-rose-400' : 'text-gold'}`}/>
              <span className={`hidden text-xs font-semibold uppercase tracking-widest sm:inline ${readOnly ? 'text-rose-300' : 'text-gold'}`}>{readOnly ? 'Expired' : 'Licensed'}</span>
            </div>
            {/* Additive toolkit nav — a way back to the hub and across to the
                other two tools. Previously this header offered only Licensed
                and Sign out, with no route out of the tool. */}
            <ToolNav current="roadmap" variant="ink" />
            {signedIn && onSignOut && (
              <button type="button" onClick={onSignOut} title="Sign out"
                className="flex shrink-0 items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2 py-1 text-parchment/60 transition-colors hover:border-white/20 hover:text-parchment sm:px-3">
                <LogOut className="h-3 w-3"/>
                <span className="hidden text-xs font-semibold uppercase tracking-widest sm:inline">Sign out</span>
              </button>
            )}
          </div>
        </div>
        {/* Tab bar */}
        <div className="mx-auto flex max-w-3xl gap-0 overflow-x-auto border-t border-gold-soft px-1 sm:px-4">
          {tabs.map(({ id, label, Icon }) => (
            <Button key={id} variant="ghost" onClick={() => { if (saveState === 'error') save(); setActiveTab(id); }}
              className={`font-cinzel h-auto shrink-0 justify-center rounded-none flex items-center gap-1 border-b-2 px-3 py-2.5 text-xs font-semibold transition-all sm:justify-start sm:gap-2 sm:px-4 sm:text-sm ${activeTab===id
                ? 'border-gold text-gold'
                : 'border-transparent text-parchment/50 hover:text-parchment/80'}`}>
              <Icon className="h-4 w-4 shrink-0"/><span>{label}</span>
            </Button>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-3xl space-y-5 px-4 py-6">

        {/* ── Expired-licence renewal banner (read-only mode) ── */}
        {readOnly && (
          <div className="overflow-hidden rounded-2xl border border-rose-200 bg-rose-50 shadow-sm">
            <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600"/>
                <div>
                  <p className="text-sm font-bold text-rose-900">{isTrial ? 'Your free trial has ended — the roadmap is read-only' : 'Your licence has expired — the roadmap is read-only'}</p>
                  <p className="mt-0.5 text-xs text-rose-700 leading-relaxed">{isTrial ? 'Your progress is saved — nothing has been deleted. Choose a plan to continue, and your work carries straight over.' : 'Your books and progress are safe and still here to view. Nothing has been deleted. Renew to start editing again.'}</p>
                </div>
              </div>
              <button type="button" onClick={() => setShowRenew(true)}
                className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-xl bg-rose-600 px-4 py-2 text-xs font-bold text-white hover:bg-rose-700 transition-colors">
                <RefreshCw className="h-3.5 w-3.5"/> {isTrial ? 'Choose a plan' : 'Renew licence'}
              </button>
            </div>
          </div>
        )}

        {loading ? <RoadmapSkeleton /> : (
        <>
        {/* ── Book shelf (shown on Roadmap + Timeline + Metadata tabs) ── */}
        {activeTab !== 'vault' && activeTab !== 'distribution' && (
          <div className="overflow-hidden rounded-2xl border border-gold-soft bg-white shadow-sm">
            <div className="flex items-center justify-between bg-ink px-5 py-3">
              <div>
                <h2 className="font-cinzel text-sm font-bold tracking-wide text-gold">My Books</h2>
                <p className="text-xs text-parchment/60">{books.length}/5 books — progress tracked independently</p>
              </div>
              {books.length < 5 && !readOnly && (
                <Button variant="ghost" onClick={() => setShowAddBook(true)}
                  className="flex items-center gap-1.5 rounded-xl border border-gold/30 bg-gold/15 px-3 py-1.5 text-xs font-semibold text-gold hover:bg-gold/25">
                  <Plus className="h-3.5 w-3.5"/>Add Book
                </Button>
              )}
            </div>

            {books.length === 0 ? (
              <div className="p-8 text-center">
                <BookOpen className="mx-auto mb-3 h-10 w-10 text-stone-200"/>
                <p className="text-sm font-semibold text-stone-600">No books yet</p>
                {!readOnly && (
                  <Button onClick={() => setShowAddBook(true)} className="mt-3 rounded-xl bg-gold-deep px-5 py-2.5 text-sm font-bold text-white hover:bg-gold">Add First Book</Button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-stone-50">
                {books.map(b => {
                  const pct      = bookPercent(b.id);
                  const selected = b.id === selectedId;
                  return (
                    <div key={b.id} onClick={() => setSelectedId(b.id)}
                      className={`flex cursor-pointer items-center gap-3 px-5 py-3 transition-colors ${selected ? 'bg-stone-50' : 'hover:bg-stone-50'}`}>
                      <div className={`h-2 w-2 shrink-0 rounded-full ${selected ? 'bg-gold' : 'bg-stone-200'}`}/>
                      <div className="min-w-0 flex-1">
                        <p className={`truncate text-sm font-semibold ${selected ? 'text-gold-deep' : 'text-stone-800'}`}>{b.title}</p>
                        {b.series && <p className="truncate text-xs text-stone-400">{b.series}</p>}
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        <div className="text-right">
                          <p className="text-xs font-bold text-gold-deep">{pct}%</p>
                          <div className="mt-0.5 h-1 w-16 overflow-hidden rounded-full bg-stone-100">
                            <div className="h-full bg-gold transition-all" style={{width:`${pct}%`}}/>
                          </div>
                        </div>
                        <button onClick={e => { e.stopPropagation(); deleteBook(b.id); }}
                          className="rounded-full p-1 text-stone-300 hover:bg-red-50 hover:text-red-400">
                          <Trash2 className="h-3.5 w-3.5"/>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Active book header ── */}
        {book && activeTab !== 'vault' && activeTab !== 'distribution' && (
          <div className="overflow-hidden rounded-2xl border border-gold-soft bg-white shadow-sm">
            <div className="grid gap-4 p-5 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-stone-400">Currently viewing</label>
                <p className="text-base font-bold text-stone-900">{book.title}</p>
                {book.series && <p className="text-xs text-stone-400">{book.series}</p>}
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-widest text-stone-400">Target Release Date</label>
                <input type="date" value={book.releaseDate||''} onChange={e=>updateReleaseDate(e.target.value)}
                  className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm text-stone-900 outline-none focus:border-gold focus:ring-2 focus:ring-gold/25"
                  style={{userSelect:'text',WebkitUserSelect:'text'}} />
              </div>
            </div>
            {daysLeft !== null && (
              <div className={`flex items-center gap-2 border-t px-5 py-3 text-sm font-semibold ${daysLeft>=0?'border-green-100 bg-green-50/60 text-green-700':'border-gold-soft bg-stone-50/60 text-gold-deep'}`}>
                <Calendar className="h-4 w-4"/>
                {daysLeft>=0 ? `${daysLeft} days until release` : `${Math.abs(daysLeft)} days since release — congratulations! 🎉`}
              </div>
            )}
          </div>
        )}

        {/* ── Tab content ── */}
        {activeTab === 'timeline' && book && (
          <TimelineTab
            progress={progress}
            onToggleTask={toggleTask}
            taskNotes={notes}
            onSaveNote={saveNote}
            taskDates={taskDates}
            onSaveDateForTask={saveDateForTask}
            bookTitle={book.title}
          />
        )}
        {activeTab === 'metadata' && book && (
          <MetadataTab
            metadata={bookMetadata}
            onSave={saveBookMetadata}
          />
        )}
        {activeTab === 'vault' && (
          <VaultTab
            credentials={credentials}
            onSave={saveCredentials}
            licenceKey={licenceData.key}
          />
        )}
        {activeTab === 'distribution' && (
          <DistributionTab
            distData={distData}
            onSave={saveDistData}
            book={book}
            formats={bookMetadata?.formats}
          />
        )}

        {/* No book selected state */}
        {activeTab !== 'vault' && activeTab !== 'distribution' && !book && books.length > 0 && (
          <div className="rounded-2xl border border-dashed border-stone-200 bg-white p-10 text-center">
            <BookOpen className="mx-auto mb-3 h-8 w-8 text-stone-200"/>
            <p className="text-sm font-semibold text-stone-500">Select a book above to view its {activeTab}</p>
          </div>
        )}
        </>
        )}

        {/* Footer */}
        <div className="rounded-2xl border border-gold-soft bg-gradient-to-br from-stone-50 to-stone-100/50 p-5 text-center">
          <BookOpen className="mx-auto mb-2 h-6 w-6 text-gold-deep"/>
          <p className="font-cinzel text-sm font-bold tracking-wide text-stone-900">Narratoria Ltd</p>
          <p className="mt-1 text-xs text-stone-500">Licenced to: <span className="font-semibold text-gold-deep">{licenceData.email}</span></p>
          {accessUntilDate && (
            <p className="mt-1 text-xs text-stone-500">Access until <span className="font-semibold text-gold-deep">{accessUntilDate}</span></p>
          )}
          <p className="mt-2 text-xs text-stone-400">This tool is licenced for personal use only. Redistribution, resale, or sharing of access is prohibited.</p>
          <p className="mt-2 text-xs text-stone-400">Narratoria is best experienced on an up-to-date version of Chrome, Safari, Edge or Firefox on desktop and mobile. Appearance may vary on older devices or operating systems.</p>
        </div>
      </div>

      {showAddBook && <AddBookModal onAdd={addBook} onClose={() => setShowAddBook(false)} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   ROOT
═══════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════
   SUBSCRIPTION TIER PREVIEW — shown to free-tier authors
═══════════════════════════════════════════════════════════ */
function SubscriptionPreview() {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-ink-900 via-ink to-ink-900" style={{fontFamily:"'Cormorant Garamond',Georgia,serif"}}>
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-gold/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-gold-deep/10 blur-3xl" />
      </div>
      <div className="relative w-full max-w-lg text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gold shadow-2xl shadow-black/50">
          <BookOpen className="h-8 w-8 text-white" />
        </div>
        <h1 className="font-cinzel text-3xl font-bold text-white">Indie Publishing<br /><span className="text-gold">Roadmap</span></h1>
        <p className="mt-2 text-sm text-parchment/50">by Narratoria Ltd</p>

        <div className="mt-8 overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-2xl backdrop-blur-sm p-6">
          <Lock className="mx-auto mb-4 h-10 w-10 text-gold" />
          <h2 className="font-cinzel text-xl font-semibold text-white">Premium Feature</h2>
          <p className="mt-3 text-sm text-parchment/80 leading-relaxed">
            The Indie Publishing Roadmap is a comprehensive checklist tool that guides you through
            every step of self-publishing — from ISBN registration to launch day and beyond.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-3 text-left">
            {[
              'Timeline & task tracking',
              'Distribution channel setup',
              'Metadata management',
              'Secure credential vault',
              'Platform-specific guides',
              'Progress auto-save',
            ].map(feature => (
              <div key={feature} className="flex items-center gap-2 text-xs text-parchment/60">
                <CheckCircle className="h-3.5 w-3.5 text-gold-deep shrink-0" />
                {feature}
              </div>
            ))}
          </div>
          <div className="mt-6 rounded-xl border border-gold-soft bg-gold/10 p-4">
            <p className="text-sm font-medium text-gold">Upgrade to Pro or Publisher to unlock</p>
            <p className="mt-1 text-xs text-gold/70">Coming soon — we'll notify you when paid tiers are available.</p>
          </div>
          <a href="/author/dashboard" className="mt-4 inline-block text-sm text-parchment/50 hover:text-gold underline">
            Back to Dashboard
          </a>
        </div>
      </div>
    </div>
  );
}

export default function PublishingRoadmap({ authorSubscription } = {}) {
  // Licence resolution (Google auto-unlock → cached key → gate) is shared with
  // the Planner via useLicence. Paid authors get a synthetic bundle licence.
  const {
    checking, licenceData, expired, authUser, isFreeAuthor,
    setUnlocked, handleSignOut: signOutLicence,
  } = useLicence('roadmap', { authorSubscription });

  // Pricing view is URL-addressable: /publishing-roadmap?view=plans (or view=pricing)
  // lands straight on the plans instead of the gate, so marketing CTAs can deep-link it.
  const wantsPlansParam = typeof window !== 'undefined'
    ? ['plans', 'pricing'].includes(new URLSearchParams(window.location.search).get('view'))
    : false;
  const [showBuy, setShowBuy]         = useState(wantsPlansParam);
  const [googleEmail, setGoogleEmail] = useState('');   // signed-in email w/ no licence → pre-fill trial
  const checkoutStatus = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('checkout') : null;

  // Keep ?view=plans in sync with the client-side toggle (no routing change), so a
  // shared/reloaded URL reflects the current view. Bare route on close.
  const syncViewParam = (show) => {
    if (typeof window === 'undefined' || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    if (show) url.searchParams.set('view', 'plans');
    else url.searchParams.delete('view');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  };
  const openPlans  = (email = '') => { setGoogleEmail(email); setShowBuy(true); syncViewParam(true); };
  const closePlans = () => { setShowBuy(false); setGoogleEmail(''); syncViewParam(false); };

  const handleSignOut = async () => { await signOutLicence(); setShowBuy(false); setGoogleEmail(''); };

  // Resolve the current view into `content` + a human label, so the
  // "Report a problem" widget can render on ALL views (gate, pricing, tool).
  let content;
  let viewLabel;
  if (checking) {
    content = (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gold/30 border-t-gold"/>
      </div>
    );
    viewLabel = 'roadmap (loading)';
  } else if (isFreeAuthor && !licenceData) {
    // Free-tier authors see the preview/upgrade CTA
    content = <SubscriptionPreview />;
    viewLabel = 'roadmap (subscription preview)';
  } else if (showBuy) {
    // Plans view wins over auto-unlock so ?view=plans is honoured even for licensed
    // sessions (e.g. an existing customer viewing plans to extend). Closing returns
    // them to their tool (licenceData) or the gate, exactly as before.
    content = (
      <PricingPlans
        product="roadmap"
        defaultEmail={googleEmail}
        signedIn={!!googleEmail}
        onUnlock={(data, exp) => { setUnlocked(data, exp); closePlans(); }}
        onBack={closePlans}
      />
    );
    viewLabel = 'roadmap (pricing plans)';
  } else if (licenceData) {
    content = <ChecklistTool licenceData={licenceData} expired={expired} signedIn={!!authUser} onSignOut={handleSignOut}/>;
    viewLabel = 'roadmap (tool)';
  } else {
    content = (
      <LicenceGate
        product="roadmap"
        onUnlock={(data, exp) => setUnlocked(data, exp)}
        onShowPlans={() => openPlans('')}
        onGoogleNoLicence={(email) => openPlans(email)}
        checkoutStatus={checkoutStatus}
      />
    );
    viewLabel = 'roadmap (licence gate)';
  }

  // Best-effort reporter email: current session, else the cached licence.
  let feedbackEmail = licenceData?.email || googleEmail || '';
  if (!feedbackEmail && typeof window !== 'undefined') {
    try { feedbackEmail = JSON.parse(localStorage.getItem('ncc_pub_licence') || 'null')?.email || ''; }
    catch { /* ignore */ }
  }

  return (
    <>
      {content}
      <RoadmapFeedbackWidget
        surface="roadmap"
        pagePath="/publishing-roadmap"
        pageContext={viewLabel}
        email={feedbackEmail}
      />
    </>
  );
}
